'use client';

// Режим мобильного приложения (обёртка Capacitor, отдельное репо scriptera-app): приложение представляется
// сайту UA-подстрокой ScripteraApp (appendUserAgent в capacitor.config.json).
// В этом режиме: нет регистрации, нет НИЧЕГО про оплату и ₽ (правила сторов) —
// см. docs/IOS-APP-PLAN.md, этап 2.
import { useEffect, useState } from 'react';
// Форма рядов родной ленты переписки (трек Н) — одна на веб и приложение, живёт в
// lib/msg-feed.ts вместе со сборкой этих рядов.
import type { NativeChatDict, NativeChatMedia, NativeChatRow } from './msg-feed';
// Блоки разобранной статьи справки (lib/help-markdown.ts) — одна форма на веб-страницу
// справки и на родные экраны приложения: второго разбора текста в кабинете нет.
import type { NativeHelpBlock } from './help-markdown';
import type { TgAttachKind } from '@scriptera/db/tg-message';
// Форма шагов родного мастера анкеты (docs/NATIVE-WIZARD-PLAN.md) — та же на веб и натив.
import type {
  NativeWizAccount,
  NativeWizDict,
  NativeWizField,
  NativeWizItem,
  NativeWizItemRow,
  NativeWizItemRows,
  NativeWizPreviewDict,
  NativeWizPreviewMsg,
  NativeWizSkin,
  NativeWizStep,
} from './wiz-native';
import { noteBridgeCall, noteBridgeEvent } from './client-journal';
import { parseAppBuild } from './app-release';

/** ИСТИННАЯ проверка «метод есть в ЭТОЙ сборке приложения».
 *  КАПКАН (владелец 2026-07-21, «Application error» на старой сборке): Plugins.* у
 *  Capacitor — Proxy, который отдаёт функцию-обёртку на ЛЮБОЕ имя метода, поэтому
 *  `!!bridge.метод` в приложении ВСЕГДА true, а вызов незарегистрированного метода
 *  отвергается («not implemented») и роняет страницу. Список реально зарегистрированных
 *  методов натив инжектит в Capacitor.PluginHeaders — проверяем по нему.
 *  В вебе (Capacitor нет) — false. Любой новый опциональный метод моста проверять
 *  ТОЛЬКО этим хелпером, НИКОГДА по наличию свойства. */
export function nativeMethod(name: string): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as unknown as {
    Capacitor?: { PluginHeaders?: Array<{ name: string; methods?: Array<{ name: string }> }> };
  }).Capacitor;
  if (!cap) return false;
  const h = cap.PluginHeaders?.find((x) => x.name === 'ScripteraNative');
  return !!h?.methods?.some((m) => m.name === name);
}

/** Нативный мост (свой Swift-код в репо scriptera-app, не npm-плагин).
 *  INVARIANT(scriptera): контракт существующего метода моста ЗАМОРОЖЕН — имя, форма
 *  аргументов и смысл поведения метода, который уже уехал в стор, не меняются НИКОГДА.
 *  Нужно другое поведение — новый метод с новым именем (образец: sheetHeaderAction2)
 *  + feature-detect через nativeMethod() + веб-запасной вариант для старых сборок.
 *  Причина: приложение грузит живой веб — сборки в сторе обновляются месяцами, и
 *  правка контракта молча ломает всех, кто не обновился. Канон: CLAUDE.md, запрет №8. */
export type PushStatus = 'prompt' | 'granted' | 'denied';
export interface ScripteraNativeBridge {
  getState(): Promise<{ available?: boolean; faceIdEnabled?: boolean; type?: string }>;
  setFaceIdEnabled(opts: { enabled: boolean }): Promise<void>;
  /** Опциональны: старые сборки приложения этих методов не имеют. */
  getPushState?(): Promise<{ status: PushStatus }>;
  requestPushPermission?(): Promise<{ status: PushStatus }>;
  /** Нативное нижнее меню iOS (жидкое стекло поверх WebView) — конфигурирует веб
   *  (shell.tsx): заголовки локализованы, иконки — имена SF Symbols. После успешного
   *  вызова натив показывает свой таб-бар, веб прячет свой (класс .native-tabs). */
  /** Токен APNs устройства (null — нет разрешения/ещё не долетел/симулятор).
   *  env — среда APNs из профиля сборки (sandbox = Xcode, production = TestFlight/App
   *  Store); сборки до 02.09.2026 поля не возвращают — сервер тогда шлёт на оба хоста.
   *  Поле ДОБАВЛЕНО к ответу, имя, аргументы и смысл метода не менялись (запрет №8). */
  getPushToken?(): Promise<{ token?: string | null; env?: 'production' | 'sandbox' | null }>;
  configureTabBar?(opts: {
    /** iconPng — свой глиф base64-PNG (шаблон @2x, красится баром) приоритетнее symbol;
     *  старые сборки поле игнорируют. Пример: «Сообщения» — фирменная пара пузырей. */
    items: { key: string; title: string; symbol: string; iconPng?: string }[];
    selected: string;
  }): Promise<{ height?: number }>;
  setActiveTab?(opts: { key: string }): Promise<void>;
  /** Переименовать пункты нижнего меню НА МЕСТЕ (живая смена языка): без пересборки
   *  меню — пересборка (configureTabBar) на живом баре сбрасывает выбор на первую
   *  вкладку и рождает фантомный didSelect (редирект, владелец 2026-07-23).
   *  Старые сборки метода не имеют — подписи ждут перезапуска приложения. */
  setTabTitles?(opts: { items: { key: string; title: string }[] }): Promise<void>;
  setTabBarHidden?(opts: { hidden: boolean }): Promise<void>;
  /** ФОТО АККАУНТА на вкладке нижнего меню (владелец 2026-08-07): base64 картинки (веб
   *  уменьшает её сам), натив режет круг и ставит ПОЛНОЦВЕТНОЙ (.alwaysOriginal).
   *  Это НЕ пересборка бара — меняется только image у существующего UITabBarItem, так
   *  что запрет на configureTabBar мид-сессии не задет. Пустая строка — вернуть значок.
   *  Старые сборки метода не имеют: значок вкладки остаётся прежним. */
  setTabIcon?(opts: { tab: string; data: string }): Promise<void>;
  /** Тема и цвет акцента для нативного меню (shell.tsx шлёт при подключении бара и
   *  при каждой смене data-theme/data-tint): бар следует теме КАБИНЕТА, не системы;
   *  выбранная вкладка красится акцентом текущего оттенка. */
  setTabBarStyle?(opts: { theme: 'light' | 'dark'; red: number; green: number; blue: number }): Promise<void>;
  /** Открыть настройки ЭТОГО приложения в Настройках iOS (включение уведомлений руками). */
  openSystemSettings?(): Promise<void>;
  /** Боковой слой в ЕДИНОМ WebView (план NATIVE-SLIDE): родной выезд справа + параллакс +
   *  свайп-назад, БЕЗ перезагрузки — содержимым слоя владеет веб (маршрут или плашка), натив
   *  только фотографирует уходящий экран и двигает карточку. Порядок: layerPush (сфотографируй,
   *  живой экран за кадр) → веб меняет содержимое → layerCommit (вдвигай). Назад: layerPopBegin
   *  (снимок в центр, живой за кадр) → веб шаг назад → layerPopCommit (убери снимок). Старые
   *  сборки методов не имеют (опционально) — feature-detect по наличию layerPush. */
  /** bottom=true — нижний лист (выезжает снизу, свайп вниз закрывает); иначе боковой выезд справа. */
  layerPush?(opts?: { bottom?: boolean }): Promise<void>;
  /** ЛИСТ-СТОПКА (Н39, владелец 02.09.2026): тот же нижний лист, но экран под ним уходит
   *  назад КОЛОДОЙ, а за ним встаёт фирменная подложка (§11 п.18) — так открывается
   *  КОНЕЧНЫЙ ОБЪЕКТ из списка (карточка доступа, человека, товара, сотрудника). НОВЫЙ
   *  метод, а не ключ у layerPush: контракт моста заморожен (CLAUDE.md запрет №8). Старые
   *  сборки метода не имеют — веб честно откатывается на layerPush (прежняя одиночная
   *  вертикальная шторка). Проверять ТОЛЬКО через nativeMethod('layerPushDeck'). */
  layerPushDeck?(opts?: { bottom?: boolean }): Promise<void>;
  /** Прокручено ли содержимое нижнего листа к верху: пока true — свайп вниз закрывает лист из
   *  любого места, false (проскроллено) — только за верхний грабер (родной iOS-лист). */
  sheetScrollAtTop?(opts: { atTop: boolean }): Promise<void>;
  layerCommit?(): Promise<void>;
  layerPopBegin?(): Promise<void>;
  layerPopCommit?(): Promise<void>;
  /** «Схлопнуть подложку»: оставить один нижний снимок (корневой экран), промежуточные снять
   *  МОЛЧА (невидимы под карточкой). Для «результатных» экранов: свайп-назад ведёт сразу на
   *  корень, минуя стопку флоу (создание из шаблона). Старые сборки метода не имеют. */
  layerFlatten?(): Promise<void>;
  /** Нативная шапка экрана сценария (по HIG): × слева, заголовок с шевроном по центру
   *  (тап → карточка «О сценарии»), «⋯» справа с родным UIMenu. icon — имя SF Symbol;
   *  соседние пункты одной section складываются в inline-группу меню; checked — галка
   *  (например, текущий вид). Тапы возвращаются событием headerAction {key}
   *  ('close' | 'title' | key пункта). Старые сборки методов не имеют. */
  configureLayerHeader?(opts: {
    title: string;
    /** Иконка слева от заголовка (SF) — режим «заголовок-переключатель вида». */
    titleIcon?: string;
    /** Родное меню на ТАПЕ по заголовку (виды с галкой); пусто — тап шлёт 'title'. */
    titleItems?: Array<{ key: string; label: string; icon?: string; checked?: boolean }>;
    items: Array<{
      key: string;
      label: string;
      icon?: string;
      checked?: boolean;
      destructive?: boolean;
      disabled?: boolean;
      section?: string;
    }>;
    /** Правая кнопка — НЕ «⋯» с меню, а одно действие своей SF-иконкой (владелец
     *  2026-08-06: на правке мастером там телефон-превью). Тап шлёт headerAction
     *  {actionKey}. Старые сборки поля игнорируют — остаётся прежнее «⋯». */
    actionIcon?: string;
    actionKey?: string;
    /** Вместе с actionIcon: «⋯» с меню items ОСТАЁТСЯ, действие — отдельной кнопкой
     *  слева от него (Э5 BOTS-MENU-NOTIFY: у правки мастером телефон И меню). Старые
     *  сборки поле игнорируют — у них действие вместо «⋯», как раньше. */
    keepMenu?: boolean;
  }): Promise<void>;
  clearLayerHeader?(): Promise<void>;
  /** ВТОРАЯ СТРОКА заголовка шапки сценария — статус ленты («обновляется»/«обновлено»)
   *  мелким серым под названием вида, как ник под именем в шапке переписки (правило 6
   *  ленты, владелец 19.08). ОТДЕЛЬНЫЙ метод, а не поле configureLayerHeader: контракт
   *  уехавших в стор методов заморожен (CLAUDE.md запрет №8), да и поле нечем нащупать —
   *  спрашиваем поддержку по имени, nativeMethod('setLayerSubtitle'). Старые сборки
   *  метода не имеют: там строку показывает сам веб (CSS .scn-sub-native).
   *  Пустой текст убирает строку целиком. */
  setLayerSubtitle?(opts: { text: string }): Promise<void>;
  /** РОДНОЕ меню «у точки» (Э5 BOTS-MENU-NOTIFY): UIMenu якорем к прямоугольнику
   *  веб-кнопки «⋯» карточки бота — в шапке его не показать, карточек много. Раскрыть
   *  UIMenu программно iOS умеет только с 17.4 (performPrimaryAction; раньше
   *  официального пути нет) — на старых iOS метод честно отвечает {supported:false},
   *  и веб оставляет свой PopMenu. probe:true — только спросить поддержку, ничего не
   *  показывая (веб зовёт раз при маунте списка). Ответ без key — закрыл мимо.
   *  Старые сборки метода не имеют — feature-detect nativeMethod('pointMenu'). */
  pointMenu?(opts: {
    probe?: boolean;
    items?: Array<{
      key: string;
      label: string;
      /** Серая подпись мелким под пунктом — buildMenu натива читает её с первой сборки
       *  pointMenu (subtitle UIAction), поле просто не было объявлено в типе. */
      sub?: string;
      icon?: string;
      destructive?: boolean;
      disabled?: boolean;
      section?: string;
    }>;
    /** Прямоугольник кнопки-якоря в CSS-пикселях вьюпорта (getBoundingClientRect). */
    x?: number;
    y?: number;
    w?: number;
    h?: number;
    theme?: 'light' | 'dark';
  }): Promise<{ supported?: boolean; key?: string } | undefined>;
  /** Нативный верх шторки. Лист (side=false): грабер + «×/← | заголовок | действие»;
   *  боковой слой (side=true): та же строка на материале под статус-баром. lead — иконка
   *  ведущей кнопки ('close' ×, 'back' ←); действие справа — текст (actionLabel) ИЛИ
   *  SF-иконка (actionIcon, напр. info.circle). Тапы — headerAction {'sheetClose' |
   *  'sheetAction'}; 'sheetClose' веб сам маппит в назад/закрыть. Старые сборки методов
   *  не имеют (остаётся DOM-шапка). */
  /** Маркер «клавиатуру ведёт натив»: веб по НАЛИЧИЮ метода отключает свою клавиатурную
   *  механику v5 (chat-keyboard.ts) — два механизма дерутся («дёргается», владелец
   *  2026-07-21). С 2026-07-26 натив окно сайта под клавиатуру НЕ ужимает (режим «поверх»,
   *  как родные приложения) и шлёт kbChange {height} — веб отбивает низ прокрутки и
   *  доскролливает поле (native-keyboard.ts). Канон: docs/concepts/IOS-KEYBOARD-CANON.md §7. */
  nativeKeyboard?(): Promise<{ native: boolean }>;
  /** Нижний док редактора сценария (владелец 2026-07-21): стеклянная капсула «как
   *  нижнее меню» — ← → (история) | + (шаг) / × (удалить связь) | ▶ «Проверить».
   *  visible=false — спрятать (плашка сверху / уход со схемы). Обе операции истории
   *  недоступны — пара растворяется, капсула сжимается. Тапы — headerAction
   *  {key:'dock:undo'|'dock:redo'|'dock:middle'|'dock:run'}; *Label — VoiceOver. */
  configureDock?(opts: {
    visible: boolean;
    canUndo?: boolean;
    canRedo?: boolean;
    middle?: 'add' | 'deleteEdge';
    run?: 'hidden' | 'run' | 'busy' | 'listening';
    undoLabel?: string;
    redoLabel?: string;
    middleLabel?: string;
    runLabel?: string;
  }): Promise<void>;
  configureSheetHeader?(opts: {
    title: string;
    /** Серая строка под заголовком (счётчик шагов мастера, владелец 2026-08-05).
     *  Старые сборки ключ игнорируют — рисовать начнёт следующая сборка Xcode. */
    subtitle?: string;
    actionLabel?: string;
    actionIcon?: string;
    /** Вторая правая кнопка шапки слоя (фильтр рядом с «+», владелец 2026-08-07):
     *  только SF-иконка, тап уходит headerAction 'sheetAction2'. Старые сборки ключ
     *  игнорируют — поддержку спрашивать через nativeMethod('sheetHeaderAction2'). */
    action2Icon?: string;
    action2Label?: string;
    lead?: 'close' | 'back';
    side?: boolean;
    /** Мелкая иконка перед заголовком (PNG base64) — логотип модели (владелец 2026-07-21). */
    titleIconPng?: string;
    /** Нативные вкладки под шапкой (режимы модели; наборы любые). Тап → headerAction 'sheettab:<key>'. */
    tabs?: Array<{ key: string; label: string }>;
    selectedTab?: string;
    /** Родной поиск в шапке шторки (как у «Моделей» в configureTopBar): натив рисует строку
     *  поиска над клавиатурой; ввод построчно уходит событием searchQuery {q} (крестик — {q:''}),
     *  открытие/закрытие — searchState {open}. Старые сборки поле игнорируют (остаётся веб-поле). */
    search?: { key: string; placeholder?: string };
    /** Стартовый вид стекла кнопок шапки (владелец 2026-07-26): true — тёмный (шторки с
     *  тёмным героем-градиентом), false — по теме кабинета. Только старт — дальше материал
     *  сам адаптируется к контенту (Apple). Старые сборки ключ игнорируют (тёмный старт). */
    glassDark?: boolean;
  }): Promise<void>;
  clearSheetHeader?(): Promise<void>;
  /** Маркер сборки: шапка слоя умеет ВТОРУЮ правую кнопку (action2Icon). Метода нет —
   *  старое приложение, кнопку рисуем прежним кружком в теле раздела. */
  sheetHeaderAction2?(): Promise<void>;
  /** ── Родной композер чата (Трек 2, CHAT-FIX-PLAN) ── текстовое поле + микрофон +
   *  отправка НАД клавиатурой, в теме кабинета. Веб конфигурирует (плейсхолдер, тема,
   *  акцент, состояние), натив рисует родное поле (UITextView авто-рост) и кнопки
   *  (как GlassCircleButton) и шлёт события composerInput/composerSubmit/composerMic.
   *  Текстом на время активности владеет НАТИВ (истина при печати) — веб зеркалит через
   *  composerInput ради payload отправки и пушит текст обратно ТОЛЬКО явно (setComposerText:
   *  старт-сид, дописать распознанное, очистить после отправки), без эха на каждый ввод.
   *  state: 'idle' обычный | 'busy' идёт ответ (поле живёт, отправка/микрофон гаснут) |
   *  'recording' идёт запись голоса | 'transcribing' распознавание (поле заблокировано).
   *  Натив по мере роста поля/движения клавиатуры шлёт composerResize {pad} — веб отдаёт
   *  этот отступ низу ленты (последнее сообщение не прячется под полем). Старые сборки
   *  метода не имеют — остаётся веб-композер (feature-detect nativeMethod('configureComposer')). */
  configureComposer?(opts: {
    placeholder: string;
    state: 'idle' | 'busy' | 'recording' | 'transcribing';
    theme: 'light' | 'dark';
    /** Локализованный текст статуса распознавания («Распознаю…») — показывается в поле,
     *  пока идёт transcribing, чтобы не было пустоты. */
    transcribing?: string;
    /** Акцент отправки — RGB текущего оттенка кабинета (как таб-бар). */
    red: number;
    green: number;
    blue: number;
    /** Скрепка слева от поля (переписка раздела «Сообщения», MESSAGES М12): тап уходит
     *  событием composerAttach, файл выбирает и шлёт веб. У чата с ассистентом её нет —
     *  туда файлы не прикладывают. Старые сборки ключ игнорируют (скрепки не будет, а
     *  веб-композер уже спрятан) — поэтому включать её можно только вместе с проверкой
     *  nativeMethod('composerAttachSupported'). */
    attach?: boolean;
    /** ПИЛЮЛЯ СЛЕВА ОТ ПОЛЯ (Scriptera A, сборка a109+, ADM-APP-FIX-3 п.5): стеклянное слово (выбор канала
     *  переписки), тап → событие composerLeftTap {key}. Каждый вызов — полный снимок: ключа нет или null —
     *  пилюли нет; поэтому шлётся в КАЖДОМ вызове. Ключ ДОБАВЛЕН (запрет №8): старые сборки его не знают. */
    left?: { key: string; label: string; icon?: string } | null;
    /** КАНАЛ ВНУТРИ КАПСУЛЫ (Scriptera A, сборка с `askText`, владелец 14.10.2026 вечер): значок `icon` слева
     *  внутри поля, тап — родное выпадающее меню из `items` (галочка `on`, серый `disabled`, `sub` —
     *  вторая строка), выбор → событие composerMenuPick {key, item}. При `menu` пилюля `left` не рисуется.
     *  Ключ ДОБАВЛЕН: старые сборки его не знают. */
    menu?: {
      key: string;
      icon: string;
      label?: string;
      title?: string;
      items: { key: string; label: string; icon?: string; sub?: string; on?: boolean; disabled?: boolean }[];
    } | null;
    /** Плашка внутри капсулы за значком `menu` (тема письма); тап → composerChipTap {key}. */
    chip?: { key: string; label: string } | null;
  }): Promise<void>;
  /** Маркер «родное поле умеет скрепку» (MESSAGES М12): метод-пустышка, по НАЛИЧИЮ
   *  которого веб понимает, что сборка нарисует скрепку и пришлёт composerAttach. Полем
   *  attach проверить нельзя — старая сборка просто молча его проигнорирует, и в переписке
   *  не осталось бы ни родной скрепки, ни веб-композера (его при родном поле прячут).
   *  Тот же приём, что nativeKeyboard. */
  composerAttachSupported?(): Promise<{ ok: boolean }>;
  /** Явно задать текст родного поля (не эхом на ввод — натив владелец текста при печати). */
  setComposerText?(opts: { text: string }): Promise<void>;
  /** Уровень звука 0..1 на кадре записи — натив рисует живую полосочку-эквалайзер (Трек 2). */
  setComposerLevel?(opts: { level: number }): Promise<void>;
  /** Убрать родной композер (уход со страницы чата) — веб возвращает свой. */
  clearComposer?(): Promise<void>;
  /** Вернуть фокус родному полю после тапа по кнопке «вниз» (устар. — форсил открытие
   *  клавиатуры, когда её не было; заменён на holdComposerFocus). */
  refocusComposer?(): Promise<void>;
  /** Удержать фокус родного поля на время тапа по вебу (кнопка «вниз»): поле НЕ отпускает
   *  клавиатуру ~600мс (canResignFirstResponder=false). Открыта — остаётся без мелька;
   *  закрыта — не открывается (фокус не форсим). Владелец 2026-07-22. */
  holdComposerFocus?(): Promise<void>;
  /** ── РОДНАЯ ПЕРЕПИСКА В ПРИЛОЖЕНИИ (docs/MESSAGES-PLAN.md, трек Н) ──
   *  Ленту переписки в приложении рисует и крутит НАТИВ, а веб остаётся мозгом: он ходит
   *  на сервер, знает правила каналов и язык кабинета и отдаёт нативу ГОТОВЫЕ ряды
   *  (`nativeChatRows` в lib/msg-feed.ts) — Swift ничего не переводит и не форматирует.
   *  Порядок: chatOpen (натив выдвигает свой экран поверх списка) → chatFeed (ряды) →
   *  chatState (шапка, окно ответа, «отвечаю сам», ошибка) → chatClose при уходе.
   *  Обратно приходят намерения человека событиями chat* (см. addListener).
   *  Поддержку сборки спрашивать ТОЛЬКО через nativeMethod('chatSupported'). */
  chatOpen?(opts: {
    dialogId: string;
    /** Имя собеседника в шапке. */
    title: string;
    /** Ник/канал мелкой строкой под именем. */
    subtitle?: string;
    avatarUrl?: string;
    theme: 'light' | 'dark';
    /** Акцент кабинета (как у композера и таб-бара). */
    red: number;
    green: number;
    blue: number;
    dict: NativeChatDict;
    /** ЦВЕТА ПУЗЫРЕЙ ЛЕНТЫ — ГОТОВЫМИ ПАРАМИ РОЛЕЙ КАБИНЕТА (владелец 31.08.2026: «розовый
     *  цвета нашего бэкграунда, более насыщенный, и внутри текст тоже нашего оттенка, но
     *  ещё сильнее насыщенного; бирюзовый — бэкграунд и цвет текста такой же глубины»).
     *  Считать их в Swift нельзя: у кабинета живая тема И живой оттенок (наборы), роли
     *  берутся из палитры — поэтому их читает веб (readBubbleColors) и шлёт строками
     *  '#rrggbb'. Ключ НОВЫЙ и необязательный: старая сборка его не знает и рисует
     *  прежними долями акцента (контракт моста заморожен, CLAUDE.md запрет №8). */
    bubbles?: NativeBubbleColors;
    /** ПЕРЕПИСКА ЛИСТОМ-СТОПКОЙ (Scriptera A, сборка a109+, ADM-APP-FIX-3 п.9): 'deck' — поверх того, что
     *  на экране (карточки клиента), вкладка не переключается; ответ {ok, present:'deck'|'slide'}. Нет
     *  ключа — прежний выезд боком во вкладке «Чаты». Ключ ДОБАВЛЕН (запрет №8). */
    present?: 'deck';
    /** АВАТАР СПРАВА В ШАПКЕ ПЕРЕПИСКИ (Scriptera A, ADM-APP-FIX-4 §2.7, п.24): `false` — аватара (вход в профиль)
     *  нет, потому что профиль этого клиента уже стоит в стопке ПОД перепиской (переписка из профиля «Продаж»);
     *  нет ключа или `true` — аватар как раньше. ДОБАВЛЕН, старые сборки не знают (запрет №8; веб шлёт его только
     *  сборкам ≥ ADM_PROFILE_MIN_BUILD). */
    info?: boolean;
  }): Promise<void | { ok?: boolean; present?: 'deck' | 'slide' }>;
  /** Ряды ленты. mode: 'reset' — вся страница заново (открытие, живое обновление),
   *  'prepend' — страница «раньше» встаёт в начало (взгляд остаётся на месте). */
  chatFeed?(opts: {
    dialogId: string;
    rows: NativeChatRow[];
    /** Есть ли что грузить дальше вверх (кнопка «показать раньше»). */
    more: boolean;
    mode: 'reset' | 'prepend';
    /** НЕОБЯЗАТЕЛЬНОЕ (круг 11, §17.0 п.8): id ряда, на который лента встаёт — переписка
     *  открыта из находки поиска. Старые сборки поле не читают, метод не менялся (запрет №8). */
    focus?: string;
  }): Promise<void>;
  /** ИЗМЕНЕНИЯ ЛЕНТЫ ВМЕСТО ВСЕЙ ЛЕНТЫ (круг 9, Э4.1 плана docs/CHAT-LOOK-PLAN.md §15.6).
   *
   *  Зачем новый метод. `chatFeed` везёт ВСЮ переписку заново при каждом пересчёте и
   *  каждые 3 с живого опроса, а натив переприменяет список целиком — это и был корень
   *  разъезжающихся высот (§15.0, разбор п.5). Здесь веб шлёт `order` — все id ленты по
   *  порядку (по нему натив сам считает вставки, удаления и перестановки стандартным
   *  `difference(from:)`) — и `rows` РОВНО с теми рядами, чей json отличается от прежнего
   *  или которых он ещё не видел. Неизменившиеся ряды натив берёт из своего хранилища.
   *  Догрузка «раньше» отдельным режимом НЕ нужна: порядок выражает её сам.
   *
   *  Метод НОВЫЙ, `chatFeed` не тронут (CLAUDE.md, запрет №8): сборка без `chatPatch`
   *  получает прежний `chatFeed` целиком, как раньше. Спрашивать поддержку — ТОЛЬКО
   *  через `nativeMethod('chatPatch')` (капкан Proxy Capacitor). */
  chatPatch?(opts: {
    dialogId: string;
    /** Все id ленты по порядку — полный состав ряда за рядом. */
    order: string[];
    /** Только новые и изменившиеся ряды; пусто — состав тот же, менять нечего. */
    rows: NativeChatRow[];
    /** Есть ли что грузить дальше вверх (кнопка «показать раньше»). */
    more: boolean;
    /** НЕОБЯЗАТЕЛЬНОЕ (круг 11, §17.0 п.8): id ряда, на который лента встаёт — переписка
     *  открыта из находки поиска. Старые сборки поле не читают (запрет №8). */
    focus?: string;
  }): Promise<void>;
  /** Живое состояние экрана: шапка, окно ответа, замок сценария, ошибка отправки. */
  chatState?(opts: {
    dialogId: string;
    title?: string;
    subtitle?: string;
    /** Остаток окна ответа Инстаграма строкой; пусто — окна нет или оно не показывается. */
    windowNote?: string;
    /** false — писать нельзя: поля ввода не должно быть вовсе (как в вебе). */
    canReply?: boolean;
    /** ПОЧЕМУ нельзя писать — ГОТОВАЯ строка на языке кабинета; встаёт на место поля
     *  ответа (владелец 2026-08-08: «если человеку нельзя писать — писать прямо у поля»).
     *  Пусто — писать можно. */
    blockedNote?: string;
    /** Диалог взят человеком на себя — ГОТОВАЯ строка-предупреждение под шапкой; пусто —
     *  строки нет. Собирает её кабинет: в Swift переводимых строк не бывает. */
    mineNote?: string;
    /** Ошибка последней отправки словами кабинета; пусто — убрать строку. */
    error?: string;
    theme?: 'light' | 'dark';
    dict?: NativeChatDict;
    /** Те же четыре цвета, что у chatOpen: тему и набор кабинета человек меняет НА МЕСТЕ,
     *  и открытая лента обязана перекраситься без пересоздания экрана. */
    bubbles?: NativeBubbleColors;
  }): Promise<void>;
  /** ВСЁ МЕДИА ПЕРЕПИСКИ ПО ПОРЯДКУ — для РОДНОГО ПРОСМОТРА (волна 3): нажали кадр в
   *  родной ленте — натив открывает свой просмотр и листает им ту же переписку, что
   *  листает веб-слой (общая чистая рука feedMedia в lib/msg-feed.ts). Состав списка
   *  считает ВЕБ: в нём живут все особые случаи (удалённый файл, репост без кадра,
   *  голосовое), и второй правды у натива быть не должно. Метод НОВЫЙ (запрет №8);
   *  сборка без него просто не откроет просмотр — как было до волны 3. */
  chatMedia?(opts: {
    dialogId: string;
    items: NativeChatMedia[];
    words: { viewer: string; openIg: string };
  }): Promise<void>;
  /** Убрать родной экран переписки (уход назад ведёт сам натив, это — программный путь). */
  chatClose?(): Promise<void>;
  /** Маркер «сборка умеет родную переписку» (тот же приём, что composerAttachSupported):
   *  полем проверить нельзя — старая сборка молча проигнорирует вызов, и человек остался
   *  бы без ленты вовсе. Нет метода — в приложении работает прежняя веб-лента. */
  chatSupported?(): Promise<{ ok: boolean }>;

  /** ── РОДНОЙ МАСТЕР АНКЕТЫ В ПРИЛОЖЕНИИ (docs/NATIVE-WIZARD-PLAN.md) ──
   *  Устроен как родная переписка: анкету РИСУЕТ натив, а веб остаётся мозгом — держит
   *  деф анкеты, считает видимые поля, проверяет ответы и создаёт бота. Натив ничего не
   *  переводит и не решает: шаги приходят готовыми строками на языке кабинета.
   *  Порядок: wizOpen (натив выдвигает экран поверх слоя) → wizPatch на каждое изменение
   *  (другой шаг, ошибка, занятость) → wizClose при уходе. Обратно — события wiz*.
   *  Поддержку сборки спрашивать ТОЛЬКО через nativeMethod('wizSupported'). */
  wizOpen?(opts: {
    /** Мелкая строка над заголовком шага — название бота, которого собираем. */
    title: string;
    steps: NativeWizStep[];
    /** Номер открытого шага (0 … steps.length − 1). */
    at: number;
    theme: 'light' | 'dark';
    dict: NativeWizDict;
    /** Есть ли у этой анкеты предпросмотр переписки (кнопка-телефон в шапке). */
    preview?: boolean;
  }): Promise<void>;
  /** Обновить открытый мастер НА МЕСТЕ: другой шаг (at), пересчитанные шаги (steps —
   *  видимость полей зависит от ответов), красная строка ошибки, занятость кнопки. */
  wizPatch?(opts: {
    at?: number;
    steps?: NativeWizStep[];
    /** ГОТОВАЯ строка ошибки шага; пустая — убрать. */
    error?: string;
    /** Идёт создание бота — кнопка ждёт. */
    busy?: boolean;
    theme?: 'light' | 'dark';
    dict?: NativeWizDict;
  }): Promise<void>;
  /** Убрать родной мастер (уход назад ведёт сам натив, это — программный путь). */
  wizClose?(): Promise<void>;
  /** ПРЕДПРОСМОТР ПЕРЕПИСКИ нижним листом поверх мастера (приказ владельца 29.08.2026:
   *  «телефончик остаётся»). Ряды считает веб из текущих ответов (те же уезжают
   *  странице /wizard-preview); натив рисует оформление сети своими силуэтами. */
  wizPreview?(opts: {
    skin: NativeWizSkin;
    account: NativeWizAccount;
    msgs: NativeWizPreviewMsg[];
    /** Пары «комментарий человека → публичный ответ бота» (вид «Комментарии»). */
    comments?: Array<{ user: string; bot: string }>;
    dict: NativeWizPreviewDict;
    /** АДРЕС НАШЕЙ СТРАНИЦЫ С НАРИСОВАННЫМ ТЕЛЕФОНОМ (ТЗ-9 Е4, владелец 2026-08-30:
     *  «перерисовать полностью, чтобы чат был так же хорош, как Инстаграм на первом
     *  экране»). Приём тот же, что у сцены знакомства: рисует веб, показывает натив
     *  веб-вью. Ключ НОВЫЙ и необязательный — старая сборка его не читает и рисует
     *  пузыри сама по прежним ключам, поэтому они продолжают слаться (запрет №8). */
    url?: string;
  }): Promise<void>;
  /** ПУБЛИКАЦИИ Instagram сеткой снимков ПОВЕРХ мастера (владелец 29.08.2026). Список
   *  знает веб (ходит в Мету) и присылает готовые ряды; натив рисует сетку и отдаёт выбор
   *  событием wizChange('mediaIds'). Тем же вызовом идёт догрузка по «Показать ещё»:
   *  открытый лист дополняется, второй не заводится. Свой лист веб в приложении открыть не
   *  может — он лёг бы ПОД системной шторкой мастера. */
  wizPosts?(opts: {
    title: string;
    items: Array<{ id: string; thumb: string; video?: boolean }>;
    picked: string[];
    hasMore: boolean;
    /** Готовые слова: «Показать ещё», «Готово», строка пустого списка. */
    more: string;
    done: string;
    empty: string;
    theme: 'light' | 'dark';
  }): Promise<void>;
  /** ФАЙЛ ДЛЯ ПОЛЯ МАСТЕРА вида `file` (владелец 19.09.2026, подарок «аудио/видео/файл»):
   *  веб просит системное окно под маски `accept` (видео — библиотека PHPicker, остальное —
   *  окно документов).
   *
   *  ФАЙЛ ЗАЛИВАЕТ САМ НАТИВ (ТЗ 20.09.2026 F4, владелец: «приложение обязано брать файл
   *  любого допустимого размера»): `upload.url` — полный адрес нашей ручки заливки, поле
   *  формы — `upload.field`. Натив шлёт событие `wizFile` трижды по ходу дела:
   *  `{name, fileName, mime, bytes, status:'uploading'}` — файл выбран и поехал;
   *  `{…, status:'done', body:<ответ ручки>}` — залит (в `body` разобранный JSON);
   *  `{…, status:'error', code:<код HTTP>|'network'}` — не доехал.
   *  Base64 через мост и предела моста БОЛЬШЕ НЕТ: строка жила в двух памятях и держала
   *  потолок 8 МБ. Метод в стор не уезжал (только сборка 3022 от 19.09) — менять его
   *  можно, прочие wiz*-методы не тронуты (запрет №8). Веб шлёт поле вида `file` лишь
   *  сборке, объявившей метод (feature-detect), старой — плашку-подсказку.
   *
   *  ТЯЖЁЛЫЙ ФАЙЛ В СЕТЬ НЕ УХОДИТ ВОВСЕ (ТЗ 20.09.2026 V.4, владелец: «любое видео с
   *  Айфона длиннее трёх секунд больше 100 МБ — гнать по сети нельзя»). Порядок: натив
   *  кладёт выбранный файл на диск и шлёт ОДНИ МЕТАДАННЫЕ — `{…, status:'picked', bytes,
   *  mime, seconds?}`, файл остаётся на телефоне; ВЕБ решает по своду правил каналов,
   *  подходит ли он, и отвечает методом `wizFileGo`. Своих правил о весе и формате у
   *  оболочки нет: числа живут в channels.ts, а сборка висит в сторе месяцами. */
  wizFile?(opts: {
    name: string;
    accept: string[];
    videos: boolean;
    upload: { url: string; field: string };
    theme: 'light' | 'dark';
  }): Promise<void>;
  /** ОТКРЫТЬ СИСТЕМНОЕ ОКНО ФОТО ПОД ПОЛЕ-КАРТИНКУ ЗАНОВО (хвост ТЗ блока V, «Выбрать
   *  другой файл» в родном вопросе про снимок): `upload` — полный адрес нашей ручки и имя
   *  поля формы, как у `wizFile`. Обычный первый заход натив делает сам по тапу ряда —
   *  этот метод нужен ровно для повторного выбора после вопроса. МЕТОД НОВЫЙ: уехавшие в
   *  стор wiz*-методы не тронуты (запрет №8). */
  wizPhotoOpen?(opts: { name: string; upload?: { url: string; field: string } }): Promise<void>;
  /** ЧТО ДЕЛАТЬ С ВЫБРАННЫМ ФАЙЛОМ (ТЗ 20.09.2026 V.4): `upload` — вези как есть;
   *  `compress` — ужми ролик НА УСТРОЙСТВЕ (AVAssetExportSession, без сети) до `maxBytes`
   *  и вези ужатый; `drop` — забудь выбранное («Выбрать другой файл», «Отмена»). Ход дела
   *  приходит тем же событием `wizFile`: `compressing` → `uploading` → `done`, либо
   *  `error` с кодом `toobig` («даже сжатый не влезает») / `compress` («сжать нечем»).
   *  МЕТОД НОВЫЙ: прочие wiz*-методы не тронуты (запрет №8). */
  wizFileGo?(opts: { name: string; action: 'upload' | 'compress' | 'drop'; maxBytes?: number }): Promise<void>;
  /** ЧТО ДЕЛАТЬ С ВЫБРАННЫМ ФАЙЛОМ ПИСЬМА (ТЗ 20.09.2026 V.4 п.5) — то же, что `wizFileGo`
   *  у мастера, но для окна «Сообщение»: `token` — метка файла из события `msgFile`,
   *  `url` — полный адрес нашей ручки с ВИДОМ (его веб знает по настоящему типу файла).
   *  МЕТОД НОВЫЙ и служит признаком способности: увидев его, веб шлёт строкам листа
   *  `upload` и больше не возит файлы через мост (старая сборка — по-старому).
   *  С П136 п.4б в `url` (query) едет и ОПИСАНИЕ ФАЙЛА ОТ ТЕЛЕФОНА (`made=phone`, длина,
   *  стороны, `hasAudio`, теги трека) — оболочка просто зовёт адрес как есть, смысл
   *  метода прежний. АДДИТИВНО к форме заливки оболочка МОЖЕТ приложить часть `poster`
   *  (jpg кадра ролика, ≤ 2 МБ): с ним ручка и кадр на раннере не снимает — файл не едет
   *  туда ни разу. Без части — кадр снимет раннер, как раньше. В ответе ручки аддитивный
   *  `timing: {recv, probe, convert, store}` (мс) — веб пишет его в журнал `msg.upload`. */
  msgFileGo?(opts: {
    token: string;
    action: 'upload' | 'compress' | 'drop';
    maxBytes?: number;
    url?: string;
  }): Promise<void>;
  /** ПОВТОРИТЬ СОРВАВШУЮСЯ ЗАЛИВКУ (П129.2): оболочка держит копию файла до ответа
   *  ручки; `token` — метка из события `msgFile`. Копии уже нет (перезапуск) — событие
   *  `msgFile {status:'error', code:'gone'}`, и веб открывает выбор заново. МЕТОД НОВЫЙ. */
  msgFileRetry?(opts: { token: string }): Promise<void>;
  /** ОТКРЫТЬ СИСТЕМНОЕ ОКНО ВЫБОРА ФАЙЛА ПИСЬМА ЗАНОВО (хвост ТЗ блока V, «Загрузить
   *  другой файл»): `kind` — ключ строки листа «Вложение», под который и открывается окно
   *  (тот же путь, что по тапу самой строки: библиотека у снимков и роликов, окно
   *  документов у звука и файла). Без него вебу из родного вопроса про негодный файл
   *  оставалась одна «Отмена» — человек не мог выбрать другой файл, не открывая лист
   *  заново руками. МЕТОД НОВЫЙ: уехавшие в стор msg*-методы не тронуты (запрет №8);
   *  сборка без него — прежнее поведение (вопрос без строки «Загрузить другой файл»). */
  msgAttachOpen?(opts: { kind: string }): Promise<void>;
  /** КОРОТКИЙ ВОПРОС РОДНЫМ ЛИСТОМ ПОВЕРХ МАСТЕРА (ТЗ 20.09.2026 F4): «Видео весит 73 МБ,
   *  максимум 50» с капсулами «Сжать» / «Выбрать другой файл» и «Отмена». Свой лист веб
   *  тут открыть не может — он лёг бы ПОД системной шторкой мастера (канон §11).
   *  Слова ГОТОВЫЕ (своих строк у Swift не бывает), ответ приходит событием
   *  `wizAskPick {key}`; пустой ключ — «Отмена» (вопрос без ответа не остаётся). */
  wizAsk?(opts: {
    title: string;
    text: string;
    /** `tone: 'danger'` — красная капсула (`.systemRed`, токен `--red`): «Удалить
     *  сообщение» (П132 п.5). Ключ АДДИТИВНЫЙ (запрет №8): старые сборки его не знают и
     *  рисуют капсулу обычной — смысл ответа (`wizAskPick {key}`) тот же. */
    actions: Array<{ key: string; label: string; tone?: 'danger' }>;
    cancel: string;
    theme: 'light' | 'dark';
    /** «ГЕРОЙ» ВОПРОСА ПРО ФАЙЛ (П135 п.11б): по центру пустой части листа — значок вида
     *  (`icon` — SF-имя из набора строк листа «Вложение») или превью (`thumb` — адрес
     *  кадра/локальный путь), под ним `name` одной строкой и `meta` («0:12 · 3,4 МБ»).
     *  Ключ АДДИТИВНЫЙ (запрет №8): старые сборки его не знают — лист как был.
     *  ВОПРОСЫ О СОСТАВЕ (П151 §11р п.3, договор с нативом): герой — только значок вида
     *  (`icon`), `name` и `meta` ПУСТЫЕ строки — строки имени и меты натив не рисует;
     *  `title` — заголовок вопроса («Кружок», «Несколько фотографий»), `text` — полный
     *  текст, `cancel` — своё слово («Не добавлять кружок»). Форма метода не менялась. */
    hero?: { icon: string; thumb?: string; name: string; meta: string };
  }): Promise<void>;
  /** ── СТРАНИЦА ОДНОГО ЭЛЕМЕНТА СПИСКА (шаг-список объектов, ТЗ 03.09.2026 В2) ──
   *  Шаг «Что запускает» и «Сообщения» уезжают нативу ОДНИМ полем-списком (kind
   *  'itemList'): строки готовы, а что стоит за строкой — знает веб. Тап по строке и
   *  выбор вида в родном меню «Добавить …» приходят событиями `wizItemListTap` /
   *  `wizItemListAdd`, и веб отвечает этим методом — страницей элемента СТОПКОЙ поверх
   *  мастера (словарь видов §11.0). Правки полей идут прежним `wizChange`, уход назад —
   *  событием `wizItemBack`.
   *  МЕТОДЫ НОВЫЕ: смысл уехавших в стор wiz*-методов не тронут (запрет №8).
   *  Вид поля 'list' (ТЗ В1 03.09) снят вместе со вторым механизмом списка
   *  (MESSAGE-COMPOSER К18.1 п.9–10): веб его больше не шлёт. */
  /*  К20 (владелец 07.09.2026, «одна шторка, шаги внутри»): у страницы появились два
   *  аддитивных ключа — `flow` (страница ПОТОКА добавления: натив кладёт её внутрь той же
   *  шторки, а не открывает ещё один лист) и `full` (странице нужна полная высота: шторка
   *  подрастает одновременно с заездом). Оба живут в `NativeWizItem`, имя и смысл метода
   *  прежние (запрет №8). */
  wizItemOpen?(opts: NativeWizItem): Promise<void>;
  /** Обновить ОТКРЫТУЮ страницу элемента на месте (пересчитанная видимость полей).
   *  `grow` и `focus` — АДДИТИВНЫЕ ключи (этап J п.2, CLAUDE.md запрет №8): развернуть
   *  тянущуюся полушторку на весь экран и поставить каретку в названное поле. Старая
   *  сборка их не читает — страница просто обновит поля, как обновляла. */
  wizItemPatch?(opts: { fields: NativeWizField[]; grow?: boolean; focus?: string }): Promise<void>;
  /** Убрать страницу элемента программно (вход удалили, мастер закрылся). */
  wizItemClose?(): Promise<void>;
  /** ── СТРАНИЦА ОДНОГО ПИСЬМА (шаг-список объектов, ТЗ 03.09.2026 В2, Д2) ──
   *  Шаг «Письма» уезжает нативу полем `kind: 'itemList'`; тап по строке приходит
   *  событием `wizItemListTap {name, key}`, и веб отвечает ЭТИМ методом — страницей
   *  письма СТОПКОЙ поверх мастера: три строки-вопроса («Сколько ждать», «Кому слать»,
   *  «Сообщение») и «Удалить письмо» внизу красным. Тап по строке — событие
   *  `wizItemRowTap {key}`; за строкой веб открывает либо страницу полей (`wizItemOpen`,
   *  механика входов), либо родное окно письма (`msgOpen`).
   *  МЕТОДЫ НОВЫЕ (запрет №8): смысл уехавших в стор wiz*-методов не тронут; сборка без
   *  вида 'itemList' анкету не берёт вовсе — человек видит прежний веб-мастер. */
  /*  К20 (владелец 07.09.2026): аддитивный ключ `flow` — страница списка ботов едет
   *  ВНУТРЬ шторки потока добавления (push), а не отдельным листом поверх меню; `half`
   *  как в К19. Имя и смысл метода прежние (запрет №8). */
  wizItemRowsOpen?(opts: NativeWizItemRows): Promise<void>;
  /** Обновить ОТКРЫТУЮ страницу письма на месте (пересчитанные значения строк). */
  wizItemRowsPatch?(opts: { title?: string; rows: NativeWizItemRow[]; remove?: string }): Promise<void>;
  /** Убрать страницу письма программно (письмо удалили, мастер закрылся). */
  wizItemRowsClose?(): Promise<void>;
  /** Маркер «сборка умеет родной мастер» (тот же приём, что chatSupported): полем
   *  проверить нельзя — старая сборка молча проигнорирует вызов, и человек остался бы
   *  без анкеты вовсе. Нет метода — в приложении работает прежний веб-мастер. */
  wizSupported?(): Promise<{ ok: boolean }>;
  /** ПРЕДЗАГРУЗКА СЦЕН МАСТЕРА (О5.9/О5.10, владелец 03.09.2026: «всё, что телефон
   *  показывает, загружено заранее»). Веб присылает ТОЧНЫЕ адреса страниц
   *  /[locale]/wizard-scene/[scene], которые мастер откроет (тот же lib/wizard-demo-url.ts);
   *  натив грузит их скрытыми веб-вью, и wizOpen с совпавшим адресом показывает готовую
   *  страницу с первого кадра. МЕТОД НОВЫЙ (запрет №8): старые сборки его не имеют — там
   *  мастер грузит сцену при открытии, как раньше.
   *
   *  ДОГОВОР О НАБОРЕ (О5.10, обе стороны обязаны совпасть):
   *  • Набор ОДИН на весь кабинет и присылается ЦЕЛИКОМ — повторный вызов заменяет
   *    прежний (общие адреса натив не перегружает, лишние отпускает).
   *  • В наборе адреса ДВУХ входов сразу: СОЗДАНИЕ из каталога «Из шаблона» и ПРАВКА
   *    ботов из списка «Боты» — каталог открыт стопкой поверх списка, оба живут разом.
   *    Сводит их веб (lib/warm-demo-scenes.ts), натив про источники не знает.
   *  • ПОРЯДОК = ПРИОРИТЕТ: сначала каталог (человек уже в нём), затем боты сверху
   *    списка. Больше своего потолка натив держать не обязан — режет С КОНЦА.
   *  • ПОТОЛОК — 8 адресов (WARM_SCENE_LIMIT): каждая прогретая страница живёт в своём
   *    скрытом WKWebView и стоит десятки МБ. Веб длиннее восьми не шлёт. */
  wizWarm?(opts: { urls: string[]; theme: 'light' | 'dark' }): Promise<void>;

  /** ── ПОДКЛЮЧЕНИЕ АККАУНТА ПРЯМО ИЗ МАСТЕРА (владелец 11.09.2026, ТЗ «Добавить аккаунт
   *  из мастера») ──
   *  На шаге «Аккаунт» под списком стоит строка «Добавить аккаунт» (поле `accountId`
   *  получило аддитивный ключ `addLabel`). Тап приходит событием `wizAddAccount`, и веб
   *  отвечает ЭТИМИ методами — той же стопкой родных страниц «Подключить», что живёт в
   *  «Настройках» (каталог → форма сервиса → «расскажите о себе»), но ПОВЕРХ шторки
   *  мастера: мастер стоит на своём шаге и никуда не уходит, закрытие стопки возвращает
   *  ровно на него.
   *  Данные — те же, что у настроек папок «Чатов» (`NativeMsgsFoldersData`: тема, готовые
   *  страницы, стопка), и разбирает их тот же общий водитель (lib/native-pages.ts):
   *  второй формы страницы в приложении не бывает.
   *  МЕТОДЫ НОВЫЕ (CLAUDE.md запрет №8): смысл уехавших в стор wiz*-методов не тронут.
   *  Поддержку спрашивать ТОЛЬКО через nativeMethod('wizConnOpen') — нет метода (старая
   *  сборка, Android): строки «Добавить аккаунт» человек не видит, а без аккаунта вовсе
   *  работает прежний ход (родное окно → «Подключения»). */
  wizConnOpen?(opts: NativeWizConnData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытую стопку НА МЕСТЕ (доехал каталог, форма ответила ошибкой, сменилась
   *  тема). Полный словарь данных, идемпотентно. */
  wizConnPatch?(opts: NativeWizConnData): Promise<void>;
  /** Убрать стопку подключения (человек её закрыл, мастер ушёл). */
  wizConnClose?(): Promise<void>;
  /** ЭТИМИ ЖЕ МЕТОДАМИ ЖИВЁТ СТРАНИЦА «ДОБАВИТЬ КАНАЛ» (ТЗ 19.09.2026 Р5; вид —
   *  20.09.2026 C2): своего метода у неё нет, это обычная страница той же стопки. Она
   *  просит `present: 'push'` — натив везёт её ШАГОМ ВНУТРЬ уже открытого листа выбора
   *  канала (лист встаёт с навигацией по ключу поля `addPush`, см. lib/wiz-native.ts),
   *  и «назад» возвращает к списку. Листа нет (старая сборка, вариантов не было вовсе) —
   *  страница встаёт своим окном: прежний, допустимый запасной ход. Закрытие стопки
   *  (`wizConnClose`) на успехе проверки уводит и страницу, и сам лист выбора. */

  /** ── РОДНОЙ ЛИСТ ВЫБОРА ШАБЛОНА (владелец 31.08.2026: «при нажатии плюс у ботов
   *  открывать вертикальную шторку, и там выбор шаблонов сразу; нарисовано в вебе для
   *  веба и на swift для приложения; вид — как у листов мастера») ──
   *  Список шаблонов знает ВЕБ (каталог bot_templates на языке кабинета) и присылает
   *  ГОТОВЫЕ ряды — натив ничего не зашивает и не переводит: правка каталога видна в
   *  приложении сразу, без пересборки. Тап по ряду возвращается событием tplPick {id},
   *  утянутый пальцем лист — tplCancel. Повторный вызов при открытом листе ДОПОЛНЯЕТ
   *  его теми же рядами (каталог доехал после кэша), второго листа не заводит.
   *  МЕТОД НОВЫЙ: старые сборки из стора его не имеют (nativeMethod('tplSheet')) — там
   *  открывается прежний веб-каталог. */
  tplSheet?(opts: {
    /** Заголовок листа («Создать бота»). */
    title: string;
    /** Серая строка под заголовком («Выберите, что бот будет делать», П48 12.09.2026).
     *  Ключ НОВЫЙ и необязательный (запрет №8): старая сборка его не читает. */
    hint?: string;
    items: Array<{
      id: string;
      title: string;
      /** Описание шаблона — что бот делает (владелец 31.08.2026). Пусто — строки нет. */
      desc?: string;
      /** Готовая подстрока ряда: категория · сколько шагов. */
      sub: string;
      /** Готовое слово метки «мастер настройки»; пусто — метки нет. */
      mark?: string;
      /** Имя картинки каталога приложения: QuickTile-instagram|telegram|catalog. */
      icon?: string;
      /** Цвет сети для плитки значка (у натива своего разбора hex нет). */
      red?: number;
      green?: number;
      blue?: number;
    }>;
    /** Готовая строка на месте пустого списка (каталог ещё едет или пуст). */
    empty: string;
    theme: 'light' | 'dark';
  }): Promise<void>;
  /** Убрать лист программно (человек ушёл со страницы, открылся мастер). */
  tplSheetClose?(): Promise<void>;
  /** ЗАКРЫТЬ ВЕСЬ РОДНОЙ ФЛОУ СОЗДАНИЯ ОДНИМ УХОДОМ (владелец 02.09.2026, Н33: «после
   *  создания вернуло на „Из шаблона“»). Окно выбора шаблона и мастер стоят стопкой один
   *  над другим (Н10), и снимать их по очереди нельзя: между двумя анимациями виден кадр
   *  «Из шаблона». Натив уводит стопку целиком (дока Apple, dismiss(animated:):
   *  анимируется только ВЕРХНИЙ лист, промежуточные просто убираются из стопки) и
   *  отвечает, когда лист уехал, — веб только после этого открывает страницу бота.
   *  МЕТОД НОВЫЙ: смысл уехавших в стор wizClose/tplSheetClose не трогаем (запрет №8);
   *  старая сборка его не имеет (nativeMethod('createFlowClose')) — там прежний путь. */
  createFlowClose?(): Promise<void>;
  /** Открыть РОДНУЮ строку поиска (владелец 2026-08-02, «как в телеге»): веб-строка над
   *  списком «Скриптов»/«Сообщений» — только вход, сам поиск сверху и клавиатура нативные.
   *  Ввод приходит событием searchQuery {q}, открытие/закрытие — searchState {open}.
   *  Сборка без метода (feature-detect nativeMethod('openSearch')) — обычное веб-поле. */
  openSearch?(opts?: { placeholder?: string }): Promise<void>;
  /** ПАСХАЛКА «быстрые плитки» над поиском (владелец 2026-08-07: «надо сделать нативной»):
   *  полоса плиток-шаблонов живёт ПОД шапкой раздела и НАД строкой поиска, открывается
   *  оттягиванием страницы вниз (родная «резинка», ни кадра на JS), при достаточном ходе
   *  задерживается на три секунды и уезжает сама. Веб только присылает содержимое и ловит
   *  тапы (headerAction 'quick:<key>'); visible=false — убрать (уход с «Ботов»).
   *  Старые сборки метода не имеют (feature-detect) — там остаётся веб-вариант в шапке. */
  configureQuickTiles?(opts: {
    visible: boolean;
    /** Фон страницы кабинета числами: полоса ложится ПОВЕРХ страницы и обязана быть
     *  непрозрачной, а тему с оттенком знает только веб. */
    bgRed?: number;
    bgGreen?: number;
    bgBlue?: number;
    /** Верх строки поиска и её высота в точках экрана: разрез проходит РОВНО по её
     *  середине (владелец 2026-08-08) — координаты знает только веб. */
    searchTop?: number;
    searchHeight?: number;
    /** Цвет самого поля поиска: полоса делается ТЕМНЕЕ него (владелец 2026-08-08), а
     *  кромки надреза — ещё темнее градиентом. */
    inputRed?: number;
    inputGreen?: number;
    inputBlue?: number;
    tiles: Array<{
      key: string;
      title: string;
      sub?: string;
      /** Имя картинки каталога приложения: QuickTile-instagram|telegram|catalog. */
      icon: string;
      /** Цвет сети числами 0..255 (как в confirm — своего разбора hex у натива нет). */
      red: number;
      green: number;
      blue: number;
      /** Стрелка в углу — у плитки «Каталог шаблонов». */
      chevron?: boolean;
    }>;
  }): Promise<{ supported: boolean } | undefined>;
  /** ПОЛОСА ПАСХАЛКИ С КРУЖКАМИ СПРАВКИ — НОВЫЙ, запрет №8 (ТЗ П125, владелец
   *  16.09.2026). Всё то же, что у `configureQuickTiles` (поля читаются тем же кодом и
   *  значат то же самое), плюс кружки-сторис: круглая картинка по адресу, подпись под
   *  ней, тап открывает вертикальное видео стопкой. Старый метод НЕ ТРОГАЕМ ни строкой —
   *  он уехал в стор, и сборки 208–264 обязаны и дальше получать свои четыре плитки.
   *  Сборка без метода (nativeMethod('configureQuickTiles2') === false) получает прежний
   *  `configureQuickTiles` без кружков — это норма старых сборок, а не поломка.
   *  Тап по кружку натив обрабатывает САМ (видео открывает он) и шлёт вебу headerAction
   *  {key: 'story:<id>'} — веб такой ключ только пишет в журнал. */
  configureQuickTiles2?(opts: {
    visible: boolean;
    /** Цвета молний над полосой hex-ами (П126.16): всегда три роли — розовый, голубой
     *  Телеграма, бирюзовый. Ключ аддитивный. */
    bolts?: string[];
    /** САМОЗАКРЫТИЕ ПОЛОСЫ (ТЗ USERTEST-1014 п.2, владелец 14.10.2026): секунды после
     *  последнего касания, через которые открытая полоса уезжает сама; 0 — сама не
     *  закрывается (у пользователя нет ни одного бота), закрыть можно свайпом вверх или
     *  тапом по молнии. Веб шлёт 0 без ботов и 10 при хотя бы одном боте; пока список ботов
     *  не доехал — ключа нет. Ключ АДДИТИВНЫЙ (запрет №8): нет ключа — прежние 4 с. */
    autoCloseSec?: number;
    bgRed?: number;
    bgGreen?: number;
    bgBlue?: number;
    searchTop?: number;
    searchHeight?: number;
    inputRed?: number;
    inputGreen?: number;
    inputBlue?: number;
    tiles: Array<{
      key: string;
      title: string;
      sub?: string;
      icon: string;
      /** ЗНАЧОК ПЛИТКИ — СИСТЕМНЫЙ СИМВОЛ (SF Symbols) вместо картинки `icon` (UT 14.10 итерация 2, п.21: у «Создать
       *  бота» — 'plus'). Ключ АДДИТИВНЫЙ (запрет №8): сборка 251+ рисует символ, 1.1 его не читает и рисует `icon`
       *  (каталог). */
      symbol?: string;
      red: number;
      green: number;
      blue: number;
      chevron?: boolean;
    }>;
    stories: Array<{
      /** 'story:<id>' — этим же ключом уходит headerAction при тапе. */
      key: string;
      /** 'video' — ролик стопкой; 'text' — страница `page` стопкой с нашим веб-видом
       *  (натив её предзагружает). Нет поля — 'video' (сборки до 273). */
      kind?: 'video' | 'text';
      /** Полный https-адрес текстовой версии (с языком кабинета); есть вместе с `video`
       *  — в стопке переключатель «Текст / Видео», подписи `wordText`/`wordVideo`. */
      page?: string;
      wordText?: string;
      wordVideo?: string;
      /** Соседи для навигации в стопке: ключи 'story:<id>' и подписи кнопок (их пишет
       *  владелец в админке; пусто — кнопки нет). Следующий — справа, предыдущий — слева. */
      next?: string;
      nextLabel?: string;
      prev?: string;
      prevLabel?: string;
      wordNext?: string;
      /** https-адрес картинки-подложки (не обязательна; натив берёт кадр ролика). */
      image: string;
      /** Подпись под кружком, на языке кабинета, одна строка. */
      caption: string;
      /** Надпись на превью ролика под кнопкой play (владелец 24.09.2026), на языке
       *  кабинета; пусто или нет поля — надписи нет (сборки до 24.09 ключ не читают). */
      hook?: string;
      /** https-адрес вертикального ролика (mp4/mov) — ОРИГИНАЛ владельца, байт в байт. */
      video: string;
      /** ТЗ HELP-VIDEO (владелец 24.09.2026), ключи аддитивные — старые сборки не читают:
       *  размер оригинала (телефон сверяет скачанный файл), запасные версии для показа из
       *  сети до окончания загрузки (720×1280 и 540×960, H.264), длительность, номер версии
       *  (растёт при замене ролика — входит в адреса файлов). */
      videoBytes?: number;
      videoMid?: string;
      videoLow?: string;
      videoSeconds?: number;
      videoRev?: number;
      /** Строка под превью, когда ролика нет ни на диске, ни в сети (слова — от веба). */
      wordOffline?: string;
      /** Имя системного символа (SF Symbols) значка внизу плитки, например 'film.fill';
       *  пусто или нет — значка нет (переключатель «значки на плитках» в админке). */
      icon?: string;
      /** Цвет плитки числами 0..255 (роль `--green`); нет — натив красит серым. */
      red?: number;
      green?: number;
      blue?: number;
    }>;
    /** true — плитки справки сразу после знака, ДО плиток мастеров; false — после. */
    storiesFirst: boolean;
  }): Promise<{ supported: boolean } | undefined>;
  /** Нативный верх КОРНЕВЫХ страниц («Сценарии»/«Модели», владелец 2026-07-21):
   *  неподвижный заголовок раздела + круглые стеклянные кнопки по краям (SF-иконки).
   *  Конфигурирует shell.tsx по активной панели стека; тапы возвращаются событием
   *  headerAction {key} с ключом кнопки ('top:*'). Старые сборки методов не имеют —
   *  остаётся веб-шапка. */
  /** ВТОРАЯ СТРОКА нативного заголовка корневой страницы (владелец 17.08: «как логин под
   *  именем в переписке»). Отдельный метод, а не параметр configureTopBar: контракт уже
   *  уехавших методов заморожен (CLAUDE.md запрет №8). Пустой текст убирает строку.
   *  Старые сборки метода не имеют — там остаётся веб-строка под шапкой. */
  setTopSubtitle?(opts: { text: string }): Promise<void>;
  configureTopBar?(opts: {
    title: string;
    /** false — режим «без фона» (владелец 2026-07-29): титул обычной краской вместо
     *  белого, стекло кнопок светлое. Старые сборки игнорируют (по умолчанию тёмный). */
    dark?: boolean;
    /** menu — РОДНОЕ UIMenu кнопки (владелец 2026-07-22): пункты как у меню шапки
     *  сценария ({key,label,icon,checked,section}); тап пункта → headerAction {key},
     *  сам тап кнопки при заданном меню события не шлёт. Старые сборки menu игнорируют
     *  (кнопка шлёт свой key — веб показывает свой поповер-фолбэк). */
    left?: {
      key: string;
      icon: string;
      /** Свой глиф base64-PNG (template, красится нативом) — приоритетнее SF-имени icon;
       *  старые сборки поле игнорируют (фолбэк — icon). Пример: монетки токенов. */
      iconPng?: string;
      label?: string;
      menu?: Array<{ key: string; label: string; sub?: string; icon?: string; checked?: boolean; section?: string; sectionTitle?: string }>;
    };
    right?: {
      key: string;
      icon: string;
      iconPng?: string;
      label?: string;
      /** Кружок ЗАЛИТ акцентом темы, глиф белый (владелец 2026-08-06: «кнопку плюс надо
       *  сделать акцентного цвета»). Старые сборки поле игнорируют — кнопка прежняя. */
      accent?: boolean;
      /** ОТБОР ПРИМЕНЁН: значок красится акцентом, кружок НЕ заливается (родной приём
       *  Apple; заливка означает главное действие). Старые сборки поле игнорируют. */
      tint?: boolean;
      menu?: Array<{ key: string; label: string; sub?: string; icon?: string; checked?: boolean; section?: string; sectionTitle?: string }>;
    };
    /** Вторая правая кнопка (владелец 2026-08-02): «+» создания скрипта рядом с фильтром
     *  (с лупой — слева от неё). Старые сборки поле игнорируют — кнопки просто нет. */
    right2?: {
      key: string;
      icon: string;
      iconPng?: string;
      label?: string;
      menu?: Array<{ key: string; label: string; sub?: string; icon?: string; checked?: boolean; section?: string; sectionTitle?: string }>;
    };
    /** Подразделы — системный сегмент-контрол под заголовком (тап → headerAction
     *  'toptab:<key>'). Слать только сборкам с setTopTab (feature-detect nativeMethod).
     *  height в ответе — высота строки шапки ниже safe-area (отступ контента,
     *  --native-top-pad). */
    tabs?: Array<{ key: string; label: string }>;
    selectedTab?: string;
    /** Кнопка-лупа «Моделей» — вплотную слева от правой кнопки (фильтра). Тап открывает
     *  РОДНОЙ поиск: строка над клавиатурой (прижата к keyboardLayoutGuide) + круглый
     *  крестик; ввод построчно уходит событием searchQuery {q}, крестик — searchQuery {q:''}.
     *  placeholder — текст в поисковой строке. Обрабатывается целиком нативом (без headerAction). */
    search?: { key: string; icon: string; label?: string; placeholder?: string };
  }): Promise<{ height?: number; safeTop?: number } | undefined>;
  // safeTop (аддитивно, 23.09.2026) — отступ островка окна в пунктах: веб кладёт его в
  // --safe-top-native (lib/native-inset.ts). Старые сборки поля не отдают.
  clearTopBar?(): Promise<void>;
  /** РОДНОЙ календарь iOS (владелец 2026-07-24): системный UIDatePicker нижним листом,
   *  подписи на локали кабинета. Дни — «YYYY-MM-DD»; ответ без value = отменил. */
  /** Вибро-отклик (владелец 2026-07-25): warning — «так нельзя», success/light — прочее. */
  haptic?(opts: { kind?: 'light' | 'warning' | 'success' }): Promise<void>;
  /** РОДНОЕ ПОДТВЕРЖДЕНИЕ iOS (владелец 2026-07-26): системный action sheet поверх экрана —
   *  вопрос заголовком, слово действия кнопкой (destructive → красная), «Отмена» внизу.
   *  Веб при этом НЕ трогают (ни снимка, ни переезда в слой), поэтому экран не дёргается —
   *  ради чего подтверждение и уводили в веб-шторку. theme/red/green/blue — вид кабинета.
   *  Старые сборки метода не имеют — остаётся веб-лист (feature-detect nativeMethod). */
  confirm?(opts: {
    question: string;
    message?: string;
    confirmLabel: string;
    cancelLabel: string;
    destructive?: boolean;
    theme?: 'light' | 'dark';
    red?: number;
    green?: number;
    blue?: number;
  }): Promise<{ ok?: boolean }>;
  /** РОДНОЙ ЛОГ ОБОЛОЧКИ (владелец 25.08.2026): кольцо последних строк приложения —
   *  переключения вкладок, выезды слоёв, уходы в фон и возвраты, перезагрузки веба,
   *  ошибки моста. Веб прикладывает его к журналу, который человек шлёт из «Диагностики»
   *  в настройках. Клиентских текстов в строках нет. Старые сборки метода не имеют —
   *  спрашивать ТОЛЬКО через nativeMethod('diagLog'), уедет журнал без родной части. */
  diagLog?(): Promise<{ lines?: string[] }>;
  /** Последний тап по пушу (натив держит его в буфере до забора; пусто — не нажимали).
   *  Веб показывает окно с тем же текстом — тап не должен оставаться без ответа. */
  takePushOpen?(): Promise<{ title?: string; body?: string; url?: string }>;
  /** Путь кабинета из Universal Link («/ru/app/bots/<id>»), по которому приложение
   *  попросили открыться. Веб переходит сам — SPA, без перезагрузки со сплэшем. */
  takeOpenPath?(): Promise<{ path?: string }>;
  /** Убрать ДОСТАВЛЕННЫЕ пуши из шторки iOS: ids — id записей центра уведомлений
   *  (userInfo.nid в payload пуша), без ids — все наши. Зовётся после «прочитано» —
   *  прочитанная новость не должна висеть пушем (владелец 2026-08-25). */
  clearDeliveredPush?(opts?: { ids?: string[] }): Promise<void>;
  /** theme/accent — вид КАБИНЕТА, а не системы: окно календаря раньше было всегда
   *  тёмным и в светлой теме било по глазам (владелец 2026-08-09). */
  pickDate?(opts: {
    value?: string;
    min?: string;
    max?: string;
    locale?: string;
    title?: string;
    doneLabel?: string;
    theme?: 'light' | 'dark';
    red?: number;
    green?: number;
    blue?: number;
  }): Promise<{ value?: string }>;
  /** РОДНОЕ ВРЕМЯ СУТОК (владелец 2026-08-14, Э6.1): системное колесо часов и минут тем же
   *  стеклянным окном, что календарь. Вход/выход — «ЧЧ:ММ» (24 часа), ответ без value =
   *  отменил. Отдельный метод, а не расширение `pickDate`: контракт уехавшего в стор
   *  метода не меняется (запрет №8) — старая сборка молча получает наши селекты. */
  pickTime?(opts: {
    value?: string;
    locale?: string;
    title?: string;
    doneLabel?: string;
    theme?: 'light' | 'dark';
    red?: number;
    green?: number;
    blue?: number;
  }): Promise<{ value?: string }>;
  /** ПОЛУШТОРКА ОДНОЙ СТРОКИ (Scriptera A, владелец 14.10.2026 вечер — тема письма): родной лист снизу,
   *  поле и «Готово». Ответ `{value}` — непустой текст; `{}` — закрыли. Наличие метода = сборка умеет
   *  `configureComposer.menu/chip`. */
  askText?(opts: {
    title: string;
    placeholder?: string;
    value?: string;
    doneLabel?: string;
    theme?: 'light' | 'dark';
    red?: number;
    green?: number;
    blue?: number;
  }): Promise<{ value?: string }>;
  /** РОДНОЙ ПРОМЕЖУТОК ДНЕЙ (владелец 2026-08-14): системный календарь iOS, промежуток
   *  набирается двумя тапами (готового промежутка у Apple нет — середину закрашивает наш
   *  код). Дни — «YYYY-MM-DD», ответ без from/to = отменил. Метод падает на сборках без
   *  него и на iOS 15 (системного календаря там ещё нет) — веб ловит и открывает свой. */
  pickDateRange?(opts: {
    from?: string;
    to?: string;
    locale?: string;
    title?: string;
    doneLabel?: string;
    theme?: 'light' | 'dark';
    red?: number;
    green?: number;
    blue?: number;
  }): Promise<{ from?: string; to?: string }>;
  /** Синк выбранного подраздела после свайпа панелей (без эха в веб). */
  setTopTab?(opts: { key: string }): Promise<void>;
  /** Пилюля подразделов за пальцем: прогресс свайпа панелей (scr-pane-drag; to пуст —
   *  упор в крайний подраздел) и доводка после отпускания (scr-pane-settle, той же
   *  длительностью, что панели). Старые сборки методов не имеют. */
  topTabDrag?(opts: { from: string; to?: string; progress: number }): Promise<void>;
  topTabSettle?(opts: { key: string; durMs: number }): Promise<void>;
  /** То же для пилюли вкладок ЛИСТА (свайп режимов формы модели, 2026-07-22). */
  sheetTabDrag?(opts: { from: string; to?: string; progress: number }): Promise<void>;
  sheetTabSettle?(opts: { key: string; durMs: number }): Promise<void>;
  /** НАТИВНОЕ ПРИВЕТСТВИЕ (владелец 26.08.2026). helloState — рисует ли натив
   *  приветствие поверх веба (active) и выбор человека ('examples'|'skip'|''), выбор
   *  отдаётся ОДИН раз (take-семантика, как takePushOpen). Старые сборки метода не
   *  имеют — веб показывает своё приветствие (feature-detect nativeMethod('helloState')). */
  helloState?(): Promise<{ active: boolean; choice: string }>;
  /** Веб сообщает нативу, вошли ли в приложение (по флагу натив решает на следующем
   *  холодном старте, показывать ли приветствие) и свои сохранённые тему и язык —
   *  приветствие стартует с них, а не с системных (иначе рассинхрон с вебом). */
  helloAuthState?(opts: { in: boolean; theme?: string; lang?: string }): Promise<void>;
  /** ВЕБ-ГЕЙТ ВХОДА ОТДАЁТ ШАГ РОДНОМУ ОНБОРДИНГУ (волна 3). Родной поток шагов заводится
   *  только регистрацией в приложении, а на страницы гейта (`/verify-phone`, `/welcome`)
   *  человек попадает и другими путями — вошёл не родной формой, поток прервали, прямой
   *  адрес — и видел там полноэкранные ВЕБ-страницы. Теперь страница зовёт этот метод и
   *  сама не рисуется, а шаг показывает натив (шторка онбординга). Метод НОВЫЙ (запрет
   *  №8): сборка без него ничего не знает — веб рисует свою страницу, как раньше. */
  /** ВОПРОС «ТАКОЙ БОТ УЖЕ ЕСТЬ» РОДНЫМ ЛИСТОМ (волна 3). Веб-лист рисуется порталом в
   *  `.cab` — внутри webHost, а родная шторка мастера стоит НАД ним, и человек вопроса
   *  не видел вовсе. Ответ приходит одним промисом (как у `confirm`): 'open' — настроить
   *  существующего, 'force' — всё равно создать нового, 'none' — лист закрыли.
   *  Метод НОВЫЙ (запрет №8); нет его — веб рисует прежний лист.
   *  С 08.09.2026 (раздел Л правило 4) обхода «всё равно создать» нет: веб шлёт ПУСТОЙ
   *  `forceLabel`, свежая сборка на пустом слове кнопку не рисует, а 'force' от старой
   *  установки веб трактует как «отведи к хозяину слова». Имя и форма метода — прежние. */
  botExistsAsk?(opts: {
    head: string;
    question: string;
    openLabel: string;
    forceLabel: string;
    closeLabel: string;
    theme: 'light' | 'dark';
  }): Promise<{ choice: 'open' | 'force' | 'none' }>;
  onbStep?(opts: {
    step: 'phone' | 'account' | 'niche';
    /** Для шага «добавьте аккаунт»: что уже подключено (подключённое не предлагаем). */
    hasTg?: boolean;
    hasIg?: boolean;
  }): Promise<{ ok: boolean }>;
  /** Мини-тур в шторке: веб отдаёт нативу число слайдов и текущий — боковой свайп
   *  ведёт натив (родная физика), позиция приезжает в window.__scrTour/__scrTourEnd.
   *  count: 0 выключает жест. Старые сборки метода не имеют (feature-detect). */
  tourState?(opts: { count: number; index: number }): Promise<void>;
  /** «Зарегистрироваться»/«Пропустить» в веб-туре: попросить натив открыть ГОТОВУЮ
   *  нативную форму (возврат нативных форм, владелец 28.08). Старые сборки метода не
   *  имеют — веб показывает свою форму (feature-detect nativeMethod('authOpen')). */
  authOpen?(opts: { mode: 'register' | 'login' }): Promise<void>;
  /**
   * ВХОД ПРОВАЙДЕРА СИСТЕМНЫМ ОКНОМ ВХОДА (UT 14.10.2026 п.11А, сборка 250+):
   * ASWebAuthenticationSession вместо Safari — окно берёт куки Safari, само закрывается по
   * адресу возврата `scriptera://connected` и вкладок в Safari не оставляет. `url` — https-
   * адрес входа из `POST /api/cabinet/connect/<provider>`. Ответ приходит ПОСЛЕ закрытия
   * окна: `{ok: true, url}` (адрес возврата) или `{ok: false, error}` — `badUrl`
   * (не https), `cancelled` (человек закрыл окно), `failed` (окно не открылось или вход
   * сорвался), `busy` (уже открыто другое окно входа).
   *
   * INVARIANT(scriptera): МЕТОД НОВЫЙ, имя, аргумент и форма ответа заморожены с первой
   * сборки (CLAUDE.md запрет №8). Имя `authOpen` ЗАНЯТО формой регистрации и уехало в стор
   * (1.1-245) — переиспользовать его нельзя. Проверка ТОЛЬКО `nativeMethod('authSession')`;
   * сборка без метода идёт прежней дорогой (window.open → Safari). Зеркало —
   * scriptera-app ScripteraNativePlugin.authSession. Успех всё равно ловит опрос
   * подключений (use-connect-via.ts): ответ окна — только подсказка, когда смотреть.
   */
  authSession?(opts: {
    url: string;
  }): Promise<{ ok: boolean; url?: string; error?: 'badUrl' | 'cancelled' | 'failed' | 'busy' }>;
  /** РОДНОЕ ОКНО-СООБЩЕНИЕ (волна 3; НОВЫЙ метод — запрет №8, старых не трогаем).
   *  Две подачи одного окна, обе повторяют веб один в один:
   *   • style 'sheet' — НИЗКИЙ лист с заголовком и текстом (веб: panel.open с
   *     sheet/sheetDetent 0.4 — «Оставьте хотя бы один способ», «Уведомления выключены»);
   *   • style 'card'  — КАРТОЧКА ПО ЦЕНТРУ со значком, заголовком, текстом и двумя
   *     кнопками (веб: .push-ask/.push-card — «Включить уведомления?» и пересказ пуша).
   *  Ответ `ok: true` — нажали первую кнопку; вторая (или закрытие) — false. Слова и
   *  значок считает веб: в Swift строк не бывает. */
  notice?(opts: {
    style: 'sheet' | 'card';
    /** SF-символ значка карточки (у листа значка нет). */
    icon?: string;
    title: string;
    text: string;
    /** Подпись главной кнопки. Пусто — кнопки нет (лист только с текстом). */
    primary?: string;
    /** Подпись второй кнопки (обычной, без заливки). Пусто — второй кнопки нет. */
    secondary?: string;
    /** ВИБРАЦИЯ ОТКАЗА в момент показа (владелец 10.09.2026, «Важные»: снятие последнего
     *  способа доставки — тумблер молча возвращался). 'error' — системный отклик ошибки
     *  iOS. Ключ ДОБАВОЧНЫЙ и необязательный: сборка без него показывает окно как всегда
     *  (CLAUDE.md запрет №8 — форма аргументов не меняется). */
    haptic?: 'error';
    theme?: 'light' | 'dark';
    red?: number;
    green?: number;
    blue?: number;
    /** КАКАЯ КНОПКА (ответ сборки 251+, аддитивно): 'primary' / 'secondary' / 'close' — закрытие отличимо от второй
     *  кнопки. Сборка 1.1 ключа не шлёт — тогда смысл только у `ok`. */
  }): Promise<{ ok?: boolean; choice?: 'primary' | 'secondary' | 'close' }>;
  /** НОВАЯ ВЕРСИЯ САЙТА — ПЕРЕЗАПУСК РЕШАЕТ ПРИЛОЖЕНИЕ (ТЗ релиза П3, 29.09.2026; НОВЫЙ
   *  метод — запрет №8, старых не трогаем). Перезагрузка страницы под живыми родными
   *  экранами «накрывала» приложение до ручного убийства; холодный запуск работает
   *  всегда — поэтому сайт сам больше не перезагружается, а отдаёт решение оболочке:
   *  она поднимает заставку, сносит родные экраны и грузит веб заново (как холодный старт),
   *  в удобный момент. `from`/`to` — сборка страницы и сборка на сервере ('' — неизвестна);
   *  `urgent: true` — страница уже сломана (обрыв чанка, мёртвая сборка), ждать нельзя.
   *  Ответ `handled: true` — оболочка взяла перезапуск на себя; иначе сайт
   *  перезагружается сам, как раньше. Поддержку спрашивать ТОЛЬКО через
   *  nativeMethod('updateReady'); нет метода — прежний `location.reload()`. */
  updateReady?(opts: { from: string; to: string; urgent?: boolean }): Promise<{ handled?: boolean }>;
  /** Была ли ПОСЛЕДНЯЯ загрузка веба перезапуском оболочки по новой версии (ТЗ релиза
   *  П3): `reloaded: true` и причина («возврат из фона», «корень, тихо», …) — ОДИН раз,
   *  дальше флаг сброшен. Веб зовёт на старте для журнала (`boot` с `nativeReload`).
   *  НОВЫЙ метод (запрет №8): спрашивать ТОЛЬКО через nativeMethod('updateState'). */
  updateState?(): Promise<{ reloaded: boolean; reason: string }>;
  /** ПЕРЕКЛЮЧАТЕЛЬ СЕРВЕРА (владелец 01.10.2026; НОВЫЙ метод — запрет №8). Приложение
   *  запоминает адрес сервера и перезапускает веб уже на нём: прод (`https://scriptera.ai`)
   *  или staging (`https://dev.scriptera.ai`, docs/HOW-IT-WORKS.md §2). Пустой `origin` —
   *  вернуться к серверу сборки. Натив принимает ТОЛЬКО https на scriptera.ai и его
   *  поддоменах; чужой адрес — `ok: false`, ничего не меняется. Выбор живёт в приложении
   *  на этом телефоне и больше ни на что не влияет. Строку «Сервер» в «Аккаунте» видят
   *  только свои учётки (lib/our-accounts.ts); спрашивать поддержку ТОЛЬКО через
   *  nativeMethod('serverSet') — в сборках без метода строки нет. */
  serverSet?(opts: { origin: string }): Promise<{ ok?: boolean; origin?: string }>;
  /** ── РОДНОЙ РАЗДЕЛ «АККАУНТ» (docs/NATIVE-ACCOUNT-PLAN.md, этап А; НОВЫЕ методы —
   *  запрет №8, старых не трогаем). Раздел РИСУЕТ натив (AccountScreen.swift), веб —
   *  мозг: собирает данные и ГОТОВЫЕ локализованные строки, шлёт их сюда и разбирает
   *  тапы событиями accountTap. Экран стоит между веб-вью и родным хромом: кружок темы,
   *  колокольчик и нижнее меню — прежние. Поддержку спрашивать ТОЛЬКО через
   *  nativeMethod('accountOpen'); нет метода (старая сборка, Android) — веб-страница
   *  раздела живёт как раньше. */
  accountOpen?(opts: NativeAccountData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытый раздел НА МЕСТЕ: смена фото/имени, языка (все строки приезжают
   *  заново), темы, содержимого подстраниц. Полный словарь данных, натив перерисовывает
   *  без анимаций. */
  accountPatch?(opts: NativeAccountData): Promise<void>;
  /** Убрать родной раздел (ушли с вкладки, вышли из кабинета). */
  accountClose?(): Promise<void>;
  /** ПРИЗНАК СПОСОБНОСТИ (10.09.2026): стопка «Аккаунта» переживает смену вкладки и хром
   *  корня считает вкладку (канон Apple). Тела нет — важен факт метода в списке:
   *  `nativeMethod('accountStackKeep')` включает память вкладок (shell.tsx) и удержание
   *  стопки вне раздела (settings.tsx); старые сборки без него — прежнее поведение 03.09. */
  accountStackKeep?(): Promise<{ ok?: boolean } | void>;
  /** ПРИЗНАК СПОСОБНОСТИ (10.09.2026, п.14б): страница «Аккаунта» умеет текстовую кнопку
   *  справа в шапке («Править»/«Готово»). Форма взята из натива: AccountScreen.swift
   *  `accountTopAction` аргументов не читает и отвечает `{ ok: true }` — важен только факт
   *  метода в списке. Веб зовёт его ТОЛЬКО через nativeMethod('accountTopAction')
   *  (contacts.tsx, messages-folders.tsx); старые сборки без него держат прежние кнопки
   *  в пузыре. */
  accountTopAction?(): Promise<{ ok?: boolean } | void>;
  /** ПРИЗНАК СПОСОБНОСТИ (19.09.2026): у страницы «Кнопка» письма есть круглая галочка
   *  «готово» справа в шапке (MessageScreen.swift, MsgButtonPage.onDone → msgTap 'btn-done').
   *  Веб при нём держит правки кнопки в черновике и кладёт в письмо только по галочке;
   *  старые сборки без метода правят письмо сразу, как раньше. Тела нет, аргументов нет. */
  msgButtonDone?(): Promise<{ ok?: boolean } | void>;
  /** ── РОДНАЯ ВКЛАДКА «ЧАТЫ» — СПИСОК ПЕРЕПИСОК (docs/NATIVE-TABS-PLAN.md; НОВЫЕ методы —
   *  запрет №8, старых не трогаем). Список РИСУЕТ натив (MsgsScreen.swift), веб — мозг:
   *  он и так держит живой опрос переписок, знает правила каналов и язык кабинета и
   *  отдаёт сюда ГОТОВЫЕ ряды. Тап по ряду возвращается событием msgsTap,
   *  а переписку открывает ВЕБ прежним путём — через СУЩЕСТВУЮЩИЙ chatOpen (родной экран
   *  переписки, трек Н; его и ChatScreen не трогаем ни строкой). Поддержку спрашивать
   *  ТОЛЬКО через nativeMethod('msgsOpen'); нет метода (старая сборка, Android) — в
   *  разделе живёт прежний веб-список. */
  msgsOpen?(opts: NativeMsgsData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытый список НА МЕСТЕ: приехали новые переписки, сменился отбор, язык
   *  или тема. Полный словарь данных, идемпотентно. */
  msgsPatch?(opts: NativeMsgsData): Promise<void>;
  /** Убрать родной список (вышли из кабинета, оболочка размонтировалась). */
  msgsClose?(): Promise<void>;
  /** ── НАСТРОЙКИ ПАПОК «ЧАТОВ» (ТЗ M.4, владелец 09.09.2026; НОВЫЕ методы — запрет №8,
   *  старых не трогаем). Полушторка со списком папок и редактором папки: содержимое
   *  страниц собирает ВЕБ теми же моделями, что страницы «Аккаунта»
   *  (NativeAccountPage/Group/Field) — второй формы страницы в приложении не бывает.
   *  Тапы возвращаются событиями msgsFoldersTap/Set/Back, и смысл у них РОВНО тот же,
   *  что у accountTap/accountSet/accountBack. Поддержку спрашивать ТОЛЬКО через
   *  nativeMethod('msgsFoldersSheet'); нет метода (старая сборка, Android) — веб
   *  открывает свою шторку «Папки». */
  msgsFoldersSheet?(opts: NativeMsgsFoldersData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытую полушторку НА МЕСТЕ: папку переименовали, условие переключили,
   *  сменились тема или язык. Полный словарь данных, идемпотентно. */
  msgsFoldersPatch?(opts: NativeMsgsFoldersData): Promise<void>;
  /** Убрать полушторку настроек папок. */
  msgsFoldersClose?(): Promise<void>;
  /** ── РОДНАЯ ВКЛАДКА «БОТЫ» (docs/NATIVE-TABS-PLAN.md; НОВЫЕ методы — запрет №8,
   *  старых не трогаем). Список ботов РИСУЕТ натив (BotsScreen.swift), веб остаётся
   *  мозгом: держит данные (usePersistentData), считает отбор и цифры, отдаёт ГОТОВЫЕ
   *  локализованные строки и разбирает тапы событиями botsTap. Экран живёт ВНУТРИ
   *  вкладки «Боты» над webHost — родной хром (шапка с «+», нижнее меню, полоса плиток)
   *  остаётся прежним. Поддержку спрашивать ТОЛЬКО через nativeMethod('botsOpen'); нет
   *  метода (старая сборка, Android) — веб-список раздела живёт как раньше. */
  /** Отвечает {ok:false}, если вкладок ещё нет (нижнее меню собирается позже) — экран
   *  не встал, и водитель обязан повторить, а не оставить человека с пустой страницей. */
  botsOpen?(opts: NativeBotsData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытый список НА МЕСТЕ: другой отбор, вкл/выкл, создание, удаление,
   *  смена языка и темы. Полный словарь данных, натив перерисовывает без анимаций. */
  botsPatch?(opts: NativeBotsData): Promise<void>;
  /** Убрать родной список (вышли из кабинета). */
  botsClose?(): Promise<void>;
  /** ── РОДНЫЕ ШТОРКИ ПЕРЕПИСКИ (docs/NATIVE-TABS-PLAN.md, волна 2, Опус-В2Г; НОВЫЕ
   *  методы — запрет №8, старых не трогаем). Сам родной экран переписки (chatOpen) и
   *  его лента не меняются ни строкой: это ДВЕ шторки поверх него.
   *
   *  КАРТОЧКА СОБЕСЕДНИКА: лист рисует натив (фото, имя, факты, действие), веб остаётся
   *  мозгом — он и сейчас держит эту карточку (DialogInfoSheet): освежает профиль,
   *  считает остаток окна ответа, знает слова. Тап по строке-действию возвращается
   *  событием chatInfoTap, уход листа — chatInfoGone (веб на него возвращает родное поле
   *  ответа, снятое на время карточки). Строки — ТОТ ЖЕ протокол, что у родных страниц
   *  «Аккаунта» (NativeAccountField): второй формы строки в приложении не бывает. */
  chatInfoOpen?(opts: NativeChatInfoData): Promise<void>;
  /** Обновить открытую карточку НА МЕСТЕ: доехал свежий профиль, переключили замок
   *  диалога, сменились тема или язык. Полный словарь, идемпотентно. */
  chatInfoPatch?(opts: NativeChatInfoData): Promise<void>;
  chatInfoClose?(): Promise<void>;
  /** ОТПРАВКА ФАЙЛА: родной выбор (снимок и видео из библиотеки или документ) и родное
   *  подтверждение-превью. Что можно приложить, знает ТОЛЬКО веб (правила каналов) — он
   *  и решает, какие источники показать и какой предел у канала. Подтверждённый файл
   *  возвращается событием chatFile, а заливку и отправку ведёт веб ПРЕЖНИМ путём
   *  (второго пути отправки в переписку не заводим). */
  chatAttachPick?(opts: NativeChatAttachAsk): Promise<void>;
  /** ── РОДНАЯ СТРАНИЦА СТАТИСТИКИ АККАУНТА (владелец 10.09.2026, BACKLOG «обход iOS
   *  10.09» п.16: раздел «Аналитика» снят, статистика соцсети живёт в карточке
   *  подключения). Страницу РИСУЕТ натив (AnalyticsScreen.swift, `AnaPageView`), веб
   *  остаётся мозгом: держит данные (статистика Instagram или бота Telegram), считает
   *  период, форматирует числа и шлёт ГОТОВЫЕ строки и уже посчитанные точки графика.
   *  Открывается СТОПКОЙ поверх карточки подключения (DeckOverlay). Прежние методы
   *  раздела (anaOpen/anaPatch/anaClose) сняты вместе с разделом: веб их больше не зовёт,
   *  а старые сборки без `anaPageOpen` получают веб-шторку статистики (feature-detect).
   *  Поддержку спрашивать ТОЛЬКО через nativeMethod('anaPageOpen'). Отвечает {ok:false},
   *  если экран не встал — водитель повторит. */
  anaPageOpen?(opts: NativeAnaPageData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытую страницу НА МЕСТЕ: доехали данные, сменился период, тема. */
  anaPagePatch?(opts: NativeAnaPageData): Promise<void>;
  /** Убрать страницу (ушли с карточки, закрыли лист). */
  anaPageClose?(): Promise<void>;
  /** ── РОДНАЯ ФОРМА РАЗОВОЙ РАССЫЛКИ (docs/NATIVE-TABS-PLAN.md, волна 2; НОВЫЕ методы —
   *  запрет №8, старых не трогаем). Анкету «кому / что / когда» РИСУЕТ натив
   *  (BcastScreen.swift) теми же строками, что страницы «Аккаунта» (NativeAccountGroup),
   *  а веб остаётся мозгом: он держит ответы, считает видимость строк, ПРОВЕРЯЕТ их и
   *  создаёт рассылку прежним конвейером мастера. Натив ничего не переводит и не решает;
   *  платного на экране нет вовсе (запрет №7). Поддержку спрашивать ТОЛЬКО через
   *  nativeMethod('bcastOpen'); нет метода (старая сборка, Android) — анкету ведёт
   *  прежний путь (родной мастер wiz* или веб-слой). */
  bcastOpen?(opts: NativeBcastData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытую форму НА МЕСТЕ: ответ поменялся, доехал счётчик «получат N»,
   *  появилась подсветка незаполненного, сменилась тема. Полный словарь, идемпотентно. */
  bcastPatch?(opts: NativeBcastData): Promise<void>;
  /** Убрать форму (создали рассылку, ушли со страницы). */
  bcastClose?(): Promise<void>;
  /** ── РОДНОЙ РАЗДЕЛ «РАССЫЛКИ» (волна 3; НОВЫЕ методы — запрет №8, старых не трогаем).
   *  Витрину карточек, героя пустого раздела, архив и страницы, которые с неё
   *  открываются (цифры рассылки, «О сценарии», «Дубликат», «Подключите аккаунт»),
   *  РИСУЕТ натив (BcastListScreen.swift), а веб остаётся мозгом: те же данные, тот же
   *  отбор и те же руки, что у веб-витрины (broadcasts.tsx + scenarios.tsx). Поддержку
   *  спрашивать ТОЛЬКО через nativeMethod('bcastListOpen'); нет метода (старая сборка,
   *  Android) — раздел открывается прежней веб-шторкой. */
  bcastListOpen?(opts: NativeBcastListData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытый раздел НА МЕСТЕ: доехали цифры, сменился статус рассылки,
   *  открылась страница. Полный словарь, идемпотентно. */
  bcastListPatch?(opts: NativeBcastListData): Promise<void>;
  /** Убрать раздел (ушли со страницы, с которой его открыли). */
  bcastListClose?(): Promise<void>;
  /** ── РОДНОЕ ОКНО «СООБЩЕНИЕ» (ТЗ docs/MESSAGE-COMPOSER-PLAN.md, Г6; НОВЫЕ методы —
   *  запрет №8, старых не трогаем). ОДНО окно на всех, кто собирает письмо: разовую
   *  рассылку, шаг цепочки и сообщения шаблонов.
   *
   *  Экран РИСУЕТ натив (MessageScreen.swift) — КАРТОЧКОЙ-ПРОТОТИПОМ письма (ТЗ И9.6,
   *  04.09.2026): один пузырь со слотами, и он же редактор. Веб остаётся мозгом целиком:
   *  держит черновик, считает по модели, что с чем уходит одним сообщением
   *  (@scriptera/db/tg-message), считает разметку (message-marks.ts), пределы берёт из
   *  словаря канала и присылает сюда ГОТОВЫЕ строки и уже посчитанные слоты. В Swift нет
   *  ни одного правила, ни одного предела и ни одного переводимого слова.
   *
   *  ФОРМА `data` СМЕНИЛАСЬ вместе с видом окна (этапы Г14 + Г15 — пара): имена методов
   *  прежние, а карточка приезжает полями `card` / `text` / `attach` вместо прежних
   *  `parts` / `format` / `attach`. Сборка со СТАРЫМ MessageScreen новой формы не поймёт,
   *  поэтому Г15 катится вместе с Г14.
   *
   *  Поддержку спрашивать ТОЛЬКО через nativeMethod('msgOpen'); нет метода (старая
   *  сборка, Android) — окно открывается прежней веб-шторкой (message-composer.tsx).
   *  Отвечает {ok:false}, если экран не встал, — водитель поднимет его следующим патчем. */
  msgOpen?(opts: NativeMsgData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытое окно НА МЕСТЕ: набрали букву, приложили файл, доехала подготовка
   *  голосового, сменились тема или язык. Полный словарь, идемпотентно. */
  msgPatch?(opts: NativeMsgData): Promise<void>;
  /** Убрать окно (письмо забрали, ушли со страницы). */
  msgClose?(): Promise<void>;
  /** ── РОДНОЙ ЭКРАН «КТО ПОЛУЧИТ» (ТЗ docs/MESSAGE-COMPOSER-PLAN.md, И4, этап Г11;
   *  НОВЫЕ методы — запрет №8, старых не трогаем). ОДИН экран на все места, где
   *  спрашивают «кому»: разовая рассылка и письмо цепочки.
   *
   *  Экран РИСУЕТ натив (AudienceScreen.swift) — списком ЖИВЫХ ЛЮДЕЙ с поиском, листом
   *  условий и режимом галочек. Веб остаётся мозгом целиком: держит условия отбора,
   *  спрашивает людей и счётчик ТОЙ ЖЕ ручкой, что веб-экран, готовит фото и плитки
   *  каналов и присылает сюда ГОТОВЫЕ строки, слова и числа. В Swift нет ни одного
   *  правила отбора, ни одного предела и ни одного переводимого слова.
   *
   *  Поддержку спрашивать ТОЛЬКО через nativeMethod('audOpen'); нет метода (старая
   *  сборка, Android) — экран открывается прежней веб-шторкой (audience-screen.tsx).
   *  Отвечает {ok:false}, если экран не встал, — водитель поднимет его следующим патчем. */
  audOpen?(opts: NativeAudData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытый экран НА МЕСТЕ: доехали люди и счётчик, поменялось условие,
   *  включили галочки, сменились тема или язык. Полный словарь, идемпотентно. */
  audPatch?(opts: NativeAudData): Promise<void>;
  /** Убрать экран (условия забрали, ушли со страницы). */
  audClose?(): Promise<void>;
  /** ── РОДНЫЕ «ПОЛУЧЕННЫЕ» (лента центра уведомлений; НОВЫЕ методы — запрет №8).
   *  Ленту РИСУЕТ натив (InboxScreen.swift), веб остаётся мозгом: он держит ленту
   *  (общий inboxStore), режет её по дням, прячет платёжное в приложении (запрет №7),
   *  отмечает прочитанное своей же ручкой и догружает страницы. Тап и «прочитать всё»
   *  возвращаются событиями. Поддержку спрашивать ТОЛЬКО через nativeMethod('inboxOpen');
   *  нет метода (старая сборка, Android) — центр открывается прежней веб-шторкой. */
  inboxOpen?(opts: NativeInboxData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытую ленту НА МЕСТЕ: доехала страница, погасла точка непрочитанного,
   *  сменилась тема или язык. Полный словарь, натив перерисовывает без анимаций. */
  inboxPatch?(opts: NativeInboxData): Promise<void>;
  /** Убрать ленту (ушли из кабинета, закрыли центр со стороны веба). */
  inboxClose?(): Promise<void>;
  /** ── РОДНЫЕ ОКНА КАРТОЧКИ БОТА (docs/NATIVE-TABS-PLAN.md, волна 3, зона «Боты»;
   *  НОВЫЕ методы — запрет №8, старых не трогаем). ОДИН экран на ВСЕ окна, которые
   *  раньше открывались веб-шторками с карточки и из её «⋯»: страница бота с цифрами и
   *  журналом запусков, деталь одного прогона, «О сценарии», «Создать дубликат»,
   *  причины «не включился / не выключился / не удалилось». Второго экрана не заводим:
   *  у веба это тоже ОДИН механизм шторки (EDITOR_SHEET), меняется только тело.
   *
   *  Устройство — то же, что у «Аналитики»: рисует натив, веб остаётся мозгом (ходит на
   *  сервер, считает слова и числа, ведёт сохранение и создание) и присылает КАТАЛОГ
   *  страниц со стопкой; намерения возвращаются событиями statsTap/statsSet/statsBack.
   *  Поддержку спрашивать ТОЛЬКО через nativeMethod('statsOpen'); нет метода (старая
   *  сборка, Android) — открываются прежние веб-шторки.
   *  Отвечает {ok:false}, если экран не встал, — водитель повторит. */
  statsOpen?(opts: NativeStatsData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытое окно НА МЕСТЕ: доехали данные, догрузилась страница журнала,
   *  сменились тема или язык. Полный словарь данных, идемпотентно. */
  statsPatch?(opts: NativeStatsData): Promise<void>;
  /** Убрать окно (закрыли со стороны веба, ушли из раздела). */
  statsClose?(): Promise<void>;
  /** ССЫЛКА КАРТОЧКИ БОТА («Открыть форму», «Открыть чат») — ЛИСТОМ-СТОПКОЙ поверх
   *  кабинета вместо системного браузера (Н38, владелец 02.09.2026). Страницу рисует
   *  штатный внутренний браузер Apple (SFSafariViewController), подача — общая колода.
   *  НОВЫЙ метод (запрет №8): спрашивать ТОЛЬКО через nativeMethod('webSheetOpen'), нет
   *  метода (старая сборка, Android, браузер) — ссылка открывается прежним новым окном. */
  webSheetOpen?(opts: { url: string; theme: 'light' | 'dark' }): Promise<{ ok?: boolean } | void>;
  /** СПРАВКА (/help) ЛИСТОМ-СТОПКОЙ С НАШИМ СОДЕРЖИМЫМ (владелец 09.09.2026, ТЗ этап G
   *  п.2): тот же системный лист и та же колода, что у остальных стопок, но внутри наш
   *  веб-вид и наша шапка (крестик слева, название по центру) — без адресной строки и
   *  кнопок Сафари. `title` — слово шапки на языке кабинета (в нативе переводимых строк
   *  нет). НОВЫЙ метод (запрет №8): спрашивать ТОЛЬКО через nativeMethod('helpSheetOpen'),
   *  нет метода (старая сборка, Android, браузер) — справка открывается прежним путём
   *  (webSheetOpen, а без него — новым окном). */
  helpSheetOpen?(opts: {
    url: string;
    theme: 'light' | 'dark';
    title: string;
  }): Promise<{ ok?: boolean } | void>;
  /** РОДНАЯ ШТОРКА «КАК ПОЛУЧИТЬ ТОКЕН» (ТЗ USERTEST-1014 п.15, владелец 14.10.2026): та же, что в онбординге
   *  (NativeAddAccountView), — из формы «Подключить Telegram» в «Подключениях» по строке «Как получить токен ›».
   *  Без аргументов: тексты — словарь натива (native.addacc.tgHow*). НОВЫЙ метод (запрет №8, сборка 250+):
   *  спрашивать ТОЛЬКО через nativeMethod('tgTokenHowOpen'); нет метода — веб открывает страницу стопкой с той
   *  же инструкцией (connection-forms, TG_HOW_PAGE). */
  tgTokenHowOpen?(): Promise<{ ok?: boolean } | void>;
  /** ── СПРАВКА РОДНЫМИ ЭКРАНАМИ (владелец 10.09.2026; НОВЫЕ методы — запрет №8,
   *  старых не трогаем). Раздел рисует натив (HelpScreen.swift) тремя экранами стопки
   *  (разделы → статьи → статья), а веб остаётся мозгом: читает тексты справки своей
   *  ручкой (/api/help), разбирает их прежним `parseMarkdown` и присылает сюда ГОТОВЫЕ
   *  страницы — теми же моделями, что страницы «Аккаунта» (NativeAccountGroup/Field/Hero),
   *  плюс блоки статьи. Натив ничего не переводит, не отбирает и в сеть не ходит.
   *
   *  Поддержку спрашивать ТОЛЬКО через nativeMethod('helpOpen'); нет метода (старая
   *  сборка, Android, браузер) — справка открывается ПРЕЖНИМ путём (helpSheetOpen с
   *  нашим веб-видом, а без него webSheetOpen или новое окно), см. useOpenHelp. */
  helpOpen?(opts: NativeHelpData): Promise<{ ok?: boolean } | void>;
  /** Обновить открытую справку НА МЕСТЕ: доехали блоки статьи, сменился отбор поиска,
   *  язык или тема. Полный словарь данных, идемпотентно. */
  helpPatch?(opts: NativeHelpData): Promise<void>;
  /** Убрать справку (закрыли лист, ушли из кабинета). */
  helpClose?(): Promise<void>;
  /** События натива: tabSelected — тык по нативной вкладке; layerPopped — человек смахнул
   *  боковой слой от левого края (веб в ответ делает шаг назад и зовёт layerPopCommit);
   *  headerAction — тап в нативной шапке сценария ({key}: 'close'|'title'|пункт меню). */
  addListener?(
    event:
      | 'tabSelected'
      | 'layerPopped'
      // Родной экран ушёл (чат, мастер, письмо, аккаунт, слои) — оболочка просит слить
      // журнал на сервер: {screen}. Канон docs/concepts/DIAGNOSTICS-CANON.md; слушает
      // startAutoJournal (client-report). Старые сборки события не шлют — это не ошибка.
      | 'diagFlush'
      // Отступ островка окна изменился ({top}) — lib/native-inset.ts. Старые сборки
      // события не шлют — это не ошибка (веб живёт на env).
      | 'safeArea'
      // Тап по пушу лёг в буфер натива (П75, 12.09.2026): веб зовёт takePushOpen сразу, не
      // дожидаясь следующего показа экрана. Старые сборки события не шлют — это не ошибка.
      | 'pushOpen'
      | 'headerAction'
      | 'searchQuery'
      | 'searchState'
      // Родной композер чата (Трек 2): ввод построчно, тап отправки, тап микрофона,
      // изменение занимаемой высоты (pad для низа ленты).
      | 'composerInput'
      | 'composerSubmit'
      | 'composerMic'
      | 'composerCancel'
      | 'composerResize'
      // Тап по скрепке родного поля (MESSAGES М12): выбор и отправку файла ведёт веб.
      | 'composerAttach'
      // Scriptera A: выбор в меню канала внутри капсулы {key, item} и тап по плашке темы {key}.
      | 'composerMenuPick'
      | 'composerChipTap'
      // Клавиатура-поверх (2026-07-26): перекрытие вьюпорта клавиатурой в px (0 — закрыта).
      | 'kbChange'
      // Родная переписка (трек Н): намерения человека с родного экрана. chatBack — ушёл
      // назад (кнопкой или жестом), chatInfo — карточка собеседника, chatOlder — «раньше»,
      // chatSubmit {text} — отправить, chatAttach — скрепка, chatMic/chatCancel — голос,
      // chatOpenUrl {url} — ссылка из пузыря, chatOpenBot {id} — подпись «Бот · …»,
      // chatVisible — экран снова показался (просит свежую ленту).
      | 'chatBack'
      | 'chatInfo'
      | 'chatOlder'
      | 'chatSubmit'
      | 'chatAttach'
      | 'chatMic'
      | 'chatCancel'
      | 'chatOpenUrl'
      | 'chatOpenBot'
      | 'chatVisible'
      // Нативное приветствие (владелец 26.08.2026): человек выбрал на нативном экране
      // {key: 'examples' | 'skip'} — веб открывает примеры или остаётся на входе.
      | 'helloChoice'
      // Смена языка на нативном приветствии {key: 'ru' | 'en'}: веб (страница входа
      // под оверлеем) запоминает язык и перегружается на нужную локаль.
      | 'helloLang'
      // Смена темы на нативном приветствии {key: 'light' | 'dark'}: веб ставит
      // data-theme и запоминает выбор — без перезагрузки.
      | 'helloTheme'
      // Родной мастер анкеты: намерения человека с родного экрана. wizNext {values} —
      // «Дальше»/«Создать бота» со всеми ответами шага, wizBack — назад, wizChange
      // {name,value} — правка поля (веб пересчитывает видимость и шлёт wizPatch),
      // wizPick {name} — тап по полю, за содержимым которого натив идёт к вебу
      // (публикации), wizPostsMore — «Показать ещё» в сетке публикаций, wizPhoto
      // {name,data} — снимок из системного выбора фото (его заливает веб),
      // wizPreviewTap — кнопка-телефон, wizCancel — «Отмена» или утянутая пальцем шторка.
      | 'wizNext'
      | 'wizBack'
      | 'wizChange'
      | 'wizPick'
      | 'wizPostsMore'
      | 'wizPhoto'
      // wizFile {name, fileName, mime, bytes, status, body?, code?} — ход файла из
      // системного окна под поле вида `file` (ответ на вызов wizFile): выбран и грузится,
      // залит (ответ ручки в `body`) или не доехал. Заливает файл НАТИВ (ТЗ 20.09 F4).
      // Состояния 'picked' (файл выбран, лежит на телефоне — решение за вебом) и
      // 'compressing' (жмём ролик на устройстве) — аддитивные (ТЗ 20.09 V.4).
      | 'wizFile'
      // wizAskPick {key} — ответ на короткий вопрос листом (wizAsk); пустой ключ — «Отмена».
      | 'wizAskPick'
      | 'wizPreviewTap'
      | 'wizCancel'
      // wizItemBack — со страницы элемента ушли назад («Готово», свайп).
      //
      // МЁРТВЫЕ ИМЕНА (MESSAGE-COMPOSER К18.1 п.9–10): `wizListOpen` и `wizListRemove` —
      // события старой ветки списка (ТЗ В1 03.09). Веб их больше НЕ слушает: шаг-список
      // остался один, `itemList`. Имена стоят здесь до сноса ветки `list` в Swift
      // (отдельный приказ) — контракт моста заморожен, имена не переиспользуются.
      | 'wizListOpen'
      | 'wizListRemove'
      | 'wizItemBack'
      // К20 (владелец 07.09.2026): у wizItemBack и wizItemRowsBack появился аддитивный
      // ключ `pop: true` — со страницы ушли СТРЕЛКОЙ НАЗАД внутри шторки потока
      // добавления (шторка на экране, под нами предыдущая страница), а не закрыли её.
      // Без ключа смысл событий прежний — уход/закрытие.
      //
      // Шаг-список ОБЪЕКТОВ (ТЗ 03.09.2026 В2, письма цепочки): wizItemListTap
      // {name, key} — тап по строке письма (веб отвечает wizItemRowsOpen);
      // wizItemListAdd {name} — пунктирная «Добавить письмо»; wizItemListRemove
      // {name, key} — смахивание «Удалить»; wizItemListMove {name, key, dir} —
      // перестановка долгим нажатием (`dir` — на сколько мест уехало письмо, со знаком);
      // wizItemRowTap {key} — тап по строке на странице письма; wizItemRowsBack — со
      // страницы письма ушли назад («Готово», свайп).
      | 'wizItemListTap'
      | 'wizItemListAdd'
      | 'wizItemListRemove'
      | 'wizItemListMove'
      | 'wizItemRowTap'
      | 'wizItemRowsBack'
      // «Готово» страницы сообщения при `askDone` (П132 п.2): натив ждёт решения веба.
      | 'wizItemRowsDone'
      // ПОДКЛЮЧЕНИЕ АККАУНТА ИЗ МАСТЕРА (владелец 11.09.2026): wizAddAccount {field} —
      // тап по строке «Добавить аккаунт» под списком аккаунтов (натив шлёт его, когда
      // лист выбора уже уехал совсем). Дальше стопку подключения ведут события
      // wizConnTap {key, page}, wizConnSet {key, value}, wizConnBack {key} — смысл РОВНО
      // тот же, что у accountTap/accountSet/accountBack: второй семантики не заводим.
      | 'wizAddAccount'
      | 'wizConnTap'
      | 'wizConnSet'
      | 'wizConnBack'
      // Родной лист шаблонов: tplPick {id} — тап по ряду (лист уже уехал), tplCancel —
      // лист утянули пальцем или закрыли крестиком.
      | 'tplPick'
      | 'tplCancel'
      // Родной раздел «Аккаунт»: accountTap {key, page} — тап по строке или герою экрана
      // ('profile' — герой: веб открывает редактор профиля, там же живёт «Выйти»;
      // остальные ключи — строки свода, веб открывает свои шторки поверх). `page` —
      // ключ родной страницы, которую строка УЖЕ открыла (волна 3): веб по нему
      // запоминает стопку, а открывать ему нечего.
      | 'accountTap'
      // Подстраница раздела: accountSet {key, value} — тумблер или выбор из листа
      // (веб пишет настройку своей же ручкой и присылает страницу заново), accountBack
      // {key} — человек ушёл со страницы назад (кнопкой или свайпом от края): веб чинит
      // адрес, сам ничего не закрывает — страница у натива уже уехала.
      | 'accountSet'
      | 'accountBack'
      // Список родной страницы подошёл к концу: accountMore {page} — веб досылает
      // следующую страницу людей (SCALE-PLAN Е2, 16.09.2026; как msgsMore у «Чатов»).
      // Старые сборки события не шлют — веб тогда дотягивает порцию фоном, как раньше.
      | 'accountMore'
      // Снимок из РОДНОГО выбора фото на странице «Профиль»: accountPhoto { data } —
      // data:image/jpeg;base64. Заливку ведёт веб (второго пути заливки нет).
      // cropped: true — сборка УЖЕ обрезала снимок родным кроппером (квадрат 512, те же
      // числа, что у веб-кроппера): веб тогда льёт готовое, не показывая свой кроп.
      // Без поля (старая сборка) всё как было — кроп веб-шторкой.
      | 'accountPhoto'
      // Родная вкладка «Чаты» (список переписок): msgsTap {id} — тап по ряду (веб
      // открывает ту же переписку, что и с веб-списка, — существующим chatOpen);
      // msgsSearchOpen — тап по плашке поиска (веб открывает РОДНУЮ строку поиска
      // сверху, ровно как на «Ботах»; набранное придёт событием searchQuery, и веб
      // отберёт САМ своей единственной рукой поиска). Прежний msgsSearch {value} снят
      // 02.09.2026 вместе со своим полем ввода на экране (Н5 фикслиста).
      | 'msgsTap'
      // msgsRowDown {id} — палец ЛЁГ на ряд (тап-вниз, до открытия переписки): веб греет
      // её ленту, чтобы экран выезжал уже с сообщениями, а не пустым (П39 обхода
      // владельца 12.09.2026). Событие НОВОЕ и необязательное: старая сборка его не шлёт,
      // и всё работает как работало — открытие ведёт msgsTap (контракт моста заморожен,
      // CLAUDE.md запрет №8; прогрев — только прогрев, открывать по нему нельзя).
      | 'msgsRowDown'
      | 'msgsSearchOpen'
      // msgsMore — родной список подошёл к концу и просит следующую страницу (правило 2
      // владельца 09.09.2026: «список чатов — весь»). СОБЫТИЕ натива, а не метод моста:
      // старая сборка его не шлёт, и веб просто его не дождётся (запрет №8).
      | 'msgsMore'
      // ПАПКИ «ЧАТОВ» (ТЗ M.4, владелец 09.09.2026): msgsFolder {id} — тап по капсуле
      // папки в полосе над списком; msgsFoldersOpen — тап по круглой кнопке настроек
      // (веб поднимает полушторку «Папки»). Прежнее msgsFilter снято вместе с воронкой:
      // единственный отбор переписок — папка. Старая сборка шлёт msgsFilter, и веб его
      // просто не слушает — воронку она рисовать перестанет, как только пропадёт поле
      // `filters` в данных.
      | 'msgsFolder'
      | 'msgsFoldersOpen'
      // РЕЖИМ ВЫБОРА ПЕРЕПИСОК (второй обход 10.09.2026, п.17в): msgsRead {ids, all} —
      // «Прочитать (N)» для отмеченных или «Прочитать все» (all: true, пустой ids).
      // Событие натива, а не метод моста: старая сборка его не шлёт — а она и слов
      // режима не получает, значит кнопки «Изменить» у неё нет вовсе (запрет №8).
      | 'msgsRead'
      // Настройки папок — родные страницы на машинерии «Аккаунта»: msgsFoldersTap
      // {key, page}, msgsFoldersSet {key, value}, msgsFoldersBack {key}. Смысл РОВНО тот
      // же, что у accountTap/accountSet/accountBack — второй семантики не заводим.
      | 'msgsFoldersTap'
      | 'msgsFoldersSet'
      | 'msgsFoldersBack'
      // СПРАВКА РОДНЫМИ ЭКРАНАМИ (владелец 10.09.2026): helpTap {key, page} — тап по
      // строке раздела/статьи и по ССЫЛКЕ внутри статьи (ключ ссылки веб уже разрешил в
      // ключ страницы статьи, поэтому натив открывает её тем же движением, что строку);
      // helpSet {key, value} — набранное в поисковой капсуле шапки (отбирает ВЕБ);
      // helpBack {key} — ушли со слоя назад, а с ключом корня — справку закрыли целиком
      // (крестик листа или утянули пальцем). Смысл РОВНО тот же, что у
      // accountTap/accountSet/accountBack — второй семантики не заводим.
      | 'helpTap'
      | 'helpSet'
      | 'helpBack'
      // Родная вкладка «Боты»: botsTap {key} — тап на экране списка. 'search' — родное
      // поле поиска (веб открывает нативный поиск), 'card:<id>' — карточка (веб ведёт
      // свой переход в бота), 'menu:<id>' — «⋯» карточки (веб показывает прежнее меню у
      // точки; рама кнопки приходит в x/y/w/h).
      | 'botsTap'
      // Родная КАРТОЧКА СОБЕСЕДНИКА (волна 2, Опус-В2Г): chatInfoTap {key} — тап по
      // строке-действию карточки (замок диалога: веб спрашивает своё подтверждение и
      // сам переключает), chatInfoGone — лист ушёл (веб возвращает родное поле ответа).
      | 'chatInfoTap'
      | 'chatInfoGone'
      // chatInfoSet {key, value} — правка строки карточки родным полем (имя, телефон,
      // почта, метки: карточка человека и карточка собеседника — одна). Событие НОВОЕ:
      // старая сборка его не шлёт, и веб просто его не дождётся (запрет №8).
      | 'chatInfoSet'
      // Родной ВЫБОР ФАЙЛА для переписки: chatFile {name, mime, bytes, data, text} —
      // человек подтвердил файл на превью (data:<mime>;base64, text — подпись из поля
      // превью); заливку и отправку ведёт веб прежним путём. chatFileHeavy {bytes} —
      // файл не проходит МОСТОМ (строка base64 — это память и веба, и оболочки): веб
      // открывает свой прежний выбор файла, где байты никуда не переезжают.
      | 'chatFile'
      | 'chatFileHeavy'
      // Родная «Аналитика»: anaTap {key} — тап на экране. 'acc:<вид>:<id>' — кружок
      // аккаунта, 'card:<ключ>' — карточка бота (обе страницы натив открывает САМ, из
      // каталога pages, а веб на событие лишь досылает свежие данные), 'hide:<ключ>' —
      // кнопка «Скрыть/Вернуть» страницы бота, 'post:<id>' — плитка публикации (веб
      // открывает её в Instagram тем же window.open, что и веб-страница).
      | 'anaTap'
      // Отбор родной «Аналитики»: anaSet {key, value} — выбор из родного листа.
      // 'cat' — группа скриптов (пусто — «Все»), 'period' — период страницы аккаунта.
      | 'anaSet'
      // Ушли назад: anaBack {key} — ключ уехавшей страницы, пустой ключ — закрыли саму
      // «Аналитику» (веб убирает свой мозг и чинит адрес; закрывать нечего — у натива
      // экран уже уехал).
      | 'anaBack'
      // Родная форма разовой рассылки: bcastSet {key, value} — правка строки (выбор из
      // листа, готовое слово родного поля, тумблер); bcastTap {key} — нажата строка,
      // содержимым которой владеет ВЕБ (день и час, промежуток дней — их он спрашивает
      // родными окнами pickDate/pickTime/pickDateRange и присылает патчем);
      // bcastPhoto {key, data} — снимок из родного выбора фото (заливает его веб);
      // bcastSubmit — нижняя кнопка («Создать рассылку»): проверки и создание ведёт веб;
      // bcastCancel — форму закрыли крестиком или свайпом от края.
      | 'bcastSet'
      | 'bcastTap'
      | 'bcastPhoto'
      | 'bcastSubmit'
      | 'bcastCancel'
      // Родные «Полученные»: inboxTap {id} — тап по записи (веб отмечает её прочитанной
      // своей же рукой); inboxReadAll — «Прочитать всё» (веб спрашивает подтверждение и
      // читает ленту); inboxMore — лента долистана до конца (веб догружает страницу);
      // inboxBack {id} — ушли назад с записи; inboxGone — ленту закрыли (крестик, свайп).
      | 'inboxTap'
      | 'inboxReadAll'
      | 'inboxMore'
      | 'inboxBack'
      | 'inboxGone'
      // Родные ОКНА КАРТОЧКИ БОТА (волна 3): statsTap {key} — тап по кнопке или строке
      // окна ('edit', 'more', 'run:<id>', 'save', 'create', 'hero', 'tint', 'retry',
      // 'bc:send' …). НОВОЕ (П42, 12.09.2026, необязательное): 'menu' — кружок «⋯»
      // шапки, и с ним приходит РАМА кнопки в x/y/w/h (как у 'menu:<id>' на «Ботах») —
      // веб ставит по ней родное меню карточки; 'period:<7|30|90>' — пилюля капсулы
      // периодов; 'events:more' — «Показать ещё» в журнале людей; 'dialog:<id>' — строка
      // человека (веб открывает переписку тем же путём, что «Контакты»); statsSet {key, value} — правка строки родным полем или выбор из
      // родного листа (название, описание, категория, имя копии); statsBack {key} —
      // ушли назад со страницы стопки, пустой ключ — закрыли окно целиком.
      | 'statsTap'
      | 'statsSet'
      | 'statsBack'
      // Родной раздел «Рассылки»: bcastListTap {key} — тап на экране. 'plus'/'hero' —
      // завести рассылку, 'card:<id>' — карточка (веб открывает её страницу цифр),
      // 'menu:<id>' — «⋯» карточки (веб показывает прежнее меню у точки; рама кнопки
      // приходит в x/y/w/h), 'run:<id>' — строка журнала запусков, прочие ключи —
      // кнопки страниц. bcastListSet {key, value} — правка поля или выбор из списка на
      // странице. bcastListBack {key} — ушли со страницы назад (пустой ключ — закрыли
      // сам раздел). bcastListGone — раздел закрыли (крестик, свайп от края с корня).
      | 'bcastListTap'
      | 'bcastListSet'
      | 'bcastListBack'
      | 'bcastListGone'
      // Родное окно «Сообщение» — КАРТОЧКА-ПРОТОТИП (ТЗ И9.6, этапы Г14/Г15). msgSet
      // {key, value} — правка: 'text' с новым ПЛОСКИМ текстом (разметку по нему
      // пересчитывает веб) либо поле страницы кнопки ('btn.*'). msgTap {key} — нажали
      // кнопку под пузырём, «Добавить кнопку» или капсулу предела; ключ веб выдал сам
      // вместе со строками. msgFormat {kind, from, to, url} — кнопка полосы разметки на
      // выделении [from, to) (веб считает разметку и возвращает патчем; своей разметки
      // натив не знает). msgFile {kind, name, mime, bytes, data} — файл из родного выбора
      // (с ТЗ 20.09.2026 V.4 п.5 у сборки с методом `msgFileGo` вместо `data` едут
      // `status` — 'picked' | 'compressing' | 'uploading' | 'done' | 'error', метка файла
      // `token` и `body` — разобранный ответ нашей ручки; сам файл через мост не ездит)
      // (data:<mime>;base64): каким видом он уйдёт, решил человек в листе «Вложение», а
      // заливку и подготовку ведёт веб. msgSubmit — «Готово» в шапке; msgCancel — ушли
      // назад («Назад», свайп).
      //
      // НОВЫЕ события карточки (только новыми именами — запрет №8): msgSlotTap {slot} —
      // тап по слоту пузыря ('media' | 'text' | 'more'); msgTextOpen {value} — страница
      // «Текст» встала (true) или ушла (false); msgTextSet {value, from, to} — правка
      // поля правки текста; msgAttachPick {kind, replace?} — выбрали вид в листе
      // «Вложение» (дальше идёт msgFile с тем же видом); `replace: true` — нажали строку
      // «Сейчас в сообщении» («Изменить»): файл заменит стоящее, а не доложится к нему
      // (с П132 п.22 новая сборка так больше не шлёт — тап по строке там просмотр; старые
      // шлют, веб их понимает). У msgFile аддитивный `replaceKey` — переэкспорт кружка/гифки
      // из «Редактировать» встаёт на место вложения с этим ключом; msgAttachDrop {key} — «Убрать вложение» /
      // «Убрать всё» / «Убрать это фото» (ключ файла, пусто — всё); msgOptionSet
      // {key, value} — тумблер листа или страницы («spoiler», «captionAbove»,
      // «previewOff»).
      | 'msgSet'
      | 'msgTap'
      | 'msgFormat'
      | 'msgFile'
      | 'msgSubmit'
      | 'msgCancel'
      | 'msgSlotTap'
      | 'msgTextOpen'
      | 'msgTextSet'
      | 'msgAttachPick'
      | 'msgAttachDrop'
      | 'msgAttachOrder'
      | 'msgOptionSet'
      // Родной экран «Кто получит» (ТЗ 04.09.2026, И4). audSearch {q} — набрали букву в
      // поле поиска (отбирает ВЕБ, своей логики у экрана нет); audMore {after} — список
      // долистан до конца, веб догружает порцию с этого курсора; audFilterOpen {value} —
      // лист условий встал (true) или ушёл (false); audFilterTap {key} — тап по строке
      // листа, за которой идёт веб («passed» — страница «Прошёл бота», «when» — родной
      // календарь), ПУСТОЙ ключ — страница условия ушла назад; audFilterSet {key, value} —
      // выбрали значение условия (список через запятую либо положение галочки);
      // audFilterReset — «Сбросить»; audSelectMode {on} — включили/выключили галочки;
      // audSelectAll {on} — «Выбрать всех» / «Снять всех»; audCheck {id, on} — галочка у
      // человека; audDone — «Готово» в шапке; audBack — ушли назад («Назад», свайп);
      // audSearchOpen {on} — ТАП ПО КАПСУЛЕ ПОИСКА (true) и её крестик (false): режим
      // поиска держит ВЕБ и возвращает его же полем `bar.active` (владелец 04.09.2026,
      // И11.5, образец «Почта»). Пустой запрос режимом не считается: поиск открыт, пока
      // его не закрыли крестиком.
      | 'audSearch'
      | 'audSearchOpen'
      | 'audMore'
      | 'audFilterOpen'
      | 'audFilterTap'
      | 'audFilterSet'
      | 'audFilterReset'
      | 'audSelectMode'
      | 'audSelectAll'
      | 'audCheck'
      | 'audDone'
      | 'audBack',
    cb: (data: {
      key?: string;
      q?: string;
      text?: string;
      /** Снимок из родного выбора фото: data:image/jpeg;base64. */
      data?: string;
      /** Снимок уже обрезан родным кроппером (accountPhoto) — веб льёт его как есть. */
      cropped?: boolean;
      /** Выбранный файл (chatFile): тип и вес — их знает только тот, кто его открыл. */
      mime?: string;
      bytes?: number;
      /** ФАЙЛ, КОТОРЫЙ ГРУЗИТ САМА ОБОЛОЧКА (ТЗ 20.09.2026 F4/V.4): ход дела
       *  ('picked' | 'compressing' | 'uploading' | 'done' | 'error'), метка файла в
       *  очереди окна письма (`token`), длительность ролика (`seconds`), разобранный
       *  ответ нашей ручки (`body`) и код отказа (`code`). */
      status?: string;
      token?: string;
      seconds?: number;
      /** msgFile 'picked' (П136 п.4б, доктрина «максимум на телефоне» — HOW-IT-WORKS §11.4):
       *  ОПИСАНИЕ ФАЙЛА ОТ ТЕЛЕФОНА. `hasAudio` — есть ли у ролика звуковая дорожка (у
       *  гифки и кружка, собранных телефоном, — false); `title`/`performer` — теги трека
       *  («Аудио»), ключ шлётся и ПУСТЫМ, если телефон теги читал, а их нет. Веб кладёт их
       *  в адрес заливки (`made=phone&seconds&width&height&hasAudio&title&performer`), и
       *  ручка `/api/cabinet/media` при полном описании файл на раннер не везёт (пробы нет).
       *  Ключи АДДИТИВНЫЕ: сборка без них работает как раньше — ручка узнаёт недостающее
       *  пробой раннера. */
      hasAudio?: boolean;
      title?: string;
      performer?: string;
      body?: Record<string, unknown>;
      code?: string;
      pad?: number;
      height?: number;
      url?: string;
      id?: string;
      dialogId?: string;
      /** Ответы шага родного мастера (wizNext) и правка одного поля (wizChange). */
      values?: Record<string, string | boolean | string[]>;
      name?: string;
      value?: string | boolean | string[];
      /** wizAddAccount: ИМЯ ПОЛЯ, у которого нажали строку-действие под вариантами
       *  («Аккаунт» → стопка подключения, «Канал» → страница «Добавить канал»). Смысл
       *  события прежний — раздаёт по имени поля ВЕБ (CLAUDE.md запрет №8). */
      field?: string;
      /** accountTap: КАКУЮ РОДНУЮ СТРАНИЦУ строка уже открыла (её `page`). Пусто —
       *  страницы у строки нет. Веб по ней запоминает стопку: закрывать и открывать
       *  ему нечего, натив это уже сделал. */
      page?: string;
      /** Окно «Сообщение»: вид разметки (msgFormat) или вид медиа (msgFile) — ключ из
       *  того же списка, который веб сам и прислал. */
      kind?: string;
      /** Окно «Сообщение», msgFormat и msgTextSet: границы выделения в ЗНАКАХ плоского
       *  текста. */
      from?: number;
      to?: number;
      /** Окно «Сообщение», msgSlotTap: какой слот пузыря нажали ('media'|'text'|'more'). */
      slot?: string;
      /** Окно «Сообщение», msgAttachPick: нажали строку «Сейчас в сообщении»
       *  («Изменить») — файл ЗАМЕНИТ стоящее, а не доложится к нему (владелец 21.09.2026). */
      replace?: boolean;
      /** Окно «Сообщение», msgFile (П132 п.22): переэкспорт кружка/гифки после
       *  «Редактировать» — файл встаёт НА МЕСТО вложения с этим ключом (тот же порядок), а
       *  не докладывается. Ключ АДДИТИВНЫЙ: без него дорога прежняя. */
      replaceKey?: string;
      /** Окно «Сообщение», msgFile (П171): КАДР, выбранный в редакторе кружка/гифки, —
       *  доли исходника 0…1; отрезок едет теми же `from`/`to` (у msgFile это СЕКУНДЫ
       *  исходника, не знаки). Веб хранит их в черновике и отдаёт редактору при
       *  «Редактировать». Ключ АДДИТИВНЫЙ: старая сборка не шлёт — редактор открывается
       *  целым кадром, как раньше. */
      crop?: { x: number; y: number; w: number; h: number };
      /** Окно «Сообщение», msgFile (П173): личность снимка в галерее телефона
       *  (`PHAsset.localIdentifier`) — веб возвращает её строкам «Сейчас в сообщении»
       *  (`NativeMsgAttachRow.assetId`), и шторка скрепки отмечает стоящие галочками.
       *  Ключ АДДИТИВНЫЙ. */
      assetId?: string;
      /** Экран «Кто получит», audMore: курсор последней приехавшей порции людей. */
      after?: string;
      /** Экран «Кто получит»: НОВОЕ положение галочки или режима (audCheck, audSelectMode,
       *  audSelectAll, audFilterOpen) — своей памяти «включено ли» у натива нет. */
      on?: boolean;
      /** Рама нажатой кнопки родного экрана в точках (botsTap 'menu:<id>') — веб отдаёт
       *  её обратно в pointMenu как якорь меню. */
      x?: number;
      y?: number;
      w?: number;
      h?: number;
    }) => void
  ): Promise<{ remove: () => void | Promise<void> }>;
}

/** Строка родного «Аккаунта»: готовые строки и роль плитки, никакой логики.
 *  Ключ возвращается событием accountTap как есть. */
export interface NativeAccountRow {
  key: string;
  title: string;
  /** Номер пузыря-группы: строки одного номера стоят в одной стеклянной карточке. */
  group: number;
  /** Цвет плитки значка — '#rrggbb' (роль палитры, разрешает веб через readRoleHex). */
  color: string;
  /** SF-символ значка строки (глиф белый, плитка цветная). */
  icon: string;
}

/** ── ПРОТОКОЛ ПОДСТРАНИЦ «Аккаунта» (docs/NATIVE-ACCOUNT-PLAN.md, этап Б) ──────────
 *  Страница свода — это ТИТУЛ и пузыри готовых СТРОК. Форма строки одна на все будущие
 *  страницы раздела (тариф, язык, безопасность, уведомления, профиль, настройки): натив
 *  умеет ровно четыре вида и ничего не додумывает.
 *   • value  — факт: имя слева, готовое значение справа (менять нечего);
 *   • switch — тумблер: положение `on`, тап шлёт accountSet { key, value: boolean };
 *   • choice — выбор из списка: значение справа и шеврон, тап открывает РОДНОЙ нижний
 *              лист с `options`, выбор шлёт accountSet { key, value: string };
 *   • button — нажимаемая строка без значения, тап шлёт accountTap { key };
 *   • nav    — переход: имя слева, готовая сводка справа и шеврон, тап шлёт
 *              accountTap { key } (веб открывает свою шторку — лента «Полученных»,
 *              список аккаунтов «Сообщений», «Тихие часы»; со свода «Настроек» — свою
 *              родную страницу, если она есть в каталоге `pages`);
 *   • text   — правка словом: значение справа и шеврон, тап открывает РОДНОЕ поле
 *              (лист с полем и клавиатурой), готовый текст шлёт accountSet
 *              { key, value: string }.
 *  Ключ строки уникален в пределах раздела — по нему веб узнаёт, что менять. */
export type NativeAccountFieldKind =
  | 'value'
  | 'switch'
  | 'choice'
  | 'button'
  | 'nav'
  | 'text'
  /** ГАЛОЧКА (волна 3): ряд выбора из веб-пузыря (KChoice/KChoiceMany — «Куда присылать»,
   *  «Язык»): имя, пояснение и галочка справа у отмеченного. Тап шлёт accountSet
   *  { key, value: boolean } — новое положение галочки. Тумблером это не заменяется:
   *  в вебе там именно галочки (канон «Элементы формы»). */
  | 'check'
  /** ПОЛЕ ПРЯМО В СТРОКЕ (ТЗ И11.8, страница кнопки письма): подпись слева, ввод справа
   *  (как «Описание» у VPN в «Настройках»). Правится НА МЕСТЕ — шторки с «Готово» под
   *  одно поле у таких строк не бывает; каждая правка шлёт свой accountSet/msgSet
   *  { key, value: string } (натив придерживает ввод ~300 мс, чтобы не слать по букве).
   *  Отличается от 'text' именно этим: 'text' открывает лист с полем, 'field' — не
   *  открывает ничего. */
  | 'field'
  /** Картинка: значение — ГОТОВОЕ слово («Картинка добавлена»), тап открывает РОДНОЙ
   *  выбор снимка, а заливку ведёт веб (второго пути заливки у кабинета нет). Вид
   *  заведён формой рассылки; страницы «Аккаунта» его не шлют. */
  | 'photo'
  /** ЧЕЛОВЕК СТРОКОЙ ТАБЛИЦЫ (страница «Контакты», волна 3): лицо (фото или плитка
   *  канала фирменного цвета), имя и @логин, связь (телефон или почта), метки чипами,
   *  дата последнего визита справа — те же пять колонок, что у веб-таблицы ct-table. */
  | 'person'
  /** ЗНАЧЕНИЕ, КОТОРОЕ КОПИРУЮТ (адрес и ключ MCP): значение справа и знак копии;
   *  тап кладёт `raw` (или `value`) в буфер обмена. Своей записи натив не делает. */
  | 'copy'
  /** ПИСЬМО КАК ОНО ВЫГЛЯДИТ (Scriptera A a110+, шторка события `adm-event`, 14.10.2026): `html` —
   *  готовая страница письма (санитизирована сервером), натив показывает её встроенным WKWebView во всю
   *  ширину группы без скриптов и переходов; `height` — необязательная высота (pt), иначе по содержимому.
   *  Вид ДОБАВОЧНЫЙ (запрет №8): старые сборки его не знают. */
  | 'html';

/** Ряд родного листа выбора: значение, готовая подпись и вторая строка (смещение пояса). */
export interface NativeAccountOption {
  value: string;
  label: string;
  hint?: string;
}

export interface NativeAccountField {
  key: string;
  kind: NativeAccountFieldKind;
  title: string;
  /** Пояснение под строкой. Видно ВСЕГДА (правило кабинета: пояснения не прячем). */
  hint?: string;
  /** СТРОКА-РАЗДЕЛ (свод «Настроек», этап Д): SF-символ пастельной плитки слева — той
   *  же, что у строк корневого свода. Пусто — плитки нет (обычная строка настройки). */
  icon?: string;
  /** Цвет плитки '#rrggbb' (роль палитры, разрешает веб через accountRoleHex). */
  color?: string;
  /** ЛОГОТИП СЕТИ ИЛИ СЕРВИСА вместо плитки значка (страница «Подключения»): имя
   *  картинки каталога приложения (ChanTG · ChanIG · SvcAmo · SvcB24 · SvcGC ·
   *  SvcYookassa — тот же приём, что у рядов «Чатов»). Чужой фирменный знак мы не
   *  перекрашиваем: подложка нейтральная, цвет знака — его собственный. Картинки у
   *  сборки нет (незнакомый сервис) — рисуется обычная плитка `icon`/`color`. */
  image?: string;
  /** ЛИЦО АККАУНТА вместо плитки значка (волна 3, строки «Сообщений пользователей»):
   *  фотография инстаграм-аккаунта или бота, base64 (с префиксом data:image/… или без) —
   *  готовит её ВЕБ тем же конвейером, что аватары «Аналитики» и «Чатов» (imgSquarePng);
   *  клиент ничего не качает сам. Пусто — рисуется запаска: логотип сети `image`, а нет
   *  и его — обычная плитка `icon`/`color`. */
  photo?: string;
  /** ЗНАЧОК ВОЗЛЕ ЗНАЧЕНИЯ справа (не плитка слева): маленький логотип каталога сборки
   *  перед готовой строкой — так веб рисует канал в карточке собеседника
   *  (`<ChannelIcon size={14}/>` внутри .msg-info-val). Имя картинки — то же, что у
   *  `image` (ChanTG · ChanIG · …). Пусто — значка нет. */
  valueImage?: string;
  /** ЗНАК СЕТИ ПЕРЕД ИМЕНЕМ СТРОКИ (владелец 10.09.2026: «значки социальных сетей надо
   *  поставить, перед логином» — строки аккаунтов на странице «Сообщения» уведомлений):
   *  имя той же картинки каталога (ChanTG · ChanIG · …), натив рисует её 14 с зазором 7
   *  слева от заголовка. Плитка слева при этом остаётся ФОТО аккаунта (`photo`): знак
   *  сети стоит РЯДОМ С ЛОГИНОМ, а не вместо лица. Пусто — знака нет.
   *
   *  Поле ДОБАВЛЕНО, а не подменяет прежнее: сборка без него рисует строку как всегда
   *  (CLAUDE.md запрет №8 — это данные страницы, контракт моста не меняется). */
  titleImage?: string;
  /** Цвет знака у заголовка (hex): картинка рисуется ШАБЛОНОМ этим цветом — тот же знак
   *  сети, что у названия на «Ботах» (`QuickTile-<сеть>` + цвет сети), и стоит ПОСЛЕ
   *  заголовка (владелец 19.09.2026: «иконки после ника, те же, что на странице боты»).
   *  Без цвета — прежний ход: картинка как есть, слева от заголовка (старые сборки). */
  titleImageColor?: string;
  /** Ряд ОТМЕЧЕН (текущий кабинет в списке кабинетов): вместо шеврона стоит галочка
   *  ролью --accent-stroke, и нажимать такой ряд нечего — он уже выбран. */
  check?: boolean;
  /** 'nav': ЧЕМ помечен правый край вместо шеврона — SF-символ действия строки (у ряда
   *  подключённого ИИ-приложения это «питание», как в вебе). Пусто — шеврон. */
  mark?: string;
  /** 'value' и 'choice': готовое значение справа (уже на языке кабинета). */
  value?: string;
  /** 'value': СТОЛБИКИ ПОД СТРОКОЙ (Scriptera A, профиль клиента «Запуски за 30 дней», ADM-APP-FIX-5 §2.3 п.5):
   *  числа от старого дня к сегодняшнему; сборка, которая ключ знает, рисует их тем же кодом столбиков, что
   *  график «Аналитики» (`NativeAnaBlock` kind `chart`). Ключ ДОБАВЛЕН (CLAUDE.md запрет №8): сборка без него
   *  рисует строку как раньше — заголовок и итог в `value` («Запуски за 30 дней — N»). */
  bars?: number[];
  /** 'choice': ЧТО именно выбрано — значение варианта, а не его подпись (по нему лист
   *  ставит галочку; подпись для этого не годится — она переводится и меняется). */
  picked?: string;
  /** 'switch': положение тумблера. */
  on?: boolean;
  /** Строка неактивна (например, телеграм не привязан) — тап и переключение молчат. */
  disabled?: boolean;
  /** 'switch' (добавлено, ТЗ HELP-WARM-FIX §1): положение ещё не подтверждено сервером (экраны статусов, SERVER_TRUTH
   *  плана прогрева) — веб держит место тумблера пустым; сборка без поля видит `disabled`. */
  wait?: boolean;
  /** 'choice': ряды листа. Тяжёлые списки (пояса) веб шлёт, только пока страница открыта. */
  options?: NativeAccountOption[];
  /** 'choice': выбор НЕСКОЛЬКИХ (боты в отборе рассылки) — лист копит галочки и
   *  возвращает список значений через запятую. Пусто/false — выбор один, как раньше. */
  multi?: boolean;
  /** ПОЛЕ ПРАВКИ — МНОГОСТРОЧНОЕ. У 'text' это лист правки (текст сообщения рассылки,
   *  кнопки по строке), у 'field' — поле ПРЯМО В СТРОКЕ, которое растёт по тексту:
   *  минимум три строки, своей прокрутки нет (владелец 13.09.2026, П80 — «Описание» бота
   *  «не помещается», описание обязано быть видно целиком и правиться на месте).
   *  Пусто/false — однострочное поле, как у имени профиля.
   *
   *  Ключ у 'field' — ДОБАВКА, а не смена смысла (CLAUDE.md запрет №8): сборка без него
   *  рисует обычное однострочное поле, и строка остаётся рабочей. */
  multiline?: boolean;
  /** ПРЕВЬЮ ПАПКИ-АККАУНТА (П91, 13.09.2026): вместо слова строки — значок сети, кружок
   *  фото (base64) и короткое имя. Ключ НОВЫЙ и необязательный: старая сборка рисует
   *  обычную строку с `value`. */
  preview?: { icon?: string; iconColor?: string; photo?: string; text: string };
  /** ПОРЯДОК И УДАЛЕНИЕ КАК В TELEGRAM (П85, владелец 13.09.2026): `dragId` — строка
   *  участвует в перетаскивании своей группы (`reorder`), это её слово в новом порядке;
   *  `swipeDelete` — ключ тапа, который натив шлёт после свайпа строки влево и тапа по
   *  красной плашке со словом `swipeLabel`. Ключи НОВЫЕ и необязательные: старая сборка
   *  их не знает и рисует обычные строки. */
  dragId?: string;
  swipeDelete?: string;
  swipeLabel?: string;
  /** 'choice': готовая подсказка строки поиска в листе; пусто — поиска у листа нет. */
  search?: string;
  /** 'button': роль цвета подписи — обычная, акцентная, опасная (роли палитры). */
  tone?: 'plain' | 'accent' | 'danger';
  /** 'text': ЧТО правится в родном поле — исходный текст со всеми переносами. Пусто —
   *  правится само `value` (у строки с одним словом они совпадают). Заведено формой
   *  рассылки: в строке значение показано ОДНОЙ строкой («первая · вторая»), а в поле
   *  человек обязан увидеть свой текст как он его писал. */
  raw?: string;
  /**
   * 'copy': АДРЕС ССЫЛКИ строки (ТЗ USERTEST-1014 п.13, строки бота и канала на странице «Добавить канал»): сборка,
   * которая ключ понимает, открывает адрес тапом по имени строки (Telegram), а знак копирования справа кладёт `raw`
   * в буфер. Ключ АДДИТИВНЫЙ (CLAUDE.md запрет №8): старая сборка его не читает — тап по строке копирует, как было.
   */
  link?: string;
  /** 'text' и 'field': готовая подсказка пустого поля. */
  placeholder?: string;
  /** 'field': сколько знаков принимает поле (предел площадки — считает его веб). */
  max?: number;
  /** 'text': готовая подпись кнопки листа правки («Сохранить»). */
  done?: string;
  /** 'check': ряд ОДНОГО ИЗ НЕСКОЛЬКИХ (язык кабинета — в вебе KChoice): снять выбор
   *  нельзя, тап по уже отмеченному ряду ничего не делает. Пусто — ряд отмечается и
   *  снимается сам по себе (галочки каналов, в вебе KChoiceMany). */
  radio?: boolean;
  /** ЗАКРЫТАЯ СТРОКА, КОТОРАЯ ОТВЕЧАЕТ НА ТАП (волна 3, страница «Профиль»): менять
   *  значение нельзя, но тап не проходит молча — строка коротко ТРЯСЁТСЯ и даёт вибро,
   *  ровно как в вебе (shakeRow в profile.tsx). Шеврона у неё нет и события вебу не
   *  уходит: это чистая отповедь, считать её нечего. */
  shake?: boolean;
  /** 'choice': КНОПКА ПОД СПИСКОМ родного листа выбора («Убрать из списка» у
   *  отключённого аккаунта — в вебе она стоит в той же низкой шторке режима). Нажали —
   *  лист уезжает и вебу уходит accountSet { key, value } с этим значением. */
  pickAction?: { label: string; value: string };
  /** 'choice': ИМЯ ПУЗЫРЯ ВЫБОРА в листе — в вебе это label FChoiceBlock над строками.
   *  Пусто — над списком ничего не стоит, и чаще всего именно так: имя строки уже стоит
   *  в шапке листа, а разговорных заголовков над пузырями не бывает (владелец
   *  10.09.2026 — «Какие переписки» снято). Слать только формальное слово. */
  pickLabel?: string;
  /** 'choice': ПОЯСНЕНИЕ ПОД СПИСКОМ листа (чем эта строка отличается от остальных —
   *  «так будет настроен аккаунт, который вы подключите позже»). В вебе это hint того
   *  же пузыря выбора. */
  pickHint?: string;
  /** ЛОГОТИП СЕРВИСА КАРТИНКОЙ ВЕБА (волна 3, «Подключения»): base64 PNG БЕЗ префикса
   *  data:. Логотипы каталога у сборки лежать не могут (их сотни и они меняются) —
   *  силуэт в фирменном цвете рисует веб тем же конвейером, что фото аккаунта, и
   *  кладёт на мягкую плитку 40 цвета сервиса (`logoColor`). Пусто — плитка по
   *  `image`/`icon`, как раньше. */
  logo?: string;
  /** Цвет плитки логотипа '#rrggbb' (цвет сервиса; подложка — им же вполсилы). */
  logoColor?: string;
  /** ТОЧКА СОСТОЯНИЯ рядом с названием (карточка доступа в вебе, .scn-on-dot): ГОТОВЫЙ
   *  цвет '#rrggbb' — веб разрешает роль (зелёная «в сценариях», серая «свободен»,
   *  сигнальная «сломан»), своих оттенков натив не заводит. Слово-подсказку веб кладёт
   *  в `hint`, ровно как в вебе. Пусто — точки нет. */
  dot?: string;
  /** Цвет ПОДПИСИ строки '#rrggbb' — тоже ролью веба (поломка подключения). Пусто —
   *  обычный серый. */
  hintColor?: string;
  /** Цвет ЗНАЧЕНИЯ справа '#rrggbb' — ролью веба: слово состояния подключения («Ошибка»
   *  сигнальным красным, «Работает» обычным серым). Поле ДОБАВЛЕНО, а не подменяет
   *  прежнее: сборка без него красит значение как всегда (CLAUDE.md запрет №8). */
  valueColor?: string;
  /** 'text' и 'field': ГОТОВЫЕ СЛОВА ОШИБКИ ПУСТОГО ОБЯЗАТЕЛЬНОГО ПОЛЯ (ТЗ П155 §3.3) —
   *  «Токен доступа не заполнен» / «Access token is empty». Есть — поле обязательное:
   *  лист правки держит «Сохранить» и ✓ погашенными, пока поле пусто, а по тапу даёт
   *  вибрацию ошибки, красную обводку поля и эти слова под ним. Пусто — поле
   *  необязательное, лист как был. Ключ аддитивный (CLAUDE.md запрет №8). */
  emptyError?: string;
  /** КРУЖОК С БУКВОЙ вместо плитки значка (аватар сотрудника, .av): фон — `color`
   *  (веб считает его по имейлу, как в вебе), буква белая. */
  initial?: string;
  /** КРУЖОК СО ЗНАЧКОМ вместо буквы (конверт у ожидающего приглашения, .av.st-pend):
   *  SF-символ; фон кружка — `color`, глиф — `initialInk`. */
  initialIcon?: string;
  /** Цвет буквы/значка кружка '#rrggbb'. Пусто — белый (обычный аватар сотрудника). */
  initialInk?: string;
  /** БУКВА В МЯГКОЙ ПЛИТКЕ цветом сервиса (.svc-circle без логотипа): подложка — `color`
   *  вполсилы, буква им же. Запаска ServiceLogo, когда логотипа у сервиса нет вовсе. */
  letter?: string;
  /** 'person': @логин серым рядом с именем. */
  login?: string;
  /** 'person': колонка «связь» — телефон или почта (в вебе ct-cell-reach). */
  reach?: string;
  /** Метки чипами: ряд человека и карточка (в вебе ct-tag / tpl-chip). */
  chips?: string[];
  /** 'person': колонка «когда» — дата последнего визита у правого края. */
  when?: string;
  /** 'text': поле — СЕКРЕТ (значение в строке точками, ввод скрытый). */
  secret?: boolean;
  /** КАКУЮ РОДНУЮ СТРАНИЦУ ОТКРЫВАЕТ строка (волна 3). Ключ строки остаётся своим
   *  (`ct:<id>` — по нему веб узнаёт, о ком речь), а страница у целого ВИДА одна:
   *  карточка человека, карточка доступа, форма подключения. Натив открывает её из
   *  каталога СРАЗУ (дёрганья запрещены) и тут же сообщает о тапе — веб наливает в неё
   *  содержимое патчем. Пусто — страницы у строки нет, тап уходит вебу как раньше. */
  page?: string;
  /** МЕНЮ ДОЛГОГО НАЖАТИЯ по строке (П190, страница «Метки»: «Переименовать» / «Удалить»,
   *  §11с `ScrPressMenu`). Выбор пункта уходит вебу обычным accountTap с ключом
   *  `<ключ строки>:<ключ пункта>`. Ключ АДДИТИВНЫЙ (запрет №8): старая сборка меню не
   *  рисует, строка работает тапом как раньше. */
  menu?: { key: string; label: string; icon?: string; tone?: 'danger' }[];
  /** 'html': страница письма целиком (санитизирована сервером). Ключ АДДИТИВНЫЙ. */
  html?: string;
  /** 'html': высота строки (pt); нет — натив меряет содержимое сам. Ключ АДДИТИВНЫЙ. */
  height?: number;
}

/** ГЕРОЙ СТРАНИЦЫ (владелец 01.09.2026, приёмка): у КАЖДОЙ родной страницы раздела
 *  сверху стоит тот же герой, что у веб-шторок (SettingsHero) — пастельная плитка со
 *  значком роли, название страницы и короткая подпись. У «Профиля» плитки нет: её место
 *  занимает КРУПНОЕ ФОТО (`photo`), и тап по нему открывает родной выбор снимка
 *  (событие accountPhoto), а заливку по-прежнему ведёт веб. */
export interface NativeAccountHero {
  /** SF-символ плитки (пусто у героя с фото). */
  icon?: string;
  /** Цвет роли '#rrggbb' — плитка вполсилы и глиф им же. */
  color: string;
  title: string;
  meta?: string;
  /** Фото героя (data:image/…;base64) — вместо плитки значка. */
  photo?: string;
  /** ЛОГОТИП СЕРВИСА вместо плитки значка (шапка карточки доступа — там в вебе стоит
   *  ServiceLogo): base64 PNG силуэта, который приготовил веб, и цвет плитки под ним. */
  logo?: string;
  logoColor?: string;
  /** Буква на месте фото, пока фото нет. */
  initial?: string;
  /** ЗНАЧОК СЕРВИСА МАЛЕНЬКОЙ ПЛАШКОЙ В УГЛУ ФОТО (карточка подключённого аккаунта,
   *  владелец 09.09.2026): base64 логотипа, который приготовил веб. Кружок тот же, что
   *  камера у «Профиля» — серый, с белой кромкой, знак внутри; чужой фирменный знак не
   *  перекрашиваем. Поле ДОБАВЛЕНО (запрет №8): сборка без него рисует прежнего героя —
   *  фото без уголка. Пусто — уголка нет. */
  photoBadge?: string;
  /** Тап по фото открывает РОДНОЙ выбор снимка (accountPhoto). Без него герой не нажимается. */
  pick?: boolean;
  /** ГОТОВЫЕ СЛОВА РОДНОГО КРОППЕРА (волна 2, Опус-В2Г): заголовок, пояснение и подписи
   *  кнопок экрана обрезки, который встаёт сразу после выбора снимка. Слова считает веб —
   *  в Swift строк не бывает. Пусто — сборка кроп не покажет и отдаст снимок как раньше. */
  pickTitle?: string;
  pickHint?: string;
  pickDone?: string;
  pickCancel?: string;
  /** Адрес фото в полный размер — тап по фото открывает родной просмотр (карточка
   *  собеседника, 09.09.2026). Добавочный ключ; пусто — фото не нажимается. */
  photoUrl?: string;
  /** РЯД ЗНАЧКОВ УСТРОЙСТВ ПОД `meta` (Scriptera A, профиль клиента, ТЗ ADM-APP-FIX-6 К6, владелец 15.10.2026):
   *  всегда четыре в этом порядке — iphone · android · pc · mac; `on` — клиент заходил с такого устройства хоть раз
   *  (значок чернилами, `label`), иначе бледный (`secondaryLabel`, прозрачность 0.3). Сборка рисует ряд 20 pt по
   *  центру под `meta`, промежуток 16; SF Symbols: iphone → `iphone`, android → `candybarphone`, pc → `pc`,
   *  mac → `laptopcomputer`. Ключ ДОБАВЛЕН (запрет №8): сборка без него рисует героя как раньше — только `meta`. */
  devices?: { kind: 'iphone' | 'android' | 'pc' | 'mac'; on: boolean }[];
  /** СЛОВО СОСТОЯНИЯ ЦВЕТОМ ПОД ГЕРОЕМ (Scriptera A, страница бота клиента, ТЗ ADM-APP-FIX-6 К10: «статус — словом
   *  цветом под героем»): `text` — готовое слово («включён»), `color` — роль палитры '#rrggbb'. Сборка рисует его
   *  строкой 15 по центру под `meta`. Ключ ДОБАВЛЕН (запрет №8): старая сборка героя рисует без него. */
  status?: { text: string; color: string };
}

/** Секция родного листа отбора (кнопка-воронка в шапке страницы): имя секции и её
 *  пункты. Выбор — ОДИН на секцию, ровно как у веб-листа FilterButton. */
export interface NativeAccountFilterItem {
  key: string;
  label: string;
  /** Вторая строка пункта серым (сеть аккаунта — «Telegram» / «Instagram»). */
  sub?: string;
  on?: boolean;
}

export interface NativeAccountFilterSection {
  title: string;
  items: NativeAccountFilterItem[];
}

/** КНОПКА ШАПКИ РОДНОЙ СТРАНИЦЫ (волна 3): круглая линза справа от титула — «+»,
 *  воронка отбора, журнал. Есть `sections` — тап открывает РОДНОЙ нижний лист отбора
 *  (тот же вид, что веб-лист FilterButton), выбранный пункт уходит вебу событием
 *  accountSet { key: <ключ кнопки>, value: <ключ пункта> }; нет — тап уходит обычным
 *  accountTap { key }. Отбирает ВЕБ: своей логики у натива нет. */
export interface NativeAccountAction {
  key: string;
  /** SF-символ кнопки. */
  icon: string;
  /** Подпись для VoiceOver (готовое слово кабинета). */
  label?: string;
  /** Отбор включён — кружок помечен акцентом (как активная воронка веба). */
  active?: boolean;
  /** Кнопка неактивна (доступов пока нельзя добавлять) — гаснет и не нажимается. */
  disabled?: boolean;
  /** Какую родную страницу открывает кнопка («Подключить», «Пригласить», журнал) —
   *  тот же приём, что у строки (см. NativeAccountField.page). */
  page?: string;
  sections?: NativeAccountFilterSection[];
}

/** Пузырь страницы: имя группы сверху, строки внутри, пояснение под пузырём. */
export interface NativeAccountGroup {
  key: string;
  label?: string;
  hint?: string;
  /** ОТВЕТ НА ДЕЙСТВИЕ под пузырём, ПОД пояснением (волна 3): «Отправлено, спасибо» /
   *  «Не удалось отправить» у «Диагностики», «Сохраняю…» у «Профиля». `bad` — красным
   *  (роль сигнальной ошибки), иначе серым, как обычное пояснение. */
  status?: { text: string; bad?: boolean };
  /** ЗАМЕТКА вместо серой подписи под пузырём: 'danger' — ошибка красным (DError),
   *  'warn' — янтарная плашка (DWarn), 'hint' — светлая подсказка (DHint). Пусто —
   *  прежняя серая подпись. */
  tone?: 'plain' | 'danger' | 'warn' | 'hint';
  /** Цвет тона заметки '#rrggbb' — роль веба (сигнальный красный, янтарный). Пусто —
   *  натив берёт свою запаску. */
  toneColor?: string;
  /** КАРТОЧКАМИ, А НЕ РЯДАМИ ОДНОГО ПУЗЫРЯ (сетка «Подключений» и каталог «Подключить»):
   *  каждая строка — свой пузырь с плиткой логотипа, названием и подписью, ровно как
   *  .scn-card веба (на телефоне сетка веба и так стоит одной колонкой). */
  cards?: boolean;
  /** СКОЛЬКО ПУСТЫХ КАРТОЧЕК ЖДЁТ СТРОКИ, пока данные едут (у веб-скелетона это число
   *  своё у каждого списка: три плитки у доступов, шесть у каталога). Пусто — одна. */
  wait?: number;
  /** СПИСОК «ГАЛОЧКА СЛЕВА, ИМЯ ВЕДЁТ НА СВОЮ СТРАНИЦУ» — в вебе это `FPickNavList`
   *  (страница «Прошёл бота»: галочка отмечает бота, имя открывает его настройки).
   *  Пусто — обычный многовыбор с галочкой СПРАВА (`KChoiceMany`). Вид говорит ГРУППА, а
   *  не строка: у неотмеченной строки перехода нет вовсе, и отличить её было бы нечем. */
  pickNav?: boolean;
  /** ПОРЯДОК СТРОК ВЕДЁТ ЧЕЛОВЕК — ПЕРЕТАСКИВАНИЕМ (владелец 13.09.2026, П85: «кнопки
   *  „Выше/Ниже“ убрать; нажимаешь „Изменить“, зажимаешь пальцем и перетягиваешь»).
   *  Значение — КЛЮЧ, которым натив вернёт новый порядок: отпустил строку — уходит
   *  accountSet(<этот ключ>, "id1,id2,…") с ключами строк группы по порядку, и записывает
   *  порядок ВЕБ. Ключи договорены заранее: `fld:order` (папки «Чатов»), `ctfd:order`
   *  («Контактов»), `btfd:order` (категории «Ботов»). Пусто — строки не перетаскиваются.
   *
   *  Ключ ДОБАВЛЕН, а не меняет смысл прежних (CLAUDE.md запрет №8): сборка из магазина
   *  его не знает и рисует обычный список — порядок у неё просто не меняется. */
  reorder?: string;
  fields: NativeAccountField[];
}

/** Подстраница раздела. `key` — он же кусок адреса /app/account/<key>. */
export interface NativeAccountPage {
  key: string;
  title: string;
  /** ОПИСАНИЕ СТРАНИЦЫ ОДНОЙ СТРОКОЙ ПОД ЗАГОЛОВКОМ, ПО ЦЕНТРУ (владелец 14.09.2026: «у
   *  папок можно сделать описание в одну строчку сверху, нативно, посерединке»). У шторок
   *  папок это «Папки ботов» / «Папки чатов» / «Папки контактов» — чтобы было видно, о
   *  каких папках речь. Ключ НОВЫЙ и необязательный (запрет №8): без него страница
   *  выглядит как раньше. */
  subtitle?: string;
  /** Герой страницы (титул живёт в нём, родная шапка остаётся с одной кнопкой «назад»).
   *  Страница может прислать своего (фото у «Профиля»), а нет — его достроит ВОДИТЕЛЬ
   *  (account-native.ts) по строке свода с тем же ключом: новые страницы получают
   *  шапку сами, без своего кода. */
  hero?: NativeAccountHero;
  /** Данные ещё едут — натив рисует пустой пузырь вместо строк (без скачков контента). */
  loading?: boolean;
  /** ПОИСК СПИСКА СТРАНИЦЫ («Контакты»): ПЛАШКА-ВХОД под шапкой — ровно та же, что у
   *  родных списков «Ботов» и «Чатов» (`ScrSearchPlate`, инвариант «одна на все родные
   *  списки»). Тап уходит вебу событием accountTap с этим ключом, и веб открывает ТОТ ЖЕ
   *  родной поиск сверху (`openSearch`); набранное возвращается общим событием
   *  searchQuery, отбирает ВЕБ и присылает отобранные строки патчем.
   *
   *  `query` — набранное слово: плашка показывает его вместо подсказки (как у «Ботов»).
   *  Поле ДОБАВЛЕНО (запрет №8): старая сборка его не читает и рисует своё поле ввода,
   *  а веб продолжает принимать от неё accountSet { key, value } — оба пути живы. */
  search?: { key: string; placeholder: string; query?: string };
  /** КРУГЛАЯ КНОПКА В УГЛУ ШАПКИ страницы (волна 3): у «Уведомлений» это колокольчик
   *  «Полученные» — ровно там же, где он стоит в вебе (InboxTopButton в шапке шторки).
   *  Тап уходит вебу событием accountTap с этим ключом; `badge` — готовая цифра
   *  непрочитанного («12», «99+»), пусто — наклейки нет. */
  action?: { key: string; icon: string; badge?: string };
  /** КРАСНАЯ СТРОКА ПОД ШАПКОЙ (волна 3): ошибка сохранения словами — как в вебе
   *  (DError над телом «Профиля»). Пусто — строки нет. */
  error?: string;
  /** СВЕТЛАЯ МЕЛКАЯ ССЫЛКА ПОД СПИСКОМ (волна 3, свод «Настроек»): «Отправить журнал
   *  диагностики» — в вебе это не строка свода, а мелкий текст-ссылка под пузырями
   *  (владелец 26.08.2026). Тап уходит вебу событием accountTap с этим ключом. */
  link?: { key: string; title: string };
  /** СТРАНИЦА-ПРАВКА ОДНОГО СЛОВА (волна 3, «Имя» профиля): поле в пузыре, автофокус и
   *  поднятая клавиатура (владелец 2026-07-25). Вид окна — по словарю (`present`); у
   *  правки имени это ГОРИЗОНТАЛЬНАЯ страница, как у Apple в «Настройках» (владелец
   *  09.09.2026): слева «назад», справа ✓. Показанная листом ('sheet') правка держит
   *  прежний × слева — шага назад у листа нет. Готовое слово уходит вебу событием
   *  accountSet { key, value } и страница закрывается. */
  /*  П190 («Метки»), ключи АДДИТИВНЫЕ (запрет №8 — старая сборка их не читает, лист как
   *  был): `strict` — ✓ погашена, пока поле пусто или равно исходному `value`;
   *  `taken` — имена, которые уже заняты: набрали такое — ✓ погашена и под полем красная
   *  строка `takenError`. Пока лист открыт, веб `value` и `taken` не меняет. */
  edit?: {
    key: string;
    value: string;
    placeholder?: string;
    done?: string;
    strict?: boolean;
    taken?: string[];
    takenError?: string;
  };
  /** ПОЛОСА ПАПОК НАД СПИСКОМ (владелец 10.09.2026, «Контакты» — как «Чаты»): капсулы с
   *  именем и числом людей, шестерёнка первой капсулой ленты. Тап по капсуле — accountTap
   *  `ctf:<id>`, по шестерёнке — `ctf:settings`. Старые сборки поле не знают и не рисуют.
   *
   *  `pages` — ПЕРВЫЕ СТРАНИЦЫ СОСЕДНИХ ПАПОК (владелец 11.09.2026, П10б: папки листаются
   *  свайпом, и соседняя страница обязана быть не пустой ДО того, как палец её дотянет):
   *  по записи на каждую папку полосы, КРОМЕ текущей, в порядке полосы. Ряды берутся из
   *  КЭША кабинета (опрос ведёт только текущая папка), поэтому кэш ещё не доехал — запись
   *  с пустыми рядами и без слов. Поля нет — свайпа нет, полоса и тап работают как раньше
   *  (то же поле и та же мера, что у «Чатов»: `pages` в msgs-native.ts). */
  folders?: {
    current: string;
  /**
   * ПАПКА-АККАУНТ — КРУЖКАМИ ВМЕСТО СЛОВА (владелец 12.09.2026, П65: «в капсуле два
   * кружочка: значок соцсети и фотография… короткие названия»). Поля НЕОБЯЗАТЕЛЬНЫЕ и
   * едут только у папки, помеченной аккаунтом:
   *   `photo`     — фотография аккаунта кружком, base64 (тот же конвейер imgSquarePng,
   *                 размер 40); пусто или поля нет — рисуется один значок сети;
   *   `icon`      — имя значка каталога приложения: 'QuickTile-telegram' | 'QuickTile-instagram';
   *   `iconColor` — фирменный цвет сети строкой '#rrggbb'.
   * Имя папки остаётся в `name` (настройки, подсказка доступности).
   *
   * INVARIANT(scriptera): поля — ТОЛЬКО необязательные добавки, метода моста под них не
   * заводится; старая сборка их не читает и рисует папку словом, как раньше. Причина:
   * контракт моста заморожен (запрет №8 в CLAUDE.md).
   */
    items: { id: string; name: string; count: number; photo?: string; icon?: string; iconColor?: string }[];
    settingsLabel: string;
    pages?: { id: string; fields: NativeAccountField[]; emptyText: string }[];
  };
  /** КНОПКИ ШАПКИ страницы (волна 3): круглые линзы справа от титула — «+», воронка
   *  отбора, журнал. Порядок тот же, что в вебе (воронка перед «+»). */
  actions?: NativeAccountAction[];
  /** ТЕКСТОВАЯ КНОПКА СПРАВА В ШАПКЕ (владелец 10.09.2026, BACKLOG «обход iOS 10.09»
   *  п.14б: список папок по канону Apple — справа «Править», в режиме правки «Готово»;
   *  редактор папки — «Готово» сохраняет). Слово готовое, на языке кабинета; тап уходит
   *  вебу событием accountTap с этим ключом. Стоит правее круглых кнопок `actions`.
   *  Старые сборки поля не знают и кнопки не рисуют (CLAUDE.md запрет №8) — поэтому
   *  веб на них оставляет прежний ход (кнопки в пузыре). */
  /** `icon` — SF-символ вместо слова (П92, 13.09.2026: галочка у мультиселект-полушторок).
   *  Ключ НОВЫЙ и необязательный (запрет №8): старая сборка показывает слово `label`. */
  /** `disabled` (Scriptera A, круг 5 §2.7: «✓» «Условий клиента»): true — кнопка видна, но погашена
   *  (alpha 0.4), тап вебу не уходит. Ключ АДДИТИВНЫЙ (запрет №8): старая сборка его игнорирует и
   *  рисует кнопку живой — поэтому хозяин страницы и сам молчит на тап, пока сохранять нечего. */
  topAction?: { key: string; label: string; icon?: string; disabled?: boolean };
  /** ПУСТО — ГЕРОЙ ЭТАЛОНА С КНОПКОЙ (ТЗ «Подключения» п.9, владелец 09.09.2026):
   *  показывать нечего — вместо героя и пузырей встаёт ровно то же, что у пустых
   *  «Рассылок» (`BcastHeroView` flat): серый символ без плитки, заголовок, описание
   *  целиком и капсула действия под ним. Тап по кнопке уходит вебу событием accountTap
   *  с этим ключом, а `page` открывает родную страницу сразу (как `page` у строки).
   *  Пусто — у страницы такого состояния нет; старые сборки поля не знают и рисуют
   *  прежнюю сноску под пузырём (запасной ход, CLAUDE.md запрет №8). */
  empty?: { key: string; page?: string; icon?: string; title: string; meta: string; cta: string };
  /** ВИД ОКНА СТРАНИЦЫ — по словарю трёх видов (Н39, владелец 02.09.2026; канон
   *  docs/NATIVE-SLIDE-PLAN.md §11.0):
   *   • 'push' (по умолчанию) — ГОРИЗОНТАЛЬНАЯ шторка: шаг вглубь того же дерева
   *     (пункт свода, список-справочник), «‹» возвращает к родителю;
   *   • 'deck' — СТОПКА: конечный объект или полноценное окно (карточка человека,
   *     доступа, сотрудника) — родитель уходит назад колодой, за ним три шара;
   *   • 'sheet' — ВЕРТИКАЛЬНАЯ шторка: короткая правка или ввод, после которого
   *     остаёшься на месте («MCP → подключить») — фон под листом СТОИТ;
   *   • 'half' — ПОЛУШТОРКА: та же вертикальная шторка, но открытая НА ПОЛОВИНЕ экрана
   *     (детенты medium+large, грабер, тянется на весь экран пальцем). Так подаются
   *     короткие списки-выборы («Новости», «Важные» уведомлений), где под листом
   *     остаётся видна страница, с которой пришли.
   *   • 'flow' и 'step' — ОДНА ВЕРТИКАЛЬНАЯ ШТОРКА С ШАГАМИ ВНУТРИ (ТЗ «Подключения»
   *     п.10–13, владелец 09.09.2026; рецепт `WizFlowSheet` — тот же, что у потока
   *     добавления триггера): 'flow' — ПЕРВЫЙ шаг, он поднимает лист НА ПОЛОВИНЕ экрана
   *     (детенты medium+large, грабер, слева крестик — он закрывает всю шторку;
   *     владелец 09.09.2026, доработка после сборки 173); 'step' — СЛЕДУЮЩИЙ шаг,
   *     он едет боком ВНУТРИ уже открытой шторки и ПОСЛЕ заезда растит её на весь
   *     экран, слева «назад». Второго листа с навигацией в приложении нет.
   *  Поле ДОБАВЛЕНО, а не заменяет прежнее поведение (CLAUDE.md запрет №8): сборка без
   *  него открывает страницу по-старому — горизонтально, и она же считает незнакомые ей
   *  'half'/'flow'/'step' обычным 'push' (запасной ход старых сборок: шаги едут
   *  отдельными горизонтальными шторками, как до этой правки). */
  present?: 'push' | 'deck' | 'sheet' | 'half' | 'flow' | 'step';
  /** ЗАКРЫТИЕ СТРАНИЦЫ СТОПКИ «×» (Scriptera A): `close` — «×» в правом кружке вместо «‹» (FIX-2 Д0);
   *  `closeLeft` — «×» в ЛЕВОМ кружке (тап — прежнее событие «назад» msgsFoldersBack {key}), правый кружок
   *  свободен под `topAction` (ADM-APP-FIX-4 §2.7, п.25: профиль клиента). Пришли оба — побеждает `closeLeft`.
   *  `closeLeft` ДОБАВЛЕН, старые сборки не знают (запрет №8; веб шлёт его только сборкам ≥ ADM_PROFILE_MIN_BUILD). */
  head?: { close?: true; closeLeft?: true };
  groups: NativeAccountGroup[];
}

/** Всё, что нужно нативу для раздела «Аккаунт» (accountOpen/accountPatch): данные и
 *  готовые строки на языке кабинета. Натив не переводит и не решает — только рисует. */
export interface NativeAccountData {
  theme: 'light' | 'dark';
  /** Герой: имя, почта, подпись канала («Telegram · +…»; пустая — не рисовать),
   *  фото data:image/…;base64 (пустое — круг с буквой initial). */
  hero: { name: string; email: string; sub: string; photo: string; initial: string };
  rows: NativeAccountRow[];
  /** КАТАЛОГ готовых подстраниц: у какой строки свода есть родная страница и что на ней.
   *  Каталог едет ЗАРАНЕЕ — тап открывает страницу мгновенно, не дожидаясь круга к вебу
   *  (правило «дёрганья запрещены»). Строка свода без страницы в каталоге открывается
   *  по-старому веб-шторкой. */
  pages: NativeAccountPage[];
  /** Какие страницы открыты ПО МНЕНИЮ ВЕБА (адрес /app/account/<key>). Натив сверяет со
   *  своей стопкой, только когда значение ИЗМЕНИЛОСЬ: иначе патч со «старым» адресом
   *  закрыл бы страницу, которую человек только что открыл пальцем. */
  stack: string[];
  /** Фон экрана и два фирменных пятна (.cab::before): базовый цвет + розовое и зелёное
   *  пятна '#rrggbb'; прозрачность и геометрия пятен — в нативе, по числам веба. */
  skin: { bg: string; spotPink: string; spotGreen: string };
}

/** ── СПРАВКА РОДНЫМИ ЭКРАНАМИ (владелец 10.09.2026; helpOpen/helpPatch) ─────────────
 *  Страница справки — та же форма, что страница «Аккаунта» (герой, пузыри готовых
 *  СТРОК, поиск), плюс одно своё поле: `blocks` — разобранная статья. Второй формы
 *  страницы и второго разбора текста в приложении не бывает.
 *
 *  ТРИ ВИДА СТРАНИЦ, все в одном каталоге:
 *   • 'help'                  — разделы (корень; подаётся листом-стопкой);
 *   • 'sec:<раздел>'          — статьи раздела;
 *   • 'art:<раздел>/<статья>' — статья: герой, блоки, соседи пузырём.
 *  Каталог едет ЗАРАНЕЕ и целиком (он лёгкий: у статей только названия и описания) —
 *  тап открывает страницу мгновенно, а сам текст статьи доезжает патчем в `blocks`
 *  (пока его нет, у страницы стоит `loading`). */
export interface NativeHelpPage {
  key: string;
  title: string;
  /** Герой страницы — тот же, что у страниц «Аккаунта» (символ раздела, название,
   *  описание). Есть герой — титул живёт в нём, шапка остаётся с одним кружком. */
  hero?: NativeAccountHero;
  /** Текст статьи ещё едет — натив рисует пустой пузырь вместо блоков (без скачков). */
  loading?: boolean;
  /** ПОИСК ПО СПРАВКЕ (только у корня): кружок лупы в шапке раскрывается в капсулу.
   *  Набранное уходит вебу событием helpSet { key, value }, отбирает ВЕБ и присылает
   *  найденные статьи теми же строками. `query` — что набрано (после смены языка и
   *  повторного открытия капсула показывает его). */
  search?: { key: string; placeholder: string; query?: string };
  /** Блоки статьи — КАК ИХ РАЗОБРАЛ `parseMarkdown` (lib/help-markdown.ts): заголовки
   *  2–4, абзацы, списки, цитаты, таблицы, дословный текст, черта; внутри строки —
   *  жирный, курсив, дословно и ссылка. Ссылки веб УЖЕ разрешил: в `href` внутренней
   *  ссылки лежит ключ страницы статьи ('art:…'), и натив открывает её тем же
   *  движением, что строку списка; ссылку со схемой (http, mailto) открывает система. */
  blocks?: NativeHelpBlock[];
  /** Пузыри готовых строк: разделы и статьи раздела. У статьи пузырей нет — под текстом
   *  соседних статей больше не бывает (П21 обхода владельца 11.09.2026, круг 2). */
  groups: NativeAccountGroup[];
  /** ПУБЛИЧНЫЙ АДРЕС СТАТЬИ — им работает кнопка «Поделиться» родной шапки (П21 обхода
   *  владельца 11.09.2026, круг 2): тот же адрес, что у страницы справки на сайте, БЕЗ
   *  «?app=1» — по ссылке из приложения человек попадает на обычную страницу. Пусто или
   *  нет вовсе (разделы, корень) — кнопки нет. Поле ДОБАВЛЕНО (CLAUDE.md запрет №8):
   *  старые сборки его не читают и шапку рисуют как раньше. */
  share?: string;
  /** ЗАГОЛОВОК РОДИТЕЛЯ для кнопки «назад» (владелец 11.09.2026, П11 обхода: «непонятно,
   *  куда ведёт кружок»): у раздела это «Справка», у статьи — имя её раздела, а открыли
   *  статью из поиска — «Справка» (родителем считается тот, кто под ней в стопке).
   *  Натив рисует капсулу «‹ <слово>» вместо голого кружка — родной приём iOS
   *  (`UINavigationItem.backButtonTitle`). Пусто — кружок как раньше; поле ДОБАВЛЕНО,
   *  старые сборки его не читают (CLAUDE.md запрет №8). */
  back?: string;
  /** ПУТЬ ДО СТРАНИЦЫ серой строкой над заголовком: «Справка › Боты» у статьи. У корня и
   *  у раздела пусто — там путь повторял бы кнопку «назад» и заголовок. Поле ДОБАВЛЕНО
   *  (запрет №8). */
  crumb?: string;
  /** Родное окно страницы: 'deck' — корень справки (лист-стопка поверх кабинета),
   *  'push' — слой стопки (горизонтальная шторка внутри листа). Словарь видов —
   *  docs/NATIVE-SLIDE-PLAN.md §11.0; третьего вида у справки нет. */
  present?: 'deck' | 'push';
}

/** Всё, что нужно нативу для справки. Натив не переводит, не отбирает и в сеть не
 *  ходит — веб присылает готовые страницы на языке кабинета. */
export interface NativeHelpData {
  theme: 'light' | 'dark';
  pages: NativeHelpPage[];
  /** Что открыто ПО МНЕНИЮ ВЕБА (снизу вверх: корень, раздел, статья, статья по ссылке).
   *  Натив сверяет со своей стопкой только на ИЗМЕНЕНИЕ — иначе патч со «старым»
   *  списком закрыл бы слой, который человек открыл пальцем секунду назад. */
  stack: string[];
  /** Фон экрана и два фирменных пятна — те же числа, что у страниц «Аккаунта». */
  skin: { bg: string; spotPink: string; spotGreen: string };
}

/** Ряд списка переписок: ГОТОВЫЕ строки кабинета. Натив ничего не форматирует, не
 *  переводит и в сеть не ходит — фото приезжает картинкой, время и пометка словами. */
export interface NativeMsgsRow {
  id: string;
  /** Имя собеседника (уже с запаской «без имени»). */
  title: string;
  /** Последняя реплика одной строкой: «Вы: Фото · подпись». */
  preview: string;
  /** Время последней реплики словами кабинета (msgDialogTime). */
  time: string;
  /** Непрочитанные; 0 — значка нет. */
  unread: number;
  /** Имя картинки каталога приложения: QuickTile-telegram|instagram. */
  icon: string;
  /** Фирменный цвет сети '#rrggbb' (чужая палитра, не наши роли — не перекрашивать). */
  iconColor: string;
  /** Имя аккаунта; пусто — не рисовать (аккаунт у клиента один). */
  account: string;
  /** Пометка состояния ГОТОВЫМ словом («Ждёт ответа», «Писать нельзя»); пусто — нет. */
  tag: string;
  /** Пометка тревожная — красится сигнальной ролью. */
  tagWarn: boolean;
  /** Фото собеседника data:image/…;base64 (пустое — кружок с буквой). */
  photo: string;
  /**
   * ФОТО АККАУНТА, КОТОРЫЙ ВЕДЁТ ПЕРЕПИСКУ (владелец 12.09.2026, П45): круглая аватарка
   * 14 pt рядом со значком сети — по ней видно, каким подключённым аккаунтом идёт
   * переписка. data:image/…;base64 тем же конвейером, что `photo`; пусто — кружка нет
   * вовсе (буквы вместо фото у аккаунта не бывает).
   *
   * INVARIANT(scriptera): поле — ТОЛЬКО необязательная добавка к ряду; ни имя, ни форма,
   * ни смысл прежних полей не меняются, метода моста под это не заводится. Старая сборка
   * поля не читает и рисует ряд как прежде. Причина: контракт моста заморожен (запрет
   * №8 в CLAUDE.md) — сборки в сторе живут месяцами.
   */
  accountPhoto?: string;
  initial: string;
  /** Цвет кружка буквы '#rrggbb' (тот же circleColor, что в вебе). */
  initialColor: string;
  /**
   * НАЙДЕННАЯ РЕПЛИКА, А НЕ ПЕРЕПИСКА (круг 11 плана CHAT-LOOK, п.17.2): при поиске
   * вслед за рядами переписок идут ряды находок — «кто, когда, кусок реплики вокруг
   * слова». `id` у такого ряда — 'hit:<id реплики>'; тап возвращается тем же событием
   * msgsTap, и веб по приставке открывает переписку НА этой реплике.
   *
   * INVARIANT(scriptera): три поля ниже — ТОЛЬКО необязательная ДОБАВКА к ряду; ни имя,
   * ни форма, ни смысл прежних полей не меняются, и метода моста под находки не
   * заводится. Причина: контракт моста заморожен (запрет №8 в CLAUDE.md) — сборки в
   * сторе живут месяцами; старая сборка этих полей не читает и обязана рисовать такой
   * ряд как обычный ряд списка, а тап по нему всё равно откроет переписку.
   * Канон: CLAUDE.md, запрет №8.
   */
  /** Переписка, в которой нашлась реплика (у ряда переписки поля нет). */
  hitDialogId?: string;
  /** Сама найденная реплика (у ряда переписки поля нет). */
  hitMessageId?: string;
  /** Где внутри `preview` стоит найденное слово (в знаках, как NSRange) — натив рисует
   *  этот кусок жирным. Слова в куске нет — поля нет. */
  mark?: { start: number; len: number };
  /**
   * ГДЕ ВНУТРИ `title` СТОИТ НАЙДЕННОЕ СЛОВО (П5 обхода владельца 11.09.2026): ряд-
   * переписка попадает в выдачу и по ИМЕНИ собеседника, а имя рисовалось обычным — было
   * не видно, где совпало (скрин 10:27 «ТРУШИНА ДАРЬЯ»). Позиция — та же мерка, что у
   * `mark`: знаки строки JavaScript (UTF-16), ими же считает NSRange в Swift.
   *
   * INVARIANT(scriptera): поле — ТОЛЬКО необязательная добавка; имя, форма и смысл
   * прежних полей ряда не меняются, нового метода моста под это нет. Старая сборка поля
   * не читает и рисует имя как обычно. Причина: контракт моста заморожен (запрет №8).
   */
  titleMark?: { start: number; len: number };
}

/** Всё, что нужно нативу для вкладки «Чаты» (msgsOpen/msgsPatch): готовые ряды и фон.
 *  Никакой логики в Swift — веб присылает уже отобранное и отсортированное; ПИЛЮЛЬ
 *  ОТБОРА на экране нет вовсе (владелец 01.09.2026: «фильтры убрать, вместо них поиск»). */
/** НАСТРОЙКИ ПАПОК «ЧАТОВ» (msgsFoldersSheet/Patch): полушторка со стопкой страниц,
 *  собранных ВЕБОМ теми же моделями, что страницы «Аккаунта». `stack` — что открыто по
 *  мнению веба (как у accountOpen): натив сверяет со своей стопкой, только когда
 *  значение изменилось. Первая страница подаётся как 'flow' (полушторка), следующие —
 *  'step' (едут боком внутри неё) — вид окна страница называет сама, полем `present`. */
export interface NativeMsgsFoldersData {
  theme: 'light' | 'dark';
  pages: NativeAccountPage[];
  stack: string[];
}

/** СТОПКА ПОДКЛЮЧЕНИЯ ИЗ МАСТЕРА (wizConnOpen/wizConnPatch) — ТЕ ЖЕ данные, что у
 *  настроек папок: тема, готовые страницы и стопка. Своей формы данных у третьего
 *  хозяина страниц нет — псевдоним, а не копия (владелец 11.09.2026). */
export type NativeWizConnData = NativeMsgsFoldersData;

export interface NativeMsgsData {
  theme: 'light' | 'dark';
  /** Подсказка плашки поиска — ГОТОВОЕ слово кабинета («Поиск»). */
  searchHint: string;
  /** Набранное слово: плашка показывает его вместо подсказки (как у «Ботов»). Ввод идёт
   *  РОДНОЙ строкой поиска сверху (openSearch → searchQuery), своего поля у экрана нет. */
  query: string;
  rows: NativeMsgsRow[];
  /** Рядов нет — ГОТОВАЯ строка кабинета, почему пусто. */
  emptyText: string;
  /** Рама веб-строки поиска в точках: родная плашка встаёт РОВНО на её место — тот же
   *  замер и тот же смысл, что у «Ботов» (владелец 02.09.2026, Н5 фикслиста). */
  search: { top: number; left: number; width: number; height: number };
  /**
   * ЕСТЬ ЛИ ЧТО ГРУЗИТЬ ДАЛЬШЕ (правило 2 владельца 09.09.2026: «список чатов — весь»).
   * Родной список, подойдя к концу, шлёт событие msgsMore, веб догружает страницу и
   * дописывает ряды. Во время поиска признака нет (выдача поиска не листается).
   *
   * INVARIANT(scriptera): поле — ТОЛЬКО необязательная добавка, метода моста под догрузку
   * не заводится; старая сборка поля не читает и живёт как раньше — первой страницей.
   * Причина: контракт моста заморожен (запрет №8 в CLAUDE.md).
   */
  more?: boolean;
  /**
   * ПАПКИ «ЧАТОВ» — ПОЛОСА НАД СПИСКОМ (ТЗ M.4, владелец 09.09.2026, как в Telegram):
   * круглая кнопка настроек и капсулы папок со счётчиком непрочитанных. Слова и цифры
   * ГОТОВЫЕ — натив не переводит и не считает. Тап по капсуле возвращается событием
   * msgsFolder { id }, тап по шестерёнке — msgsFoldersOpen {}.
   *
   * Поля нет (старая сборка, старый веб) — полосы на экране нет вовсе, список идёт
   * без отбора. ВОРОНКИ (прежнее поле `filters`) веб больше НЕ ШЛЁТ: единственный отбор
   * переписок — папка.
   */
  folders?: {
    /** id активной папки; пусто — без отбора. */
    current: string;
    /** Папки по порядку (position). */
  /**
   * ПАПКА-АККАУНТ — КРУЖКАМИ ВМЕСТО СЛОВА (владелец 12.09.2026, П65: «в капсуле два
   * кружочка: значок соцсети и фотография… короткие названия»). Поля НЕОБЯЗАТЕЛЬНЫЕ и
   * едут только у папки, помеченной аккаунтом:
   *   `photo`     — фотография аккаунта кружком, base64 (тот же конвейер imgSquarePng,
   *                 размер 40); пусто или поля нет — рисуется один значок сети;
   *   `icon`      — имя значка каталога приложения: 'QuickTile-telegram' | 'QuickTile-instagram';
   *   `iconColor` — фирменный цвет сети строкой '#rrggbb'.
   * Имя папки остаётся в `name` (настройки, подсказка доступности).
   *
   * INVARIANT(scriptera): поля — ТОЛЬКО необязательные добавки, метода моста под них не
   * заводится; старая сборка их не читает и рисует папку словом, как раньше. Причина:
   * контракт моста заморожен (запрет №8 в CLAUDE.md).
   */
    items: { id: string; name: string; unread: number; photo?: string; icon?: string; iconColor?: string }[];
    /** Готовое слово для голосового доступа к кнопке настроек. */
    settingsLabel: string;
  };
  /**
   * ПЕРВЫЕ СТРАНИЦЫ СОСЕДНИХ ПАПОК (правила 6–7 владельца 11.09.2026: «нативный офигенный
   * свайп — свайпаю вбок, контент едет горизонтально, а пузырь в полосе папок ползёт
   * пропорционально»). Папки листаются пейджером, и соседняя страница обязана быть НЕ
   * ПУСТОЙ ещё до того, как палец её дотянет: здесь лежит по записи на каждую папку из
   * `folders.items`, КРОМЕ текущей (её ряды — обычное поле `rows`), в том же порядке.
   *
   * Ряды собраны той же рукой, что `rows`, и взяты ИЗ КЭША кабинета — опрос сервера
   * по-прежнему ведёт только текущая папка. Кэша ещё нет (прогрев не доехал) — у записи
   * пустые `rows` и `emptyText: ''`: натив рисует пустую страницу БЕЗ слов, слова приедут
   * вместе с рядами. `emptyText` — те же слова, что `emptyText` экрана: «переписок пока
   * нет» или «ничего не найдено», смотря есть ли у папки условия.
   *
   * Поля нет — свайпа нет: полоса папок и тап по слову работают как раньше. Во время
   * поиска и при одной папке поля нет тоже.
   *
   * INVARIANT(scriptera): поле — ТОЛЬКО необязательная добавка, метода моста под пейджер
   * не заводится; старая сборка поля не читает и живёт как раньше — одной страницей.
   * Причина: контракт моста заморожен (запрет №8 в CLAUDE.md).
   */
  pages?: { id: string; rows: NativeMsgsRow[]; emptyText: string }[];
  /**
   * РЕЖИМ ВЫБОРА ПЕРЕПИСОК — КНОПКА «ИЗМЕНИТЬ» В ШАПКЕ (второй обход 10.09.2026,
   * BACKLOG п.17б/в, образец — «Чаты» Telegram): у переписок появляются кружки-галочки,
   * внизу одна кнопка — «Прочитать (N)» для отмеченных или «Прочитать все», когда не
   * отмечено ничего. УДАЛЕНИЯ В РЕЖИМЕ НЕТ.
   *
   * Тут едут ТОЛЬКО СЛОВА кабинета: сам режим и отметки ведёт натив (галочка обязана
   * вставать под пальцем тем же кадром), а наверх возвращается одно намерение — событие
   * `msgsRead { ids, all }`, и читает переписки ВЕБ своей ручкой.
   *
   * `readLabel` несёт метку `%n` — на её место натив ставит число отмеченных. Это
   * единственная подстановка на экране: слово, его форма и порядок слов остаются здесь.
   *
   * INVARIANT(scriptera): поле — ТОЛЬКО необязательная добавка, метода моста под режим
   * не заводится; старая сборка поля не читает, и кнопки «Изменить» у неё нет вовсе.
   * Причина: контракт моста заморожен (запрет №8 в CLAUDE.md).
   */
  edit?: {
    /** «Изменить» — включить режим. */
    label: string;
    /** «Готово» — выйти из режима. */
    doneLabel: string;
    /** «Прочитать (%n)» — действие для отмеченных. */
    readLabel: string;
    /** «Прочитать все» — когда не отмечено ничего. */
    readAllLabel: string;
  };
  /** Фон экрана и два фирменных пятна — те же числа, что у родного «Аккаунта». */
  skin: { bg: string; spotPink: string; spotGreen: string };
}

/** Ряд родного списка «Ботов»: ГОТОВЫЕ строки кабинета и плитка канала. Натив ничего не
 *  переводит, не считает и не решает — только рисует. Ключ возвращается событием botsTap. */
/** Значок-ссылка у имени карточки: ключ тапа, SF-символ и готовая подпись (её натив
 *  ставит подписью доступности — своих слов у него не бывает). */
export interface NativeBotsLink {
  key: string;
  icon: string;
  label: string;
}

export interface NativeBotsRow {
  /** Ключ карточки: 'card:<id>' и 'menu:<id>' собираются из него. */
  id: string;
  title: string;
  /** ОПИСАНИЕ бота второй строкой (в вебе — .scn-card-desc у ScnCardBody); пусто —
   *  строки нет. Считает веб — тем же выражением, что кормит веб-карточку. */
  desc: string;
  /** ПОДСТРОКА «КТО ЭТОТ БОТ» — ПО ЧАСТЯМ, а не одной склеенной строкой: в вебе она
   *  собрана из кусков со своим видом (.scn-card-meta), и значок сети рисуется РОВНО
   *  перед логином. Порядок тот же, что в вебе: категория · метка мастера · значок
   *  сети + логин; точка-разделитель встаёт только там, где слева что-то есть. */
  /** Имя своей категории; пусто — категории нет. */
  category: string;
  /** Цвет имени категории '#rrggbb'; пусто — серым. */
  categoryColor: string;
  /** Бот собран анкетой — палочка мастера и слово «быстрая настройка» (знак листа
   *  шаблонов, .tpl-wiz-mark в вебе). */
  wand: boolean;
  /** ГОТОВОЕ слово метки мастера («быстрая настройка»); пусто — только палочка. */
  wandLabel: string;
  /** Логин аккаунта. СТРОКИ С ЛОГИНОМ НА КАРТОЧКЕ БОЛЬШЕ НЕТ (владелец 13.09.2026, П78:
   *  «аккаунт понятен по аватарке») — веб шлёт сюда пустую строку, и рисовать её нечем.
   *  Поле оставлено ради старых сборок из стора: они его читают и просто не найдут строки. */
  login: string;
  /** ЗНАЧОК СЕТИ ПЕРЕД ЛОГИНОМ. Вместе с `login` пуст всегда (П78). */
  loginIcon: string;
  /** ЗНАЧОК СЕТИ СПРАВА ОТ НАЗВАНИЯ — имя картинки каталога приложения
   *  (QuickTile-telegram|instagram): натив рисует его в строке имени ПОСЛЕ названия и
   *  значков-ссылок, 14×14, тоном `titleIconColor`. Пусто — значка нет (у формы и чата на
   *  сайте сети нет). Новый необязательный ключ (запрет №8): старая сборка его не знает и
   *  рисует строку имени как прежде. */
  titleIcon?: string;
  /** Цвет значка сети '#rrggbb' — тот же фирменный тон, что у плитки канала (channelHex).
   *  Новый необязательный ключ (запрет №8). */
  titleIconColor?: string;
  /** ЗНАЧКИ-ССЫЛКИ У ИМЕНИ («Открыть форму», «Открыть чат»): в вебе это .scn-name-link
   *  рядом с названием. Открывает их ВЕБ прежним путём (window.open) — натив только
   *  рисует и возвращает тап ключом '<key>:<id>'. */
  links: NativeBotsLink[];
  /** ФОТО АККАУНТА, от чьего имени работает бот (data:image/…;base64 без приставки —
   *  тот же конвейер, что у аватара «Аккаунта» и кружков «Аналитики»). Главнее плитки
   *  канала: в вебе аватар карточки — это ЛИЦО аккаунта (ScnCardAvatar, владелец
   *  2026-08-01), а плитка канала встаёт, только когда фото нет. */
  photo: string;
  /** Плитка канала: имя картинки каталога приложения (QuickTile-telegram|instagram|catalog). */
  icon: string;
  /** Цвет сети числами — своего разбора hex у плитки нет (как у ряда листа шаблонов). */
  red: number;
  green: number;
  blue: number;
  /** Состояние СЛОВАМИ кабинета («Включено», «Выключено», «включаем…»). */
  state: string;
  /** Тон чипа состояния: 'run' | 'off' | 'wait'. */
  stateTone: string;
  /** Чип состояния — КНОПКА (в вебе это `<button>` с onSt: тап включает/выключает бота
   *  или открывает причину «не включился»). Тап уходит событием 'st:<id>'. Пусто —
   *  чип статичен (у веб-карточки без onSt он тоже просто `<span>`). */
  stateTap: boolean;
  /** Чип состояния сейчас в ходу («включаем…») — тап молчит, как disabled у веба. */
  stateBusy: boolean;
  /** Включён ли бот (14.09.2026). Переключателя на карточке больше НЕТ (владелец 14.09:
   *  включение и выключение — только в меню, у приложения — долгим нажатием карточки);
   *  ключ остаётся для сборок 205–208, которые рисовали UISwitch. Необязательный. */
  stateOn?: boolean;
  /** КОНВЕРСИЯ ЗА НЕДЕЛЮ (та же, что цифры легенды) — нижняя строка карточки (владелец
   *  14.09.2026): слово и значение готовыми строками; справа от неё натив ставит состояние
   *  точкой и словом (`state`, `stateTone`). Ключ новый и необязательный. */
  conv?: { label: string; value: string };
  /** Готовые строки цифр карточки («Контактов: 3», «Конверсия 12%»); пусто — цифр нет. */
  nums: string[];
  /** Готовая строка чипа сбоев за сутки; пусто — чипа нет. */
  errors: string;
  /** Чип сбоев — КНОПКА (в вебе KBareButton с onErrors: тап открывает журнал запусков на
   *  упавших прогонах). Тап уходит событием 'err:<id>'. */
  errorsTap: boolean;
  /** ЛИНИЯ ОТКРЫТИЙ ПО ДНЯМ на карточке (владелец 10.09.2026, BACKLOG «обход iOS 10.09»
   *  п.16б: не столбики, а ровная линия с подписанными верхней и нижней точками).
   *  `points` — в порядке рисования слева направо (старые → сегодня), `hi`/`lo` —
   *  ГОТОВЫЕ подписи крайних точек (числа на языке кабинета). Пусто — линии нет
   *  (открытий не было или пульс ещё едет). Старые сборки поле не знают. */
  line?: {
    points: number[];
    hi: string;
    lo: string;
    /** ВТОРАЯ ЛИНИЯ — подарки по дням тем же порядком (владелец 12.09.2026: «добавь на
     *  график подарки»), и подпись её максимума. Ключи НОВЫЕ и необязательные (запрет №8). */
    points2?: number[];
    hi2?: string;
    /** Цвет линии hex и потолок её оси (владелец 14.09.2026: «график одного цвета, а
     *  конверсия точка другого — у тебя там разлад»). Ключи НОВЫЕ и необязательные
     *  (запрет №8): пусто — цвет сети бота и потолок по точкам, как было. */
    ink?: string;
    max?: number;
  };
  /** ВТОРАЯ ЛИНИЯ КАРТОЧКИ — КОНВЕРСИЯ ПО ДНЯМ (П77, 13.09.2026): свой график под линией
   *  контактов, ось 0–100. Ключ НОВЫЙ и необязательный (запрет №8). */
  line2?: { points: number[]; hi: string; lo: string; /** Цвет линии hex (П113); пусто — роль `--green`. */ ink?: string };
  /** ЛЕГЕНДА ЛИНИЙ карточки (владелец 12.09.2026, П69: «непонятно, что за линии и цифры»):
   *  готовые строки «N запусков за 7 дней», «N подарков» с цветом кружка (hex — цвет сети
   *  бота / зелёная роль). Ключ НОВЫЙ и необязательный (запрет №8): старая сборка видит
   *  карточку без легенды, как было. */
  legend?: Array<{ text: string; ink: string; /** Без кружка (П113): цифра, а не линия. */ plain?: boolean }>;
  /** ТРЕТЬЯ ЛИНИЯ КАРТОЧКИ — КОНВЕРСИЯ своей осью 0…`max` (П119, владелец 14.09.2026:
   *  на карточке рисуются те линии, которые человек выбрал в «О боте»). Ключ НОВЫЙ и
   *  необязательный (запрет №8): старая сборка рисует две линии, как рисовала. */
  line3?: { points: number[]; max: number; ink?: string };
  /** СРОК ГРАФИКА ОДНОЙ ЦИФРОЙ (П119): готовая строка «7 дней» / «90 дней, по неделям» —
   *  натив ставит её в конец строки подписей. Ключ НОВЫЙ и необязательный. */
  lineDays?: string;
  /** БОТ ВЫКЛЮЧЕН — значок у названия рядом со значком сети (П119). Ключ НОВЫЙ и
   *  необязательный: старая сборка значка не рисует, состояние она берёт из подвала. */
  off?: boolean;
}

/** Всё, что нужно нативу для вкладки «Боты» (botsOpen/botsPatch): данные и готовые
 *  строки на языке кабинета. Идемпотентно — патч шлёт весь словарь заново. */
export interface NativeBotsData {
  theme: 'light' | 'dark';
  rows: NativeBotsRow[];
  /** Готовые слова экрана: подсказка поиска, набранный запрос, строка пустого списка
   *  (она же говорит, что пусто ИЗ-ЗА ОТБОРА — текст считает веб). */
  dict: { search: string; query: string; empty: string; /** Пустая папка-категория (П81). */ emptyFolder?: string };
  /** ПАПКИ-КАТЕГОРИИ (владелец 13.09.2026, П81): капсула над списком как у «Чатов»; id —
   *  имя категории, «all» — все боты. Ключ НОВЫЙ и необязательный (запрет №8): старая
   *  сборка его не читает и рисует список без капсулы. Тапы: 'folder:<id>',
   *  'folder:settings'. */
  folders?: { items: Array<{ id: string; name: string; unread: number }>; current: string; settingsLabel: string };
  /** Геометрия веб-строки поиска в точках — ЖИВОЙ замер. Родное поле встаёт РОВНО на её
   *  место: полоса плиток («пасхалка» ЖЕЛЕЗНОЕ, UI-FIXES-JOURNAL 07–08.08) режет экран по
   *  середине этой строки, и своей геометрии у родного поля быть не должно. */
  search: { top: number; left: number; width: number; height: number };
  /** Фон экрана и два фирменных пятна (.cab::before) — те же роли, что у «Аккаунта». */
  skin: { bg: string; spotPink: string; spotGreen: string };
  /** Цвета чипов карточки '#rrggbb' — РОЛИ палитры кабинета, разрешает их веб (у натива
   *  своего разбора палитры нет, а тема и набор кабинета живые): «Включено» — зелёный,
   *  «Выключено» — янтарный, ход — голубой, сбои — сигнальный красный. */
  tones: { run: string; off: string; wait: string; err: string; warn: string; link: string };
  /** ПОЛОСА ПАПОК «БОТОВ» СПРЯТАНА ЧЕЛОВЕКОМ (владелец 13.09.2026, П85: кнопка «Скрыть
   *  папки» на странице «Настройки → Папки → Боты»): капсулы категорий над списком не
   *  рисуются вовсе, остаётся один список. Сами категории при этом живы — спрятана
   *  только полоса. Пусто/false — полоса как обычно.
   *
   *  Ключ ДОБАВЛЕН (CLAUDE.md запрет №8): сборка без него полосу рисует как раньше. */
  foldersHidden?: boolean;
  /** ПОЛОСА-ПРЕДУПРЕЖДЕНИЕ ПОВЕРХ СПИСКА (в вебе .k-toast--warn): пересечение
   *  триггеров при включении бота. Текст считает веб, гасит его тоже веб (пустая
   *  строка — полосы нет). Своего таймера и своих слов у натива нет. */
  toast: string;
}

/** КАРТОЧКА ЧЕЛОВЕКА родным листом — ОДНА НА «ЧАТЫ» И «КОНТАКТЫ» (владелец 09.09.2026,
 *  отменяет прежнюю пару «лист собеседника + страница карточки контакта»): крупное фото,
 *  имя и логин героем, факты строками, метки и переменные группами, действия внизу.
 *  Слова, остаток окна ответа, свежий профиль и факты человека считает ВЕБ одной рукой
 *  (`useContactCardNative` в components/cabinet/contacts.tsx) — натив только рисует.
 *  Строки — общий протокол NativeAccountField (второй формы строки в приложении нет):
 *  факт — 'value', действие — 'nav'/'button' (тап уходит chatInfoTap), правка — 'text'
 *  (готовое слово возвращается chatInfoSet). */
export interface NativeChatInfoData {
  /** Чья карточка: патч чужой натив не примет (открыли соседнюю). Из «Чатов» это id
   *  переписки, из «Контактов» — 'ct:<id контакта>'; натив ключ не разбирает. */
  dialogId: string;
  theme: 'light' | 'dark';
  /** Герой: фото (data:image/…;base64), имя и логин. Выбора снимка тут не бывает. */
  hero: NativeAccountHero;
  /** ПЛОСКИЙ СПИСОК ФАКТОВ одним пузырём — прежняя форма карточки. Старые сборки читают
   *  ТОЛЬКО его, поэтому веб шлёт сюда те же строки, что стоят в первой группе. */
  fields: NativeAccountField[];
  /** Пояснение под карточкой готовой строкой (окно ответа Инстаграма); пусто — нет. */
  hint: string;
  /** ГРУППЫ КАРТОЧКИ (метки, служебные метки, переменные, действия) — ДОБАВЛЕННОЕ поле
   *  (CLAUDE.md запрет №8): сборка без него рисует прежний один пузырь из `fields`, а
   *  свежая — все группы и не читает `fields` вовсе. */
  groups?: NativeAccountGroup[];
  /** ПОДСТРАНИЦА КАРТОЧКИ «Служебные данные» (владелец 09.09.2026, ТЗ «Контакты» п.4):
   *  идентификатор, служебные метки ботов и переменные — техническое из карточки убрано
   *  и лежит шагом вглубь. Поле есть — натив показывает подстраницу ВМЕСТО карточки
   *  (шаг вбок внутри того же листа), «назад» возвращает событием chatInfoTap с ключом
   *  'ct:tech:back'. Поля НЕТ — карточка как обычно; открывает и закрывает подстраницу
   *  ВЕБ (он мозг), натив только рисует и просит.
   *  ДОБАВЛЕННОЕ поле (CLAUDE.md запрет №8): сборка без него подстраницы не знает и
   *  рисует прежнюю карточку. */
  sub?: { title: string; groups: NativeAccountGroup[] };
  /** КРУЖКИ ШАПКИ ЛИСТА (владелец 10.09.2026, п.12е): слева крестик (закрыть), справа —
   *  «Открыть переписку» (тап → chatInfoTap с его ключом). Старые сборки поле не знают. */
  head?: { close: string; open?: { key: string; label: string } };
  /** ЛИСТ МЕТОК ПОВЕРХ КАРТОЧКИ (п.12г): `open` — показать/убрать (решает веб); `items` —
   *  метки человека; `known` — чем можно дополнить (ручные метки людей клиента, без уже
   *  стоящих); тапы: `ct:tag:add:<слово>`, `ct:tag:rm:<слово>`, `ct:tags:close`. */
  tags?: {
    open: boolean;
    title: string;
    items: string[];
    known: string[];
    /** ВСЕ МЕТКИ КЛИЕНТА, включая те, что уже стоят у человека (П17 обхода владельца
     *  11.09.2026, круг 2): родной лист рисует ими один общий список с галочками —
     *  «эти есть, эти можно поставить». Ключ ДОБАВОЧНЫЙ (CLAUDE.md запрет №8): сборка без
     *  него работает по-прежнему — `items` отдельно, `known` отдельно. */
    all?: string[];
    /** Заголовок общего списка («Все метки») — слово от веба, как и все остальные. */
    allLabel?: string;
    addLabel: string;
    /** КОРОТКОЕ слово кнопки-капсулы у поля («Добавить»): в капсуле длинное `addLabel`
     *  («Добавить метку») не помещается. Ключ ДОБАВОЧНЫЙ (запрет №8) — сборка без него
     *  подписывает кнопку прежним `addLabel`. */
    addBtn?: string;
    addPh: string;
    emptyHint: string;
    doneLabel: string;
    busy: boolean;
    /** СВАЙП «УДАЛИТЬ» В РОДНОМ СПИСКЕ МЕТОК (владелец 15.09.2026): слово плашки, вопрос
     *  подтверждения и «Отмена». Ключи добавочные (запрет №8): сборка без них свайпа не
     *  показывает, остальное работает как прежде. */
    delLabel?: string;
    delAsk?: string;
    cancel?: string;
    /** Подпись под списком: метки общие для всех людей, удаление снимает у всех
     *  (владелец 15.09.2026). Добавочный ключ (запрет №8). */
    sharedNote?: string;
  };
}

/** ЧТО МОЖНО ПРИЛОЖИТЬ В ПЕРЕПИСКУ — решает веб (правила каналов, chatAttachLimit), а
 *  натив открывает ровно те источники, которые ему назвали, и отдаёт выбранный файл
 *  СРАЗУ (событие chatFile).
 *
 *  ЛИСТА-ПРЕВЬЮ С ПОДПИСЬЮ БОЛЬШЕ НЕТ (владелец 01.09: «как в вебе»): веб на этом месте
 *  ничего не показывает — файл уходит сразу, подписью служит текст поля ответа. Поэтому
 *  сюда не едут ни предел канала, ни слова превью: и «формат не поддерживается», и
 *  «файл слишком большой» веб говорит САМ, там же, где говорит в вебе (onPickFile). */
export interface NativeChatAttachAsk {
  theme: 'light' | 'dark';
  /** Показывать библиотеку снимков и видео (канал принимает картинку или видео). */
  photos: boolean;
  /** Показывать выбор документа. */
  files: boolean;
  /** Видео из библиотеки разрешено (канал принимает video) — иначе только снимки. */
  videos: boolean;
  /** MIME-типы документа (пусто — любой файл): тот же список, что у веб-поля выбора. */
  accept: string[];
  /** Сколько байт файла ещё разумно провезти МОСТОМ. Больше — событие chatFileHeavy, и
   *  веб открывает свой прежний выбор файла (там байты никуда не переезжают). */
  bridgeBytes: number;
  /** Готовые слова окна «откуда брать файл». */
  dict: {
    title: string;
    cancel: string;
    photo: string;
    file: string;
  };
}

/** ── РОДНАЯ СТРАНИЦА СТАТИСТИКИ АККАУНТА (anaPageOpen/anaPagePatch) ────────────────
 *  Одна страница — аккаунт Instagram или бот Telegram (components/cabinet/
 *  account-stats-native.tsx), открывается из карточки подключения. Натив НИЧЕГО не
 *  считает и не переводит: числа приезжают ГОТОВЫМИ строками, точки графика — уже
 *  посчитанными. */

/** Карточка одной цифры: подпись, ГОТОВОЕ значение (уже с разделителями языка) и
 *  вторая строка. Пустое значение — «пока нет данных» (строку тоже шлёт веб). */
export interface NativeAnaCard {
  label: string;
  value: string;
  sub: string;
  /** Значения нет — веб уже положил в `value` свою строку «пока нет данных», а натив
   *  рисует её мелким серым (в вебе это тот же `.sub` на месте цифры). */
  dim?: boolean;
}

/** Плитка публикации Instagram: снимок берётся по адресу (тот же путь, что у сетки
 *  публикаций родного мастера), под ним готовые строки лайков и комментариев. */
export interface NativeAnaPost {
  id: string;
  thumb: string;
  video: boolean;
  likes: string;
  comments: string;
}

/** Кусок страницы статистики. Натив умеет ровно пять видов и ничего не додумывает:
 *   • stats  — ряд карточек чисел;
 *   • chart  — пузырь с графиком: имя, крупная строка и столбики по дням;
 *   • posts  — сетка публикаций;
 *   • button — кнопка страницы (тап шлёт anaTap с её ключом);
 *   • note   — готовая строка объяснения. */
export interface NativeAnaBlock {
  kind: 'stats' | 'chart' | 'posts' | 'button' | 'note';
  /** Имя блока над содержимым ('chart' | 'posts'). */
  label?: string;
  /** 'stats': карточки чисел. */
  cards?: NativeAnaCard[];
  /** 'chart': столбики в порядке рисования. */
  bars?: number[];
  /** 'chart': крупная строка над графиком («За неделю: 12»). */
  value?: string;
  /** 'chart' | 'posts': ГОТОВАЯ строка, когда рисовать нечего. */
  empty?: string;
  /** 'posts': плитки публикаций. */
  posts?: NativeAnaPost[];
  /** 'button': ключ тапа и подпись кнопки. */
  key?: string;
  /** 'note': готовая строка. */
  text?: string;
}

/** Родной лист выбора «Аналитики» (отбор по группам, период страницы): готовые подписи,
 *  ряды — общая форма листа «Аккаунта» (второго вида листа в приложении не заводим). */
export interface NativeAnaPick {
  /** Заголовок листа. */
  title: string;
  /** Подпись кнопки (что выбрано сейчас). */
  label: string;
  /** ЧТО выбрано — значение ряда, а не подпись (по нему лист ставит галочку). */
  picked: string;
  options: NativeAccountOption[];
}

/** Страница статистики (аккаунта или бота). `key` — он же ключ тапа, открывшего её. */
export interface NativeAnaPage {
  key: string;
  title: string;
  /** Герой страницы — та же форма, что у страниц «Аккаунта» (общий вид, не копия). */
  hero?: NativeAccountHero;
  /** Период страницы — родной лист выбора кнопкой в шапке; пусто — периода у неё нет. */
  period?: NativeAnaPick;
  /** Данные ещё едут — натив держит место пустым пузырём, без скачков содержимого. */
  loading?: boolean;
  blocks: NativeAnaBlock[];
}

/** Всё, что нужно нативу для родной «Аналитики». Идемпотентно — патч шлёт словарь заново. */
export interface NativeAnaPageData {
  theme: 'light' | 'dark';
  /** Одна страница статистики: аккаунт Instagram или бот Telegram. */
  page: NativeAnaPage;
  /** Фон экрана и два фирменных пятна (.cab::before) — те же роли, что у «Аккаунта». */
  skin: { bg: string; spotPink: string; spotGreen: string };
  /** Цвета графика — РОЛИ палитры кабинета (--ov-bar у столбика, --line2 у пустого дня).
   *  `alpha` — прозрачность роли столбика: в вебе он нарисован чернилами ВПОЛСИЛЫ, а hex
   *  канал прозрачности не несёт; число меряется на живой странице, своего у натива нет. */
  bars: { ink: string; zero: string; alpha: number };
}

/** ── РОДНЫЕ ОКНА КАРТОЧКИ БОТА (statsOpen/statsPatch, волна 3) ────────────────────
 *  Одно окно на все прежние веб-шторки карточки: страница бота с цифрами и журналом,
 *  деталь прогона, «О сценарии», «Создать дубликат», причины «не включился/не
 *  выключился/не удалилось». Натив умеет ровно перечисленные ниже куски и ничего не
 *  додумывает; слова, числа и порядок считает ВЕБ.
 *
 *  INVARIANT(scriptera): виды кусков повторяют элементы веб-набора один в один —
 *  'stats' = FBlock с цифрой, 'chart' = FBlock со столбиками (.ov-bars), 'note' = FHint
 *  или DEmpty, 'error' = FFormError/keybanner, 'button' = KButton набора, 'fields' =
 *  пузырь строк (NativeAccountGroup), 'runs' = панель «Запуски» (график + группы по
 *  дням + «Показать ещё»), 'facts'/'steps' = деталь прогона,
 *  'periods' = KSegment набора (капсула периодов), 'events' = строка списка людей
 *  («Контакты»/«Чаты»). Новый вид заводится
 *  ТОЛЬКО вместе с новым элементом веб-набора. Причина: «упростил» и «объединил» —
 *  брак (аудит халявы 01.09). Канон: docs/NATIVE-TABS-PLAN.md. */

/** Карточка одной цифры окна (в вебе — FBlock: имя серым НАД пузырём, значение внутри). */
export interface NativeStatsCard {
  label: string;
  /** ГОТОВОЕ значение (уже с разделителями языка и словом-подписью). */
  value: string;
  /** Пояснение под пузырём (FBlock hint) — пусто, если его нет. */
  hint?: string;
  /** Разбивка причин под значением («не доставлено» у рассылки): готовые пары. */
  rows?: Array<{ label: string; value: string }>;
}

/** Строка журнала запусков — ГОТОВЫЕ слова (узел, время · повод · длительность, причина
 *  падения человеческим языком). Натив ничего не форматирует и не переводит. */
export interface NativeStatsRun {
  id: string;
  /** Главное в строке: узел, на котором прогон закончился (или слово статуса). */
  title: string;
  /** Прогон упал — красная точка сразу после названия (.runs-dot.error). */
  bad: boolean;
  /** Мелкая приписка «отменён»/«идёт» рядом с названием; пусто — её нет. */
  tag: string;
  /** Вторая строка: «14:20 · По событию · 3 с» одной готовой строкой. */
  meta: string;
  /** Причина падения СЛОВАМИ (запрет №7: сырого текста движка тут не бывает). */
  why: string;
}

/** СТРОКА ЖУРНАЛА СОБЫТИЙ страницы бота (kind 'events', П42 владельца 12.09.2026):
 *  человек, а не прогон. Ряд — тот же, что в «Контактах» и «Чатах»: кружок с лицом или
 *  буквой, имя, что случилось, время справа. Слова считает ВЕБ.
 *
 *  НОВЫЙ вид строки, а не правка старой (запрет №8): `NativeStatsRun` остаётся как есть —
 *  его читают уже уехавшие в стор сборки. */
export interface NativeStatsEvent {
  /** Ключ тапа: 'dialog:<id>' — открыть переписку; пусто — строка не нажимается. */
  key: string;
  /** Лицо человека готовым снимком (base64); пусто — рисуем кружок с буквой. */
  photo: string;
  /** Буква кружка и её цвет ролью ('#rrggbb') — считает веб, своей палитры у натива нет. */
  initial: string;
  initialColor: string;
  /** Имя или «@логин». */
  title: string;
  /** Что случилось: «новое обращение» / «получен подарок». */
  text: string;
  /** Время справа (готовая строка). */
  time: string;
  /** КЛЮЧ МЕНЮ ДОЛГОГО НАЖАТИЯ по строке человека (владелец 14.09.2026): натив возвращает
   *  его вместе с рамой пальца, а само меню поднимает веб — родным меню у пальца, тем же,
   *  что у карточки бота. Пусто — меню нет. Ключ НОВЫЙ и необязательный (запрет №8). */
  menuKey?: string;
}

/** Шаг прогона в детали (в вебе — .jrd-step). */
export interface NativeStatsStep {
  name: string;
  /** Точка шага: 'ok' | 'error'. */
  status: string;
  /** Готовая длительность; пусто — её нет. */
  dur: string;
  /** Причина падения шага СЛОВАМИ; пусто — шаг прошёл. */
  why: string;
  /** Готовая строка «есть файл»; пусто — файла не было. */
  file: string;
  /** Кусок данных шага (раскрывается по тапу, как <details> в вебе). */
  sample: string;
  /** Готовая подпись раскрытия («Данные шага»). */
  sampleLabel: string;
}

/** Кусок окна. Виды перечислены в инварианте выше. */
export interface NativeStatsBlock {
  kind:
    | 'stats'
    | 'chart'
    | 'button'
    | 'note'
    | 'error'
    | 'fields'
    | 'runs'
    | 'facts'
    | 'steps'
    /** Капсула периодов над графиком (П42): пилюли того же вида, что папки «Чатов». */
    | 'periods'
    /** Журнал событий людьми (П42) — вместо групп прогонов в 'runs'. */
    | 'events'
    /** ГЕРОЙ КУСКОМ СТРАНИЦЫ (П158 п.1): «Пока без данных» + «Включён»/«Выключен», титул
     *  шапки (имя бота) остаётся. Только сборке с маркером nativeMethod('statsBotPage2'). */
    | 'hero';
  /** 'hero': тот же общий герой, что у страниц «Аккаунта» (П158 п.1). */
  hero?: NativeAccountHero;
  /** 'fields': ЛИСТ ПАПКИ «КАК МЕТКИ» (П158 п.4) — строка `folders.key` открывает родной
   *  список с «Добавить папку» сверху и галочкой у одной папки. Сборка без маркера
   *  `statsBotPage2` ключа не знает и открывает прежний лист выбора (`choice`). */
  folders?: NativeStatsFolders;
  /** Имя куска над содержимым ('chart' | 'fields'). */
  label?: string;
  /** Пояснение под куском ('fields' — подпись пузыря). */
  hint?: string;
  /** 'stats': пузыри цифр. */
  cards?: NativeStatsCard[];
  /** 'chart': крупная строка над графиком («За неделю: 12») и столбики по дням. */
  value?: string;
  bars?: number[];
  /** 'button': ключ тапа, подпись и роль цвета. */
  key?: string;
  tone?: 'plain' | 'accent' | 'danger';
  /** 'button': кнопка занята (создаём копию, отправляем) — тап молчит. */
  busy?: boolean;
  /** 'note' | 'error': готовая строка. */
  text?: string;
  /** 'fields': строки пузыря — ОБЩИЙ протокол строк «Аккаунта» (второй формы строки в
   *  приложении не бывает): правка словом, выбор из листа, тумблер. */
  fields?: NativeAccountField[];
  /** 'runs': линия-«кардиограмма» за 30 дней — готовые точки (пусто — графика нет), и
   *  ряд ошибок под ней. Своего счёта у натива нет: максимум и минимум приходят
   *  ГОТОВЫМИ строками. */
  line?: number[];
  lineErr?: number[];
  /** ВТОРАЯ ЛИНИЯ — подарки по дням тем же порядком (владелец 12.09.2026); ключ НОВЫЙ и
   *  необязательный (запрет №8), старая сборка его не читает. */
  line2?: number[];
  /** СВОЯ ОСЬ ВТОРОЙ ЛИНИИ (П94, 13.09.2026): потолок (100 у конверсии); пусто — общая
   *  шкала с первой линией, как было. Новый необязательный ключ. */
  line2Max?: number;
  /** Цвет ВТОРОЙ линии hex (П113, 14.09.2026: контакты цветом сети рядом с серой линией
   *  «запустили бота»); пусто — роль `--green`, как было. Новый необязательный ключ. */
  line2Ink?: string;
  /** Цвет линии hex — цвет сети бота (Telegram/Instagram), 12.09.2026; пусто — роль
   *  графиков кабинета, как было. Новый необязательный ключ. */
  lineInk?: string;
  /** ТРЕТЬЯ ЛИНИЯ — конверсия по дням (владелец 14.09.2026): своя ось 0…line3Max, цвет
   *  hex (бирюзовая роль `--green`) и слово легенды. Подписи легенды у новой сборки —
   *  выключатели линий. Ключи новые и необязательные (запрет №8). */
  line3?: number[];
  line3Max?: number;
  line3Ink?: string;
  line3Name?: string;
  /** Слова легенды линий для полосы над графиком (П68, 12.09.2026): «Запуски» и
   *  «Подарки». Ключи новые и необязательные — без них полоса пустая, как раньше. */
  lineName?: string;
  line2Name?: string;
  lineMax?: string;
  lineMin?: string;
  /** 'runs': ГОТОВЫЕ подписи всплывающей плашки графика — по одной на точку линии (в
   *  вебе она появляется, когда водишь по графику пальцем). Дату и слова считает веб. */
  tips?: Array<{
    day: string;
    runs: string;
    err: string;
    /** «N подарков» за день (12.09.2026, необязательно). */
    gift?: string;
    /** «25 %» — конверсия дня второй линии (П94, 13.09.2026, необязательно). */
    conv?: string;
    /** «25 %» — конверсия дня ТРЕТЬЕЙ линии (14.09.2026, необязательно). */
    pct?: string;
  }>;
  /** 'runs': группы прогонов по дням («Сегодня», «21 июля»).
   *  'events': ТЕ ЖЕ группы, но строками ЛЮДЕЙ (П42) — вид строки решает вид куска. */
  groups?: Array<{ day: string; items: NativeStatsRun[] }> | Array<{ day: string; items: NativeStatsEvent[] }>;
  /** 'periods': пилюли периода и выбранная сейчас. Тап шлёт statsTap 'period:<key>'. */
  items?: Array<{ key: string; label: string }>;
  current?: string;
  /** 'runs' | 'events': кнопка «Показать ещё» (ключ тапа в `key`: 'more' у прогонов,
   *  'events:more' у людей), её подпись и занятость; пусто — хвост журнала кончился. */
  more?: string;
  /** 'runs' | 'chart' | 'steps': ГОТОВАЯ строка, когда рисовать нечего. */
  empty?: string;
  /** 'facts': крупная строка «когда» и пилюли статуса/повода/длительности. */
  when?: string;
  tags?: Array<{ text: string; tone: string }>;
  /** 'steps': шаги прогона. */
  steps?: NativeStatsStep[];
  /** Кусок ещё едет — натив держит место скелетоном (в вебе там Sk). */
  loading?: boolean;
  /** 'runs' (ТОЛЬКО ВЕБ, APP-CLAIMS-1020 ред. 6, П26.1 = П12): три одиночные серии окна бота — пилюли
   *  «Запуски · N», «Контакты · N», «Конверсия · N %» с мультивыбором; счётные рисуются столбиками, конверсия —
   *  линией 0…100 %. Новые необязательные ключи (запрет №8): сборка в поле их не читает и рисует line/line2/line3. */
  series?: NativeStatsSeries[];
  /** Ключи включённых сейчас серий (выбор окна; при открытии — набор «О боте» `cardPrefs.lines`). Тап по пилюле —
   *  statsTap 'series:<key>'. */
  seriesOn?: string[];
}

/** Серия графика окна бота в вебе (П26.1). */
export interface NativeStatsSeries {
  key: 'runs' | 'contacts' | 'conv';
  /** Слово серии: «Запуски». */
  word: string;
  /** Итог за период: «1», «1,2 тыс.», «25 %». */
  total: string;
  /** Цвет hex — `LINE_INK` (константы плиток пасхалки, П13). */
  ink: string;
  points: number[];
  /** Подпись точки для скраббинга: «1 запуск», «25 %». */
  tips: string[];
  /** 'bar' — счётные серии; 'line' — конверсия 0…100 %. */
  mark: 'bar' | 'line';
}

/** Лист папки бота (П158 п.4). Тапы листа приходят statsTap ключами
 *  `about:cat:pick:<имя>` ("" — без папки), `about:cat:add:<имя>`, `about:cat:del:<имя>`. */
export interface NativeStatsFolders {
  /** Ключ строки пузыря, тап по которой открывает лист. */
  key: string;
  title: string;
  /** Все папки клиента по порядку и выбранная ("" — «Без папки»). */
  items: string[];
  picked: string;
  /** APP-CLAIMS-1020 ред. 3, П11 (добавлено): бот в нескольких папках — все выбранные; есть — лист мультивыбора
   *  (тап — `about:cat:toggle:<имя>`, лист не закрывается, строки «Без папки» нет). Сборки без поля — `picked`. */
  pickedAll?: string[];
  /** Первая строка списка — «Без папки». */
  none: string;
  /** «Добавить папку»: строка, окно с полем, его подсказка и кнопка. */
  add: string;
  addPh: string;
  addBtn: string;
  cancel: string;
  /** Свайп «Удалить» и вопрос подтверждения; пусто — свайпа нет. */
  del?: string;
  delAsk?: string;
  emptyHint?: string;
  note?: string;
}

/** Страница окна. `key` — он же ключ, которым её открыли и которым она вернётся назад. */
export interface NativeStatsPage {
  key: string;
  /** ПОЛУШТОРКА (П120, владелец 14.09.2026: «„Создать дубликат“ тоже должна быть
   *  полушторка»): страница встаёт на половине экрана. Ключ НОВЫЙ и необязательный. */
  half?: boolean;
  /** КРЕСТИК ВМЕСТО СТРЕЛКИ СЛЕВА (П130 п.11, владелец 24.09.2026): у карточки бота и у
   *  «Создать дубликат» — «×», остальные страницы окна — «‹». Ключ аддитивный. */
  close?: boolean;
  /** Титул шапки страницы. */
  title: string;
  /** КРУЖОК «⋯» СПРАВА ШАПКИ (П42 владельца 12.09.2026): то же родное меню, что у
   *  карточки на «Ботах». Тап шлёт statsTap { key: 'menu', rect } — раму кнопки веб
   *  отдаёт обратно в pointMenu якорем меню.
   *
   *  ЗАПАСКИ ДЛЯ СТАРЫХ СБОРОК НЕТ НАРОЧНО (запрет №8 разрешает только добавки): сборка
   *  из стора поля не знает и «⋯» не рисует — там пункты по-прежнему доступны с карточки
   *  в списке «Ботов», а кнопку «Редактировать» в теле страницы владелец велел убрать
   *  (12.09), и возвращать её ради старых сборок значило бы держать два разных вида. */
  menu?: boolean;
  /** Действие в ПРАВОМ углу шапки (в вебе — кнопка ✓ шапки шторки, «Сохранить»):
   *  готовая подпись и ключ тапа; пусто — действия нет. */
  action?: {
    key: string;
    label: string;
    busy?: boolean;
    /** ГАЛОЧКА ВМЕСТО СЛОВА (П99, владелец 13.09.2026): SF-символ кружка справа шапки
     *  («checkmark»); старая сборка ключа не знает и рисует слово. Новый, необязательный. */
    icon?: string;
  };
  /** Герой страницы — тот же вид, что у страниц «Аккаунта». */
  hero?: NativeAccountHero;
  /** Данные ещё едут — натив держит место пустым пузырём, без скачков содержимого. */
  loading?: boolean;
  blocks: NativeStatsBlock[];
  /** КАРЕТКА В ЭТО ПОЛЕ И КЛАВИАТУРА СРАЗУ (П167 п.2, владелец 27.09.2026: «у названия
   *  должен быть автофокус, и у других подобных полей»): ключ единственного текстового поля
   *  страницы («Новая категория», «Создать дубликат»); курсор — в конец. Тот же смысл, что у
   *  `wizItemPatch.focus`. Ключ АДДИТИВНЫЙ (запрет №8): старая сборка не читает — каретка
   *  по тапу, как раньше. */
  focus?: string;
}

/** Всё, что нужно нативу для родных окон карточки. Идемпотентно — патч шлёт словарь
 *  заново. */
export interface NativeStatsData {
  theme: 'light' | 'dark';
  /** КАТАЛОГ страниц: тап открывает следующую МГНОВЕННО, не дожидаясь круга к вебу. */
  pages: NativeStatsPage[];
  /** Что открыто ПО МНЕНИЮ ВЕБА. Натив сверяет со своей стопкой только на ИЗМЕНЕНИЕ. */
  stack: string[];
  /** Фон экрана и два фирменных пятна (.cab::before) — те же роли, что у «Аккаунта». */
  skin: { bg: string; spotPink: string; spotGreen: string };
  /** Цвета графиков — РОЛИ палитры кабинета (те же, что у «Аналитики»), плюс сигнальный
   *  красный линии ошибок (--runsc-err) и тона пилюль статуса прогона. */
  bars: { ink: string; zero: string; alpha: number; err: string };
  tones: { ok: string; err: string; warn: string; mut: string };
}

/** ── РОДНАЯ ФОРМА РАЗОВОЙ РАССЫЛКИ (bcastOpen/bcastPatch) ─────────────────────────
 *  Состав — тот же, что у веб-шторки мастера: «кому», «что» и «когда» тремя пузырями
 *  ГОТОВЫХ СТРОК (NativeAccountGroup — та же форма строки, что у страниц «Аккаунта»:
 *  второго языка форм в приложении не заводим). Проверки и отправку ведёт ВЕБ: натив
 *  только рисует и возвращает намерения.
 *
 *  INVARIANT(scriptera): на этом экране НЕ БЫВАЕТ цен, валют и предложений оплаты
 *  (CLAUDE.md запрет №7). Рассылка ничего платного не показывает — ни строкой, ни
 *  подсказкой; веб таких строк сюда не кладёт. */
export interface NativeBcastData {
  theme: 'light' | 'dark';
  /** Заголовок экрана — ГОТОВОЕ слово кабинета («Новая рассылка»). */
  title: string;
  /**
   * ГЕРОЙ ФОРМЫ — НЕОБЯЗАТЕЛЕН И БОЛЬШЕ НЕ ШЛЁТСЯ (владелец 04.09.2026, И10 п.3
   * docs/MESSAGE-COMPOSER-PLAN.md): «значок, „Новая рассылка“ и три строки описания —
   * убрать целиком; заголовок в шапку, первая группа „Кому“ начинается сразу под шапкой».
   * Поле оставлено в словаре, чтобы не ломать форму аргументов (запрет №8): старая
   * сборка героя не увидит и просто ничего не нарисует.
   */
  hero?: NativeAccountHero;
  groups: NativeAccountGroup[];
  /** Нижняя кнопка: ГОТОВАЯ подпись, занятость (идёт создание) и запрет нажатия. */
  submit: { label: string; busy: boolean; disabled: boolean };
  /** ГОТОВАЯ строка помехи под кнопкой (что человек не заполнил); пусто — помех нет.
   *  Технических ошибок платформы тут не бывает (CLAUDE.md запрет №7 и п.7 правил). */
  error: string;
  /** Фон экрана и два фирменных пятна — те же роли, что у родного «Аккаунта». */
  skin: { bg: string; spotPink: string; spotGreen: string };
}

/** ── РОДНОЙ ЭКРАН «КТО ПОЛУЧИТ» (audOpen/audPatch/audClose) ───────────────────────
 *  Эталон ОДИН В ОДИН — веб-экран `components/cabinet/audience-screen.tsx` (ТЗ И3,
 *  раскладка И9.1 по образцу «Почты» iOS): шапка «‹» слева и «Выбрать» справа,
 *  заголовок-счётчик под ней, серые унаследованные условия, список живых людей теми же
 *  рядами, что «Контакты», подвал словами последними строками списка и НИЖНЯЯ ПРИЛИПШАЯ
 *  ПОЛОСА с воронкой и поиском. Все слова и числа считает веб; натив рисует и возвращает
 *  намерения.
 *
 *  INVARIANT(scriptera): на этом экране НЕ БЫВАЕТ цен, валют и предложений оплаты
 *  (CLAUDE.md запрет №7) — в приложении покупок нет вовсе.
 */

/** ЧЕЛОВЕК В СПИСКЕ — те же пять колонок, что у ряда «Контактов» (`person`): лицо
 *  (фото профиля мессенджера, а нет фото — плитка канала), имя и @логин, связь, метки
 *  чипами, дата последнего визита. Фото готовит ВЕБ (клиент ничего не качает сам). */
export interface NativeAudPerson {
  id: string;
  title: string;
  login?: string;
  reach?: string;
  chips?: string[];
  /** «Без меток» — ГОТОВОЕ слово веба, когда меток у человека нет. */
  hint?: string;
  when?: string;
  /** Фото (data:image/…;base64) — тот же конвейер, что у «Контактов» и «Чатов». */
  photo?: string;
  /** Плитка канала: силуэт PNG и фирменный цвет. Пусто — буква имени. */
  logo?: string;
  logoColor?: string;
  letter?: string;
}

export interface NativeAudData {
  theme: 'light' | 'dark';
  /**
   * ЧТО ЭТО ЗА ЭКРАН (владелец 04.09.2026, И10 п.10):
   *  • 'people' — «Кто получит» разовой рассылки: счётчик, живые люди, поиск, выбор;
   *  • 'conditions' — ТОЛЬКО ЛИСТ УСЛОВИЙ (цепочка: событие запуска и «Кому» каждого
   *    сообщения). Ни списка людей, ни счётчика, ни поиска, ни режима галочек: «кому»
   *    проверяется В МОМЕНТ ОТПРАВКИ, и список подходящих сейчас был бы обещанием,
   *    которого никто не давал. Строки условий, их страницы и «Сбросить» — те же самые.
   * Старая сборка поля не знает и рисует экран людей — для неё цепочка и так уходит в
   * веб-вид (форму аргументов этим не ломаем, запрет №8).
   */
  mode?: 'people' | 'conditions';
  /**
   * ЭКРАН — СТРАНИЦА ШТОРКИ ПИСЬМА (К23.1 п.3, владелец 07.09.2026): условия открыты СО
   * СТРАНИЦЫ ПИСЬМА цепочки, значит натив кладёт их внутрь той же шторки (push, шапка
   * `WizPageHead`, «Сбросить» справа в строке шапки), а не поднимает отдельный лист.
   * Нет ключа — прежнее поведение (строка шага, разовая рассылка).
   *
   * INVARIANT(scriptera): ключ АДДИТИВНЫЙ — старая сборка его не читает и откроет экран,
   * как открывала (контракт моста заморожен, CLAUDE.md запрет №8).
   */
  flow?: boolean;
  /** ОТДЕЛЬНЫМ ЛИСТОМ ПОВЕРХ (П130 п.13, владелец 24.09.2026: «сбоку ничего не
   *  выезжает»): страница встаёт вертикальной шторкой поверх верхнего показанного
   *  экрана (полушторка при `half`, полная иначе), а не едет боком в шторку потока.
   *  `stack` сильнее `flow`. Ключ АДДИТИВНЫЙ — старая сборка его не читает и кладёт
   *  страницу боком, как клала (контракт моста заморожен, CLAUDE.md запрет №8). */
  stack?: boolean;
  /** ГОТОВЫЙ заголовок-счётчик: «Получат 1 234 человека», у письма цепочки «Подходят 95
   *  из 120», в режиме галочек «Выбрано 12 из 1 234», пока считается — «Считаем…».
   *  Заголовок ОДИН: в каком режиме экран, веб уже знает — второго слова не шлём. */
  title: string;
  /**
   * НИЖНЯЯ ПРИЛИПШАЯ ПОЛОСА, как в «Почте» iOS (И9.1, владелец 04.09.2026): слева
   * круглая кнопка-воронка «Фильтр» (при заполненных условиях — акцентом и с числом
   * бейджем), по центру поле поиска. Под заголовком между ним и списком НЕТ НИЧЕГО.
   * `hidden` — режим галочек: полоса уезжает вниз (0,2 с), список занимает её место.
   */
  bar: {
    /** Подпись кнопки-воронки для VoiceOver (сама кнопка — круглый глиф). */
    filterLabel: string;
    /** Число заполненных СВОИХ условий; 0 — бейджа нет и кружок обычный. */
    badge: number;
    searchPlaceholder: string;
    /** Что сейчас набрано в поиске. Отбирает ВЕБ. */
    q: string;
    hidden: boolean;
    /**
     * ПОИСК РАСКРЫТ (владелец 04.09.2026, И11.5, образец «Почта»): капсула разъезжается
     * на всю ширину и встаёт над клавиатурой, кнопка фильтра уезжает, справа появляется
     * кружок с крестиком, а «‹» в шапке скрыт. Режим держит ВЕБ (натив шлёт про него
     * `audSearchOpen {on}`) — второй правды о нём нет. Подпись крестика — `searchClose`.
     */
    active: boolean;
    /** Подпись кружка «✕» поиска для VoiceOver. */
    searchClose: string;
  };
  /** Лист условий: заголовок, четыре строки-ПЕРЕХОДА (все `nav` — И9.3) и «Сбросить».
   *  `page` — открытая СТРАНИЦА условия; ключи: 'passed' (боты галочками),
   *  'passed:<id бота>' (как прошёл / когда проходил / убрать бота), 'received', 'has',
   *  'lacks'. Что открыто, решает ВЕБ — своей памяти у натива нет. Все страницы одного
   *  устройства: группы рядов плюс пояснение подвалом. */
  filter: {
    title: string;
    rows: NativeAccountField[];
    reset: string;
    /** ГАЛОЧКА СПРАВА В ШАПКЕ ВМЕСТО «Сбросить» (П130, условия письма цепочки): слово
     *  для VoiceOver; пусто — галочки нет. Ключ аддитивный. С П132 п.2 веб его не шлёт. */
    check?: string;
    /** КНОПКА «ГОТОВО» ВНИЗУ ЛИСТА УСЛОВИЙ (П132 п.2, модель А, владелец 25.09.2026):
     *  слово кнопки; пусто — кнопки нет. Тап — то же, что «назад»: значения уже в анкете.
     *  Ключ АДДИТИВНЫЙ (запрет №8): старая сборка его не знает — лист закрывается свайпом
     *  и «‹», как закрывался. */
    done?: string;
    page: {
      key: string;
      title: string;
      groups: NativeAccountGroup[];
      /** Поиск по списку страницы: ГОТОВАЯ подсказка поля; пусто — поля нет (строк мало).
       *  Единственное место, где отбор по буквам ведёт САМ натив: список уже приехал
       *  целиком и весь на экране, а отмеченное из него не пропадает. */
      search?: string;
      /** Слова «ничего не нашлось» — их показывает тот, кто отбирает. */
      nothing?: string;
    } | null;
  };
  /** УНАСЛЕДОВАННЫЕ УСЛОВИЯ письма цепочки — серые строки над списком; не нажимаются
   *  (править их надо у того письма, откуда они пришли, И6 п.2). */
  inherited: { title: string; sub?: string }[];
  people: NativeAudPerson[];
  /** Строка «Показать ещё» под списком (она же догрузка по прокрутке): готовая подпись,
   *  занятость и курсор порции. null — грузить больше нечего. */
  more: { label: string; busy: boolean; after: string } | null;
  /**
   * РЕЖИМ ГАЛОЧЕК ПО ОБРАЗЦУ «ПОЧТЫ» (И9.1). Нижней полосы «Выбрать всех / Снять всех»
   * больше нет — её роль забрала ЛЕВАЯ кнопка шапки:
   *  • `topLeft`: 'back' — обычный кружок «‹» (режим выбора выключен), 'all' — слово
   *    «Выбрать всё», 'none' — слово «Снять всё» (отмечены все показанные);
   *  • `topRight`: 'select' — слово «Выбрать», 'close' — кружок «✕» (закончить выбор).
   * Подписи готовые (`leftLabel`/`rightLabel`) — в Swift строк не бывает. `checked` —
   * кто сейчас отмечен (считает веб), `allMode` — правило «все по условиям, кроме
   * снятых». `max` — готовая строка про потолок ручного выбора; пусто — не достигнут.
   */
  select: {
    on: boolean;
    topLeft: 'back' | 'all' | 'none';
    topRight: 'select' | 'close';
    leftLabel: string;
    rightLabel: string;
    checked: string[];
    allMode: boolean;
    max?: string;
    /** «Выбрать» СЕРАЯ и неактивная — так капсула стоит в поиске, пока список результатов
     *  пуст (И11.5): отмечать нечего. Оживает, как только люди по запросу нашлись. */
    rightDisabled: boolean;
  };
  /** Подвал словами: кому мы не пишем и как цепочка набирает людей. */
  footer: string[];
  /** Слова пустого списка и сбоя загрузки (готовые; пусто — их нет). */
  empty: string;
  error: string;
  /** Фон экрана и два фирменных пятна — те же роли, что у родного «Аккаунта». */
  skin: { bg: string; spotPink: string; spotGreen: string };
}

/** ── РОДНОЙ РАЗДЕЛ «РАССЫЛКИ» (bcastListOpen/bcastListPatch) ──────────────────────
 *  Витрина раздела и всё, что открывается с неё, — эталон ОДИН В ОДИН: веб-раздел
 *  (components/cabinet/broadcasts.tsx + общий список scenarios.tsx с kind="broadcasts").
 *  Натив рисует; веб остаётся мозгом: те же данные, тот же отбор, те же слова, те же
 *  руки тапов (карточка, «⋯», «Отправить/Остановить»), что у веб-витрины.
 *
 *  INVARIANT(scriptera): на этих экранах НЕ БЫВАЕТ цен, валют и предложений оплаты
 *  (CLAUDE.md запрет №7) — в приложении покупок нет вовсе.
 */

/** Карточка рассылки — состав веб-карточки (ScnCardAvatar + ScnCardBody с начинкой
 *  рассылки bcCard): аватар (фото аккаунта либо плитка канала), название, описание
 *  («вид начала» словами), подстрока «категория · логин», строка цифр запуска и чип
 *  состояния. Своих полей натив не додумывает. */
export interface NativeBcastCard {
  /** Ключ карточки: 'card:<id>' и 'menu:<id>' собираются из него. */
  id: string;
  title: string;
  /** Описание карточки («вид начала» словами кабинета); пусто — строки нет. */
  desc: string;
  /** ПОДСТРОКА КАРТОЧКИ — ЧАСТЯМИ, как она сложена в вебе (ScnCardBody): имя категории
   *  своим цветом, метка мастера, точка-разделитель, значок сети и логин аккаунта.
   *  Своих правил у натива нет — он только раскладывает присланные части. */
  category: string;
  /** Цвет имени категории '#rrggbb'; пусто — категории нет. */
  categoryColor: string;
  /** Рассылка собрана анкетой — палочка мастера и слово рядом с ней. */
  wand: boolean;
  /** Подпись метки мастера ГОТОВЫМ словом («быстрая настройка»). */
  wandLabel: string;
  /** Логин аккаунта, от чьего имени идёт рассылка; пусто — логина нет. */
  login: string;
  /** Значок сети перед логином: имя картинки каталога сборки; пусто — значка нет
   *  (в вебе он рисуется только там, где логин вообще бывает). */
  loginIcon: string;
  /** Плитка канала: имя картинки каталога сборки (QuickTile-telegram|instagram|catalog). */
  icon: string;
  /** Цвет сети числами — своего разбора hex у плитки нет. */
  red: number;
  green: number;
  blue: number;
  /** ФОТО АККАУНТА (data:image/…;base64) — лицо карточки, как в вебе; готовит его веб
   *  тем же конвейером, что аватар «Аккаунта». Пусто — рисуется плитка канала. */
  photo: string;
  /** ГОТОВАЯ строка цифр запуска («Отправлено 12 · доставлено 11»); пусто — цифр нет
   *  (ни разу не отправляли — четырёх нулей вместо них в вебе тоже не бывает). */
  nums: string;
  /** Состояние СЛОВАМИ кабинета («Черновик», «Отправляется», «Готово»). */
  state: string;
  /** Тон чипа состояния: 'run' | 'off' | 'wait'. */
  stateTone: string;
  /** ГОТОВАЯ строка чипа сбоев за сутки; пусто — чипа нет. Чип НАЖИМАЕТСЯ (как в вебе):
   *  тап уходит ключом 'errors:<id>' и открывает журнал на упавших прогонах. */
  errors: string;
  /** Карточка только что создана (копия) — короткая вспышка, как .scn-fresh в вебе. */
  fresh?: boolean;
}

/** Карточка АРХИВА старого журнала — только чтение (ArchiveCard в broadcasts.tsx):
 *  кружок значка своим цветом, заголовок, «Отправлено N · 5 августа» и чип «Архив». */
export interface NativeBcastArchiveCard {
  id: string;
  title: string;
  /** «Отправлено N · 5 августа» — ГОТОВАЯ строка. */
  desc: string;
  /** Подпись чипа («Архив»). */
  chip: string;
  /** Письмо (конверт вместо значка телеграма) — у почтовых записей журнала. */
  mail: boolean;
  /** Цвет кружка числами (circleColor по названию — тот же, что в вебе). */
  red: number;
  green: number;
  blue: number;
}

/** Кусок страницы, открытой с витрины. Вид разбирается по `kind`; своих слов, чисел и
 *  правил у натива нет — всё приезжает готовым. */
export interface NativeBcastBlock {
  kind:
    /** Красная строка ошибки формы (FFormError/DError). */
    | 'error'
    /** Серый абзац (FHint). */
    | 'hint'
    /** Пузырь «имя серым над ним, значение внутри» (FBlock): цифры страницы. */
    | 'stat'
    /** Пузырь с графиком-столбиками (те же точки, что рисует веб). */
    | 'chart'
    /** Пузырь ПОЛЕЙ ПРАВКИ (FBlock + FIn): у каждого поля свой заголовок, значение
     *  правится ПРЯМО В ПОЛЕ, каждое изменение уходит вебу событием bcastListSet. */
    | 'fields'
    /** Раскрытый список с галочкой отдельным пузырём (FChoiceBlock). */
    | 'choice'
    /** Кнопка (KButton/KActionBar): ключ уходит событием bcastListTap. */
    | 'button'
    /** Ряды-переходы пузырём (журнал запусков): день над пузырём, строки внутри. */
    | 'rows'
    /** Линия-«кардиограмма» журнала запусков (спутник рядов). */
    | 'line'
    /** Плашка-предупреждение со своим действием (DWarn веба: янтарная подложка, знак
     *  «!», текст и кнопка) — «Подключите телеграм-бота». */
    | 'warn'
    /** Данные ещё едут — пустой пузырь вместо строк (без скачков контента). */
    | 'skeleton'
    /** Строка фактов чипами (шапка одного запуска: состояние, как запустили, сколько шло). */
    | 'facts'
    /** Шаги одного запуска: точка «прошёл/упал», имя, длительность, причина, данные. */
    | 'steps';
  key?: string;
  /** Имя пузыря серым НАД ним / подпись поля / подпись кнопки. */
  label?: string;
  /** Значение внутри пузыря ГОТОВОЙ строкой. */
  value?: string;
  /** Пояснение под полем или под пузырём (видно ВСЕГДА — правило кабинета). */
  hint?: string;
  /** 'error'/'hint'/'note': сам текст. */
  text?: string;
  /** 'stat': расшифровка под значением («не доставлено» по причинам). */
  rows?: Array<{ label: string; value: string }>;
  /** 'chart'/'line': точки В ПОРЯДКЕ РИСОВАНИЯ (натив ничего не переставляет). */
  bars?: number[];
  /** 'line': вторая линия (упавшие прогоны) и подписи максимума/минимума. */
  errBars?: number[];
  max?: string;
  min?: string;
  /** 'chart'/'rows': ГОТОВАЯ строка «показывать нечего». */
  empty?: string;
  /** 'fields': сами поля пузыря (заголовок, значение, подсказка, ошибка). */
  items?: Array<{
    key: string;
    label: string;
    value: string;
    hint?: string;
    error?: string;
    placeholder?: string;
    /** Многострочное поле (описание) — иначе однострочное. */
    multiline?: boolean;
    maxLength?: number;
  }>;
  /** 'choice': выбранное значение и ряды списка. */
  picked?: string;
  options?: Array<{ value: string; label: string; hint?: string }>;
  /** 'button': роль цвета и запрет нажатия. */
  tone?: 'plain' | 'accent' | 'danger';
  disabled?: boolean;
  /** 'facts': чипы строки фактов (tone — роль цвета чипа: run/off/wait/err). */
  chips?: Array<{ text: string; tone?: string }>;
  /** 'steps': шаги одного запуска. `open` — данные шага раскрыты (решает веб). */
  steps?: Array<{
    key: string;
    name: string;
    bad?: boolean;
    dur?: string;
    why?: string;
    file?: string;
    sample?: string;
    sampleLabel?: string;
    open?: boolean;
  }>;
  /** 'rows': пузыри-дни со строками журнала. */
  groups?: Array<{
    day: string;
    rows: Array<{
      /** Ключ строки: уходит событием bcastListTap как есть. */
      key: string;
      /** Главное в строке — узел, на котором прогон закончился. */
      title: string;
      /** «10:15 · вручную · 3 с» — ГОТОВАЯ строка. */
      sub: string;
      /** Прогон упал — красная точка после заголовка. */
      bad?: boolean;
      /** «Отменён»/«Идёт» мелкой припиской; пусто — приписки нет. */
      note?: string;
      /** Причина падения ЧЕЛОВЕЧЕСКИМ ЯЗЫКОМ (техтекст движка клиенту не показываем —
       *  CLAUDE.md запрет №7); пусто — строки нет. */
      why?: string;
    }>;
  }>;
}

/** Страница, открытая с витрины (цифры рассылки, «О сценарии», «Дубликат», «Подключите
 *  аккаунт»): своя шапка с «назад», необязательная кнопка действия справа и куски. */
export interface NativeBcastPage {
  key: string;
  title: string;
  /** Кнопка-действие в шапке страницы (✓ «Сохранить» у «О сценарии»); пусто — нет. */
  action?: { key: string; icon: string; label: string };
  /** Данные ещё едут — натив рисует пустой пузырь, а не «пусто» (без скачков). */
  loading?: boolean;
  blocks: NativeBcastBlock[];
  /** КАРЕТКА В ЭТО ПОЛЕ СРАЗУ (П167 п.2): ключ единственного текстового поля страницы
   *  («Дубликат» — имя). Ключ АДДИТИВНЫЙ (запрет №8). */
  focus?: string;
}

/** Всё, что нужно нативу для раздела «Рассылки». Идемпотентно — патч шлёт словарь заново. */
export interface NativeBcastListData {
  theme: 'light' | 'dark';
  /** Заголовок раздела и подпись кнопки «+» шапки — ГОТОВЫЕ слова кабинета. */
  title: string;
  plus: string;
  rows: NativeBcastCard[];
  /** Архив старого журнала: подпись над списком и сами карточки (пусто — архива нет). */
  archive: { label: string; rows: NativeBcastArchiveCard[] };
  /** ГЕРОЙ ПУСТОГО РАЗДЕЛА (владелец 20.08, Д2; вид переучен 04.09.2026 — И10 п.4).
   *  Стоит ТОЛЬКО у пустого раздела без отбора и без архива — тогда «+» шапки не
   *  рисуется вовсе (владелец 21.08, претензия 4).
   *  Канон пустого состояния Apple: `icon` — SF-символ СЕРЫМ (secondaryLabel), около 56,
   *  БЕЗ плитки и без контура; `title` — про СОСТОЯНИЕ («Рассылок пока нет»), а не дубль
   *  шапки; `meta` — не больше двух строк; `flat` — кнопка ОБЫЧНАЯ капсулой ролью
   *  --accent, без градиента (`skin.ctaFrom/ctaTo` при этом остаются в словаре: старая
   *  сборка их знает и рисует как рисовала — форму аргументов не ломаем, запрет №8). */
  hero?: { title: string; meta: string; cta: string; icon?: string; flat?: boolean };
  /** Пустой итог СЛОВАМИ (чем именно он пуст — считает веб); пусто — строки нет. */
  emptyLine: string;
  /** Полосы над списком ГОТОВЫМИ строками: беда запуска рассылки, протухший ключ. */
  banners: string[];
  /** Данные собраны не до конца — первый кадр придержать (правило волны). */
  ready: boolean;
  /** Фон экрана и два фирменных пятна (.cab::before) — те же роли, что у «Аккаунта»,
   *  плюс пара градиента кнопки героя (.cta-grad: от --accent до --violet). */
  skin: { bg: string; spotPink: string; spotGreen: string; ctaFrom: string; ctaTo: string };
  /** Цвета чипов и плашек ролями палитры кабинета (их разрешает веб). */
  tones: { run: string; off: string; wait: string; err: string; amber: string };
  /** Столбик графика и пустой день — ТЕ ЖЕ роли, что у графиков веба (.ov-bars):
   *  своих оттенков и своей прозрачности натив не заводит. */
  bars: { ink: string; zero: string; alpha: number };
  /** Каталог страниц, которые открываются с витрины (едет заранее — тап открывает
   *  страницу мгновенно, не дожидаясь круга к вебу). */
  pages: NativeBcastPage[];
  /** Какие страницы открыты ПО МНЕНИЮ ВЕБА. Натив сверяет со своей стопкой только на
   *  ИЗМЕНЕНИЕ: иначе патч со «старым» желанием закрыл бы страницу, открытую пальцем. */
  stack: string[];
}

/** Запись ленты «Полученные»: ГОТОВЫЕ строки кабинета. Натив ничего не форматирует и
 *  не переводит — время и день приезжают словами.
 *  ПЛИТКИ-ЗНАЧКА У ЗАПИСИ НЕТ (аудит халявы 01.09, docs/NATIVE-TABS-PLAN.md): в вебе
 *  строка ленты — точка непрочитанного, заголовок, время справа и зачин; значок по виду
 *  уведомления натив ПРИДУМАЛ сам. Поля icon/color сняты вместе с ним — заводить их
 *  заново запрещено. */
export interface NativeInboxRow {
  id: string;
  title: string;
  /** Текст записи целиком: в ленте натив показывает две строки, на самой записи — весь. */
  body: string;
  /** Время записи словами кабинета («08:50»). */
  time: string;
  /** Заголовок записи-страницы («Сегодня · 08:50») — ГОТОВАЯ строка. */
  when: string;
  unread: boolean;
}

/** Пузырь ленты — один на день: подпись дня и записи внутри. */
export interface NativeInboxGroup {
  /** Подпись дня («Сегодня», «14 августа») — ГОТОВАЯ строка. */
  day: string;
  rows: NativeInboxRow[];
}

/** Всё, что нужно нативу для ленты «Полученные» (inboxOpen/inboxPatch). */
export interface NativeInboxData {
  theme: 'light' | 'dark';
  /** Заголовок экрана — ГОТОВОЕ слово кабинета («Полученные»). */
  title: string;
  groups: NativeInboxGroup[];
  /** Записей нет — ГОТОВАЯ строка, почему пусто. */
  emptyText: string;
  /** Подпись «Прочитать всё»; пусто — непрочитанного нет, слова не рисуем. Стоит она в
   *  имени ПЕРВОГО пузыря справа — ровно как в вебе (аудит халявы 01.09), а не в шапке. */
  readAll: string;
  /** Лента ещё едет (первая страница) — натив рисует пустой пузырь, а не «пусто». */
  loading: boolean;
  /** Есть что догружать: доехал низ — натив просит следующую страницу (inboxMore). */
  more: boolean;
  /** Какую запись показать страницей поверх ленты (пуш привёл в саму новость); пусто —
   *  открыта сама лента. Натив сверяет со своей стопкой только на ИЗМЕНЕНИЕ — иначе
   *  патч со «старым» желанием закрыл бы запись, открытую пальцем секунду назад. */
  open: string;
  skin: { bg: string; spotPink: string; spotGreen: string };
}

/** ── РОДНОЕ ОКНО «СООБЩЕНИЕ» (msgOpen/msgPatch) ────────────────────────────────────
 *  Окно — КАРТОЧКА-ПРОТОТИП письма (ТЗ И9.6, владелец 04.09.2026; прежний экран чата с
 *  панелью «+ / поле / Aa» отменён): один исходящий пузырь со СЛОТАМИ — медиа, текст,
 *  «приложить ещё», кнопки под пузырём — и строка «Уйдёт одним сообщением». Тап по слоту
 *  открывает его настройку: лист «Вложение», страницу кнопки; текст правится НА МЕСТЕ.
 *
 *  INVARIANT(scriptera): натив НЕ решает, что с чем уходит одним сообщением, и НЕ знает
 *  пределов канала. Он рисует присланные слоты и возвращает намерения. Причина: правила
 *  Телеграма живут в одном словаре (channels.ts) и одной модели (@scriptera/db/tg-message),
 *  а сборка в сторе висит месяцами.
 *  Канон: docs/MESSAGE-COMPOSER-PLAN.md И9.6–И9.8, Д1. */

/** Файл в слоте медиа: наша ссылка на него и готовые слова плашки. */
export interface NativeMsgMedia {
  /** Ключ файла в письме — он же уезжает обратно в `msgAttachDrop`, когда человек убирает
   *  ОДИН кадр альбома («Убрать это фото»). Своих ключей натив не придумывает. */
  id: string;
  /** id исходного файла, из которого сделан этот (перевод голосового, кружок из ролика; П135):
   *  натив связывает личности, кадр и кольцо не сбрасываются. Ключ АДДИТИВНЫЙ. */
  derivedFrom?: string;
  /** Стороны кадра (П136 п.3): мозаика альбома по пропорциям; нет — квадрат. Ключи АДДИТИВНЫЕ. */
  width?: number;
  height?: number;
  /** Ссылка НАШЕГО домена (клиент ничего не качает сам — адрес даёт веб). */
  url: string;
  /** Кадр (натив грузит картинку) или плашка со значком. */
  image: boolean;
  /** SF-значок плашки (видео, звук, файл); пусто — плашки нет. */
  icon: string;
  /** Имя файла и «1:07 · 3,4 МБ» — ГОТОВЫМИ строками. */
  name: string;
  meta: string;
  /** РОЛИК (П129.5): кадром с play и длительностью, а не плашкой. Ключи АДДИТИВНЫЕ —
   *  сборки до 24.09 их не читают и рисуют плашку, как раньше. */
  video?: boolean;
  /** Полный адрес кадра-постера ролика; пусто — натив снимает кадр из самого ролика. */
  poster?: string;
  /** «1:07» этого файла (у альбома — у каждого кадра своя). */
  duration?: string;
  /** КОЛЬЦО ХОДА (П129.2): доля заливки 0…1; нет ключа — файл на месте. */
  ring?: number;
  /** Заливка сорвалась: на кадре «Не загрузилось · Повторить» (`msgTap retry:<id>`). */
  failed?: boolean;
}

/** Кнопка ПОД пузырём — настоящая inline-кнопка Телеграма. */
export interface NativeMsgButton {
  /** Ключ тапа (возвращается msgTap). */
  key: string;
  /** Подпись кнопки; своих слов натив не придумывает. */
  label: string;
  /** ПРИКОЛОТАЯ КНОПКА (ТЗ Г7): подпись правится, а убрать её и переставить нельзя —
   *  это ветвление самого бота. Ключа нет — кнопка обычная (старая сборка его не читает
   *  и ведёт себя как прежде, запрет №8). */
  pinned?: boolean;
  /** ПУСТАЯ КНОПКА (ТЗ И11.7): подписи у неё ещё нет, `label` — готовое слово «Без
   *  подписи». Рисуется СЕРОЙ капсулой: настоящая кнопка говорит голубым Телеграма, а
   *  эта ещё не настоящая. Своей проверки «пусто ли» у натива нет — слово переводится,
   *  и сравнивать его со своей строкой Swift не может (CLAUDE.md запрет №8). */
  empty?: boolean;
  /** КНОПКА-ССЫЛКА (П93): в углу капсулы стрелка «↗», как в Telegram. Ключ НОВЫЙ и
   *  необязательный. */
  link?: boolean;
  /** НОМЕР РЯДА С НУЛЯ (ТЗ М2.5, владелец 16.09.2026): кнопки одного ряда рисуются в
   *  ОДНУ строку равной ширины, как клавиатура Телеграма. Приколотые кнопки шага (З7)
   *  стоят своим рядом первыми. Ключ НОВЫЙ и необязательный: старая сборка его не
   *  читает и рисует кнопки столбиком, как рисовала (CLAUDE.md запрет №8). */
  row?: number;
  /** ЦВЕТ КНОПКИ (ТЗ М2.6, Bot API 9.4): 'primary' — синяя, 'success' — зелёная,
   *  'danger' — красная. Нет ключа — обычная кнопка (голубой Телеграма, как раньше).
   *  Зелёный и красный приезжают ролями палитры в `skin` (green/danger). */
  style?: 'primary' | 'success' | 'danger';
}

/** КАРТОЧКА ПРЕВЬЮ ССЫЛКИ в слоте медиа (ТЗ И11.7): так её развернёт Телеграм у
 *  человека. Данные приносит наша ручка `/api/cabinet/link-preview` — клиент чужой сайт
 *  не качает. Пусто в `title`/`image` — карточка с одним доменом (сайт про себя ничего
 *  не сказал). */
export interface NativeMsgPreview {
  host: string;
  title: string;
  description: string;
  /** Полный адрес картинки сайта; пусто — карточка без картинки. */
  image: string;
}

/** Капсула-предложение прямо в пузыре («Сжать», «Обрезать», «Выбрать минуту»). */
export interface NativeMsgAction {
  key: string;
  label: string;
}

/** СЛОТ МЕДИА — верх пузыря (И9.6 п.1). Пусто — пунктирная область со словами. */
export interface NativeMsgSlotMedia {
  /** Чем рисовать: '' (пусто) | 'photo' | 'album' | 'animation' | 'voice' | 'round' |
   *  'audio' | 'audioAlbum' | 'document' | 'documentAlbum'. Вид посчитан вебом. */
  kind: string;
  /** Слова пунктирной области пустого слота. */
  empty: string;
  /** Файлы слота: у альбома все, у прочих один, у пустого слота ни одного. */
  items: NativeMsgMedia[];
  /** Голосовое: волна 0…100 (её считает раннер) и ГОТОВАЯ строка длительности. */
  peaks: number[];
  duration: string;
  /** Кадр закрыт до тапа (спойлер). */
  spoiler: boolean;
  /** Файл ещё готовится (раннер) — ГОТОВАЯ строка; слот стоит с кольцом хода. */
  busy: string;
  /** Предел СЛОВАМИ под медиа («Видео 73 МБ, Телеграм принимает до 50»); пусто — всё
   *  в порядке. Технических поломок тут не бывает (CLAUDE.md запрет №7). */
  limit: string;
  /** Капсулы под строкой предела — что человек может с этим сделать. */
  actions: NativeMsgAction[];
  /** Слова кадра с сорвавшейся заливкой (П129.2): «Не загрузилось» и «Повторить». */
  failedWord?: string;
  retryWord?: string;
  /** КАРТОЧКА ПРЕВЬЮ ССЫЛКИ вместо пустого слота (И11.7): бывает, только когда медиа
   *  нет вовсе, в тексте есть ссылка и человек не убрал карточку крестиком. Тап по ней
   *  открывает тот же лист «Вложение». Пусто — карточки нет. */
  preview?: NativeMsgPreview;
  /** ОТРЕЗОК РОЛИКА для капсул «Обрезать» и «Выбрать минуту»: что показать в шторке с
   *  ползунком. Нет поля — резать нечего. */
  trim?: {
    /** Длина ролика, секунды (её знает веб — снял при загрузке). */
    total: number;
    /** Потолок отрезка, секунды: у кружка — минута, у «Обрезать» — 0 (свободно). */
    max: number;
    title: string;
    hint: string;
    done: string;
  };
}

/** КАРТОЧКА-ПРОТОТИП письма: один пузырь со слотами (И9.6). */
export interface NativeMsgCard {
  media: NativeMsgSlotMedia;
  /** Слот текста. `show: false` — у кружка подписи не бывает, вместо слота серая строка
   *  `note`. `html` — Telegram-HTML (натив собирает из него NSAttributedString). */
  text: { show: boolean; html: string; placeholder: string; note: string; marks?: NativeMsgMark[]; text?: string };
  /** ПУНКТЫ ДОЛГОГО НАЖАТИЯ ПО ТЕКСТУ ПУЗЫРЯ (ТЗ П151 §8): «Редактировать» открывает строку
   *  ввода над клавиатурой, «Копировать текст» — в буфер. Пусто — текста у пузыря не бывает
   *  (кружок). Ключ новый — старая сборка его не читает. */
  textMenu?: NativeMsgTextMenuItem[];
  /** ТОЛЬКО КРУГ (П129.4, владелец 24.09.2026: «не пузырь — ни текста, ни кнопок»): кружок
   *  рисуется без пузыря, угла со временем и строк; ключ новый, старая сборка рисует как
   *  раньше. */
  bare?: boolean;
  /** Строка «приложить ещё» внутри пузыря; `kind` пуст — строки нет (докладывать нечего).
   *  Тап открывает лист «Вложение» сразу на этом виде. */
  more: { kind: string; label: string };
  /** Кнопки под пузырём и пунктирная «Добавить кнопку»; у альбома вместо них строка. */
  buttons: NativeMsgButton[];
  buttonsNote: string;
  addButton: string;
  /** ВРЕМЯ В УГЛУ ПУЗЫРЯ, ГОТОВОЙ СТРОКОЙ (И11.7): у рассылки с назначенным временем —
   *  её час, иначе текущее время (веб обновляет раз в минуту). */
  clock: string;
  /** Старое письмо из нескольких сообщений — красная строка; пусто — письмо одно. */
  legacy: string;
  /** ДАТА-КАПСУЛА НАД ПУЗЫРЁМ («Сегодня», П93): готовое слово; пусто — капсулы нет.
   *  Ключ НОВЫЙ и необязательный. */
  dateChip?: string;
  /** ПОДПИСЬ НАД МЕДИА (П169 п.2, владелец 27.09.2026: «подпись над медиа вообще не
   *  работает»): true — текст пузыря рисуется НАД кадром, кадр снизу, время в углу кадра —
   *  как Telegram показывает `show_caption_above_media` (рецепт `capAbove` «Чатов»). Бывает
   *  только у картинки/ролика/альбома — и с пустым текстом тоже (П181: заглушка «Написать
   *  подпись» стоит над кадром). Ключ АДДИТИВНЫЙ (запрет №8):
   *  старая сборка его не читает и рисует подпись снизу, как рисовала. */
  captionAbove?: boolean;
}

/** ВКЛАДКА ВИДА ШТОРКИ СКРЕПКИ у строки «Сейчас в сообщении» (П182, владелец 27.09.2026):
 *  натив добавляет её на своё место в порядке видов, если среди «Добавить»/«Заменить» её нет. */
export interface NativeMsgAttachTab {
  /** Вид пункта листа ('photoVideo' | 'animation' | 'voice' | 'round' | 'audio' | 'document'). */
  key: string;
  /** Слово вкладки — то же, что у строки этого вида в «Добавить» (`tab`, нет — `label`). */
  label: string;
  /** SF-значок вкладки — тот же набор, что у строк листа. */
  icon: string;
}

/** Строка листа «Вложение»: вид, что с ним будет и чем его выбирают. */
export interface NativeMsgAttachRow {
  /** Ключ вида ('photoVideo' | 'animation' | 'voice' | 'round' | 'audio' | 'document') —
   *  он же приезжает обратно в msgAttachPick и msgFile. */
  key: string;
  /** id исходного файла у строки «Сейчас в сообщении» (П135): натив связывает личности через
   *  `MsgIdAlias`, строка не пересоздаётся после перевода. Ключ АДДИТИВНЫЙ. */
  derivedFrom?: string;
  /** ЛИЧНОСТЬ СНИМКА В ГАЛЕРЕЕ (П173 п.2, владелец 27.09.2026: «должны стоять галочки на
   *  том, что уже есть»): у строки «Сейчас в сообщении» — `PHAsset.localIdentifier`, с
   *  которым файл пришёл в `msgFile`; по нему сетка «Фото/видео» ставит галочку, номер — по
   *  порядку строк группы. Нет ключа — файл не из галереи (окно документов, камера,
   *  старая запись). Снятая галочка — прежнее `msgAttachDrop { key }`. Ключ АДДИТИВНЫЙ. */
  assetId?: string;
  label: string;
  /** КОРОТКАЯ ПОДПИСЬ ЗНАЧКА ВКЛАДКИ шторки скрепки (ТЗ П151 §11ж п.4, «Фото/видео»); нет —
   *  подпись = `label`. Ключ АДДИТИВНЫЙ: `label` остаётся заголовком листа.
   *  У СТРОК ГРУППЫ `current` (П182) — ОБЪЕКТ `NativeMsgAttachTab`: вкладка вида стоящего
   *  файла, которой нет среди групп «Добавить»/«Заменить». Строкой у `current` он не бывал
   *  никогда, а старая сборка читает `tab` только строкой (`as? String`) — объект пропускает. */
  tab?: string | NativeMsgAttachTab;
  /** Подсказка строки («до 10», «заменит фото и уберёт текст»); пусто — подсказки нет. */
  hint: string;
  /** SF-значок строки. */
  icon: string;
  /** Строка серая и не нажимается. */
  disabled: boolean;
  /** ГОТОВАЯ причина второй строкой у выключенной; пусто — причины нет. */
  reason: string;
  /** Показывать ли для этого вида библиотеку снимков (иначе окно документов). */
  photos: boolean;
  /** Библиотека отдаёт и видео (не только снимки). */
  videos: boolean;
  /** MIME-типы окна документов (пусто — любой файл). */
  accept: string[];
  /** Сколько файлов можно взять за раз (1 — мультивыбора нет). */
  multiple: number;
  /** СТРОКА ГРУППЫ «СЕЙЧАС В СООБЩЕНИИ» (И11.7): справа кружок-крестик, тап по нему
   *  шлёт `msgAttachDrop { key }` (ключ файла или 'preview'). Выбора файла у такой
   *  строки нет вовсе. */
  drop?: boolean;
  /** Готовое слово крестика для читалки (в вебе aria-label). */
  remove?: string;
  /** Миниатюра кадра слева (ссылка нашего домена); пусто — рисуется значок `icon`. */
  thumb?: string;
  /** Ролик без постера (П129.5): натив снимает кадр для строки из самого файла. */
  videoUrl?: string;
  /** ОКНО «КРУЖОК» (П129.4): у строки вида «кружок» — слова окна подготовки (заголовок,
   *  серая строка о переделке, «Добавить», «Отмена») и потолок отрезка в секундах; натив с
   *  этим ключом открывает окно кропа и отрезка ДО заливки, без него — прежняя дорога. */
  clip?: {
    /** 'round' — окно круга; 'gif' — обрезка беззвучной гифки перед заливкой (с выбором
     *  «Галерея / Файлы»); 'trim' — обрезка уже залитого ролика по тапу строки (раннер `trim`);
     *  'audio' (П172, НОВОЕ значение) — у строки «Аудио»: после выбора файла — редактор отрезка
     *  (`trim`), затем трек встаёт в сообщение. Сборка, не знающая 'audio', строку открывает
     *  окном документов, как раньше (значение ей незнакомо — ветки редактора не срабатывают). */
    mode: 'round' | 'gif' | 'trim' | 'voice' | 'audio';
    title: string;
    hint: string;
    done: string;
    cancel: string;
    max: number;
    /** КОРОТКОЕ СЛОВО СЕКУНД для подписи отрезка под лентой кадров («0:05 — 0:35 · 30 с»;
     *  ru «с», en «s») — своих строк у Swift нет. Ключ АДДИТИВНЫЙ (П132 п.16, просьба
     *  родного редактора): старая сборка его не читает. */
    sec?: string;
    /** 'gif': капсулы пропорций кадра над лентой редактора (П133 п.7): ключ 'free' или
     *  «W:H», слово — готовое («Свободно» / «1:1»). Ключ АДДИТИВНЫЙ: старая сборка его не
     *  читает, кадр остаётся как был. */
    ratios?: Array<{ key: string; label: string }>;
    /** 'trim': полный адрес ролика для предпросмотра. */
    url?: string;
    /** 'gif': слова низкого листа выбора источника. У 'round' (П175 п.1, владелец 27.09.2026:
     *  «кружок из файлов тоже нужно, как гифку»): `gallery` — «Выбрать видео» (сетка роликов
     *  галереи), `files` — «Файлы» (окно документов, только видео), `record` — «Записать»
     *  (камера, как прежде); выбранный ролик идёт в тот же редактор круга. Ключи аддитивные:
     *  старая сборка у кружка их не читает и открывает только запись. */
    gallery?: string;
    files?: string;
    /** 'voice' (П129.7): «Записать» на листе источника и «Записать заново» в окне записи. */
    record?: string;
    again?: string;
    /** РЕДАКТОР ОТРЕЗКА ЗВУКА (П172, владелец 27.09.2026): у строки «Голосовое» — после выбора
     *  ФАЙЛА (записанное на месте — без редактора), у строки «Аудио» — после выбора файла
     *  всегда, до постановки в сообщение: тот же лист, что у гифки/кружка (ручки начала и
     *  конца, отрезок за середину), слова готовые — заголовок, серая строка, «Готово», предел
     *  секунд (0 — без предела). Обрезка — на телефоне, уходит уже отрезанный файл.
     *  Слова `title`/`hint`/`done` самого `clip` у голосового НЕ меняются — их читает окно
     *  записи сборки из стора. Ключ АДДИТИВНЫЙ (запрет №8). */
    trim?: { title: string; hint: string; done: string; max: number };
    /** ПРАВКА УЖЕ СТОЯЩЕГО КРУЖКА ИЛИ ГИФКИ (П132 п.22, пункт меню «Редактировать»): у строки
     *  группы «Сейчас в сообщении» `url` — полный адрес залитого файла (как у 'trim'), а
     *  `from`/`to` (секунды) и `crop` (доли кадра 0…1) — текущие отрезок и кадр, с ними
     *  редактор и открывается. Результат натив переэкспортирует и шлёт `msgFile` с
     *  `replaceKey` = ключ строки: веб ставит новый файл НА МЕСТО стоящего. Ключи
     *  АДДИТИВНЫЕ: старая сборка их не читает. */
    from?: number;
    to?: number;
    crop?: { x: number; y: number; w: number; h: number };
  };
  /** СТРОКУ «СЕЙЧАС В СООБЩЕНИИ» МОЖНО НАЖАТЬ — ЭТО «ИЗМЕНИТЬ» (владелец 21.09.2026:
   *  «редактирование или удаление фотки не кликается»): вид, которым откроется выбор
   *  файла, чтобы ЗАМЕНИТЬ стоящее (`msgAttachPick { kind, replace: true }`). Пусто или
   *  нет ключа — строка только рассказывает, что лежит в письме, и нажимается лишь
   *  крестик (так у альбома: там кадр убирают, а не меняют пачку целиком). */
  editKind?: string;
  /** ОБОЛОЧКА ГРУЗИТ ФАЙЛ САМА (ТЗ 20.09.2026 V.4 п.5): адрес нашей ручки и имя поля
   *  формы. Ключ АДДИТИВНЫЙ — веб шлёт его только сборке, объявившей метод `msgFileGo`;
   *  без него дорога прежняя (файл уезжает вебу строкой base64 в пределах моста). Вид
   *  файла («фото или видео») веб допишет к адресу, когда узнает настоящий тип. */
  upload?: { url: string; field: string };
  /** СТРОКА «НАЗВАНИЕ» У ТРЕКА И ФАЙЛА (П130 п.32в/г, владелец 24.09.2026): тап открывает
   *  лист с одним полем (тот же лист, что у строк кнопки), правка уезжает `msgSet`
   *  ключом `att.name:<id>`. Ключ аддитивный: старая сборка строку рисует без тапа.
   *  С П132 п.22 (владелец 25.09.2026) отдельной строки «Название» больше нет: этот же лист
   *  лежит на САМОЙ строке трека/файла группы «Сейчас в сообщении» и открывается пунктом
   *  долгого нажатия «Переименовать» (`menu`). Сборка, не знающая `menu`, открывает его
   *  тапом по строке — как открывала строку «Название». */
  rename?: NativeMsgButtonSheet;
  /** ЧТО ПОКАЗАТЬ ПО ТАПУ (П132 п.22, владелец 25.09.2026: «обычный тап — просмотр»): у
   *  строки группы «Сейчас в сообщении» — вид файла, его полный адрес `url` (файл залит) и/или
   *  `local` (метка/путь копии на телефоне из `msgFile`, пока не залит), тип и имя. Натив
   *  открывает просмотр сам (QuickLook, у кружка и гифки — плеер по кругу); вебу событие не
   *  нужно. Ключ АДДИТИВНЫЙ: старая сборка его не читает. */
  view?: {
    kind: TgAttachKind;
    url?: string;
    local?: string;
    mime: string;
    /** Стороны кадра (П135 п.2): высота шторки просмотра по пропорции; нет — натив читает файл. */
    width?: number;
    height?: number;
    name: string;
    /** СЛОВО ВИДА ДЛЯ ШАПКИ ПРОСМОТРА (П151 §11р п.1–2, владелец 27.09.2026: «только у
     *  файлов и аудио пишешь название файла, у всех остальных — заголовок [вид]»): «Фото»,
     *  «Видео», «Гифка», «Кружок», «Голосовое сообщение» — готовое слово из словаря видов.
     *  У трека и файла ключа НЕТ — натив показывает `name`. Ключ АДДИТИВНЫЙ (запрет №8):
     *  старая сборка его не читает, шапка у неё как была. */
    title?: string;
  };
  /** ПУНКТЫ ДОЛГОГО НАЖАТИЯ (П132 п.22): ровно эти, в этом порядке и с этими ГОТОВЫМИ словами —
   *  натив рисует родное контекстное меню и своих пунктов не добавляет. 'view' — просмотр
   *  (как тап, `view`), 'rename' — лист `rename`, 'edit' — редактор кадра и отрезка по `clip`.
   *  Нет ключа — меню у строки нет. Ключ АДДИТИВНЫЙ. */
  menu?: Array<{ key: 'view' | 'rename' | 'edit'; label: string }>;
}

/** Адрес ручки заливки для родного экрана: тот же у мастера и у окна письма. */
export type MsgUpload = { url: string; field: string };

/** ЛИСТ «ВЛОЖЕНИЕ» (И9.7): группы «Добавить» и «Заменить», тумблеры и «Убрать». */
export interface NativeMsgAttachSheet {
  title: string;
  /** СЛОВА ТАБОВ ШТОРКИ ВЫБОРА «Галерея / Файлы» (П130 п.24, владелец 24.09.2026):
   *  галерея открывается сразу в своей шторке, снизу переключатель. Ключи аддитивные. */
  gallery?: string;
  files?: string;
  /** «ОТКРЫТЬ» — кнопка страницы-заглушки вкладок «Аудио»/«Файл» шторки скрепки (П151 §11п п.3):
   *  открывает окно документов ещё раз. Ключ аддитивный: старая сборка его не читает. */
  open?: string;
  /** `footer` — одна серая строка ПОД группой (П132 п.13: «Кружок уберёт и текст
   *  сообщения» у группы «Заменить»); пусто или нет ключа — строки нет. Ключ АДДИТИВНЫЙ:
   *  старая сборка строку не покажет, и это допустимо (подсказка, а не правило). */
  groups: { key: string; label: string; rows: NativeMsgAttachRow[]; footer?: string }[];
  /** Тумблеры внизу («Спойлер», «Подпись над медиа»); пусто — медиа нет.
   *  `hint` — ПОЯСНЕНИЕ ПОД СЛОВОМ ТУМБЛЕРА (П168 п.1, владелец 27.09.2026: «непонятно, что
   *  это такое»): готовая строка, натив рисует ряд двухстрочным. Ключ АДДИТИВНЫЙ (запрет
   *  №8): старая сборка его не читает — ряд «слово + тумблер», как был. */
  options: { key: string; label: string; on: boolean; hint?: string }[];
}

/** Кнопка панели разметки над клавиатурой. */
export interface NativeMsgFormat {
  /** Вид разметки — возвращается в msgFormat как есть. */
  kind: string;
  label: string;
  /** SF-значок кнопки. */
  icon: string;
  /** Разметка стоит на текущем выделении — кнопка нажата. */
  on: boolean;
}

/** ТЕКСТ ПРАВИТСЯ НА МЕСТЕ, В ПУЗЫРЕ (ТЗ И11.7, владелец 04.09.2026: страница «Текст»
 *  отменена). Тап по слоту текста делает его редактируемым, снизу поднимается
 *  клавиатура, над ней — полоса разметки с «Готово». */
export interface NativeMsgTextEdit {
  /** Telegram-HTML текста (натив собирает из него NSAttributedString). */
  html: string;
  placeholder: string;
  /** Готовый счётчик «N из 4096»; ПУСТО — до предела ещё далеко, счётчика нет (Б3.4). */
  counter: string;
  /** Знаков больше предела — счётчик красным, «Готово» окна не работает. */
  over: boolean;
  /** Полоса разметки над клавиатурой (восемь капсул). */
  format: NativeMsgFormat[];
  /** Подпись «Готово» на правом краю полосы — снимает фокус, клавиатура уезжает. */
  done: string;
  /** КУСКИ РАЗМЕТКИ плоского текста `html` (ТЗ П150 §2.7): рисовалки поля и пузыря берут
   *  вид отсюда, HTML не разбирают. Ключ новый — старая сборка его не читает. */
  marks?: NativeMsgMark[];
  /** ПЛОСКИЙ текст (без тегов), к которому относятся `marks`: `marksToTgHtml(text, marks)`
   *  даёт канон того же `html`. Ключ новый (П150). */
  text?: string;
  /** Куда поставить каретку: после блока (цитата/код) — сразу за ним, иначе конец выделения. */
  caret?: number;
}

/** Кусок разметки: границы в единицах JS-строки (UTF-16, как у NSString), `to` не включается. */
export interface NativeMsgMark {
  from: number;
  to: number;
  /** bold · italic · underline · strike · spoiler · code · pre · quote · expandable · link */
  kind: string;
  /** Только у link. */
  url?: string;
  /** Только у pre: язык блока кода. */
  language?: string;
}

/** Готовые слова окна: в Swift переводимых строк не бывает. */
export interface NativeMsgDict {
  done: string;
  back: string;
  cancel: string;
  /** Подпись полосы разметки над клавиатурой; с П150 — название подменю «Форматировать». */
  format: string;
  play: string;
  pause: string;
  /** СЛОВА ФОРМАТИРОВАНИЯ П150 (Telegram-iOS `TextFormat.*`, слово в слово): подменю,
   *  пункты, алерт ссылки (`linkText` с «%@» — туда натив ставит выделенный текст) и тост
   *  копирования кода. Ключ новый — старая сборка его не читает. */
  fmt?: NativeMsgFmtDict;
  /** СЛОВА СТРОКИ ВВОДА НАД КЛАВИАТУРОЙ (ТЗ П151 §8): плейсхолдер, подпись галочки и
   *  скрепки для читалки. Ключ новый; нет его — натив берёт `card.text.placeholder` и `done`. */
  inputBar?: NativeMsgInputBarDict;
  /** ДОСТУП К ФОТО СВОЕЙ СЕТКИ ГАЛЕРЕИ (ТЗ П151 §11ж п.1): заголовок и текст листа отказа,
   *  капсула «Открыть настройки», строка «доступны только выбранные» и «Выбрать ещё». Ключ
   *  новый; нет его — натив листа отказа и строки не показывает. */
  photoAccess?: NativeMsgPhotoAccessDict;
}

export interface NativeMsgPhotoAccessDict {
  title: string;
  text: string;
  settings: string;
  limited: string;
  more: string;
}

export interface NativeMsgInputBarDict {
  placeholder: string;
  done: string;
  attach: string;
}

/** Пункт меню долгого нажатия по тексту пузыря (ТЗ П151 §8). */
export interface NativeMsgTextMenuItem {
  key: 'edit' | 'copy';
  label: string;
}

export interface NativeMsgFmtDict {
  format: string;
  quote: string;
  spoiler: string;
  bold: string;
  italic: string;
  monospace: string;
  link: string;
  strikethrough: string;
  underline: string;
  code: string;
  linkAdd: string;
  linkEdit: string;
  linkText: string;
  linkPh: string;
  cancel: string;
  done: string;
  codeCopied: string;
}

// ── НОВАЯ ФОРМА СТРАНИЦЫ КНОПКИ (ТЗ 21.09.2026, раздел 4) ────────────────────────────
//
// Страница — СГРУППИРОВАННАЯ ТАБЛИЦА, как системные «Настройки» iOS: карточки-пузыри
// одна под другой. Карточка «Вид» (подпись · расположение · цвет) — строки-переходы со
// своими листами; карточка «Что делает» — четыре галочки ПРЯМО НА СТРАНИЦЕ; ниже —
// карточки настроек выбранного варианта; в самом низу «Удалить кнопку».
//
// Форма ровная нарочно: ОДНА структура карточки и ОДНА структура строки с
// необязательными полями. Натив и веб-близнец строятся строго по ней — иначе они
// разойдутся, а сверять два разных описания одного экрана нечем.
//
// INVARIANT(scriptera): всё это — ДОБАВКА к `NativeMsgButtonPage`, а не подмена. Поля
// `groups` и `remove` остаются как были и считаются как были: сборка из стора их читает
// и рисует прежнюю простыню. Новая сборка, увидев `sections`, рисует страницу по ТЗ и
// `groups` игнорирует. Причина: контракт моста заморожен (CLAUDE.md запрет №8) —
// у уехавшей в стор сборки поведение не меняется никогда.
// Канон: ~/projects/BUTTON-PAGE-TZ.md разделы 4–5.

/** РОЛЬ цвета кружка (строка «Цвет» и её лист) — готовый hex натив берёт из
 *  `NativeMsgData.skin` (mut · blue · green · danger). Своих hex страница не шлёт:
 *  цвет живёт по ролям палитры, а роли уже уехали в окно вместе с обликом. */
export type NativeMsgButtonSwatch = 'mut' | 'blue' | 'green' | 'danger';

/** ПОЛЕ: в строке-поле карточки (адрес) или единственное поле листа (подпись, ответ,
 *  имя новой метки). Правка уезжает вебу как msgSet { key, value }, а веб присылает
 *  пересчитанную страницу. */
export interface NativeMsgButtonField {
  key: string;
  /** Подпись поля. Пусто — подписи нет: её несёт заголовок карточки или шапка листа. */
  title?: string;
  value: string;
  placeholder: string;
  /** Серая строка под полем (пояснение, не ошибка). */
  hint?: string;
  /** Клавиатура поля: 'url' — адресная. Пусто — обычная. */
  keyboard?: 'url';
  /** Предел знаков площадки; пусто — предела нет. */
  max?: number;
  /** ГОТОВАЯ строка остатка знаков; пусто — счётчика нет. */
  counter?: string;
  /** Значение СЕЙЧАС не годится: галочка (листа или страницы) не принимает его. */
  bad?: boolean;
  /** ГОТОВЫЕ слова ошибки под полем. Пусто — говорить нечего. С ТЗ П155 п.1.9: у адреса
   *  «Укажите адрес» (пусто) или «Проверьте адрес» (негодный), у подписи (`btn.text` в
   *  листе строки `btn.row.text`) — «Заполните подпись», пока она пуста. */
  error?: string;
  /** МНОГОСТРОЧНОЕ ПОЛЕ НА ВСЮ КАРТОЧКУ (ТЗ П155 п.1.3, «Что ответить»): поле растёт по
   *  тексту, титула у строки нет. Ключ аддитивный: сборка без него рисует однострочное
   *  поле в строке, правка уходит тем же msgSet { key, value }. */
  multiline?: boolean;
  /** ЗНАЧЕНИЕ ДОПИСЫВАЕТСЯ К КЛЮЧУ И УЕЗЖАЕТ ТАПОМ: msgTap { key: `${key}${value}` }.
   *  Так заводится новая метка (ключ `btn.tag.new:`) — она правит не кнопку, а список,
   *  из которого выбирают. Пусто — обычная правка поля msgSet { key, value }. */
  appendKey?: boolean;
}

/** СТРОКА-ГАЛОЧКА ЛИСТА (расположение, цвет). Тап шлёт msgTap { key }; лист от тапа НЕ
 *  закрывается — правку кладёт галочка в шапке листа. */
export interface NativeMsgButtonOption {
  key: string;
  title: string;
  hint?: string;
  /** Кружок 20 pt слева — роль цвета из `skin` (лист «Цвет»). Пусто — кружка нет. */
  swatch?: NativeMsgButtonSwatch;
  on: boolean;
  /** Выбрать нельзя (ряд уже полон) — строка тусклая, причина стоит в `hint`. */
  disabled?: boolean;
}

/** ЛИСТ МЕТОК — ТОЧНАЯ КОПИЯ КАРТОЧКИ СОБЕСЕДНИКА (владелец 21.09.2026: «метки надо
 *  сделать как тут. Там можно добавить метку, удалить метку и галочку снять»). Эталон —
 *  `ContactTagsSheet` в ios/App/App/ChatInfoSheet.swift: первая строка «Добавить метку»
 *  розовым, ниже все метки кабинета, у выбранной розовая галочка, смахивание влево —
 *  «Удалить» метку У ВСЕХ с родным подтверждением.
 *
 *  ОТЛИЧИЕ ОТ КАРТОЧКИ ОДНО: метка у кнопки не больше одной — поставили другую, прежняя
 *  снимается сама.
 *
 *  INVARIANT(scriptera): СНЯТИЕ МЕТКИ ШЛЁТ ТОТ ЖЕ КЛЮЧ, ЧТО И ПОСТАНОВКА, — повторный
 *  тап по отмеченной строке уезжает как msgTap { key: `${pickKey}<имя>` }, и водитель
 *  окна читает его как ПЕРЕКЛЮЧЕНИЕ (стоит эта метка — снять, иначе поставить).
 *  Второго ключа на снятие нет: тап несёт НАМЕРЕНИЕ, а не состояние экрана, и залп
 *  правок по галочке листа считается от накопившегося черновика (капкан залпа, см.
 *  msgBtnTapValue в message-native.ts). Причина: лишняя сущность в мосту — лишний
 *  разбор на обоих концах. Канон: ~/projects/BUTTON-PAGE-TZ.md раздел 4. */
export interface NativeMsgButtonTags {
  /** Розовая первая строка — «Добавить метку». Поле имени открывается ВНУТРИ листа. */
  add: string;
  /** Заголовок поля новой метки и подсказка пустого поля. */
  addTitle: string;
  addPlaceholder: string;
  /** Ключ заводки: msgTap { key: `${addKey}<имя>` }. */
  addKey: string;
  /** Все метки кабинета; `on` — та, что стоит у кнопки (она не больше одной). */
  items: { value: string; on: boolean }[];
  /** Ключ постановки И СНЯТИЯ: msgTap { key: `${pickKey}<имя>` } (см. инвариант выше). */
  pickKey: string;
  /** Смахивание влево — удалить метку У ВСЕХ: слово красной плашки, вопрос родного
   *  подтверждения и ключ msgTap { key: `${swipeKey}<имя>` }. Слов нет — смахивания у
   *  строк не бывает вовсе. */
  swipe?: string;
  swipeAsk?: string;
  swipeKey?: string;
}

/** ЛИСТ строки-перехода. Видов ровно три:
 *   • field — одно поле с клавиатурой («Подпись», «Что ответить»);
 *   • radio — строки-галочки, выбор один («Расположение», «Цвет»);
 *   • tags  — список меток кабинета по рецепту карточки собеседника («Метка»).
 *  Лист — низкая вертикальная шторка; шапка общая (крестик · титул · галочка), слова
 *  крестика и галочки натив берёт из `NativeMsgDict`. ГАЛОЧКА СОХРАНЯЕТ, КРЕСТИК НЕТ. */
export interface NativeMsgButtonSheet {
  kind: 'field' | 'radio' | 'tags';
  title: string;
  /** Серая строка под содержимым листа. */
  hint?: string;
  field?: NativeMsgButtonField;
  options?: NativeMsgButtonOption[];
  tags?: NativeMsgButtonTags;
}

/** ЧТО ЗА СТРОКА КАРТОЧКИ:
 *   • nav    — переход: титул слева, значение справа серым, шеврон; тап открывает `sheet`;
 *   • check  — галочка: выбор ОДИН в пределах карточки, снять нельзя — только сменить
 *              (виды, «откуда отписать», метки); тап шлёт msgTap { key };
 *   • field  — поле прямо в строке (адрес); правка шлёт msgSet { key, value };
 *   • switch — тумблер прямо в строке; переключение шлёт msgTap { key };
 *   • action — нажимаемая строка без значения и без шеврона («Удалить кнопку»); тап
 *              шлёт msgTap { key }. */
export type NativeMsgButtonRowKind = 'nav' | 'check' | 'field' | 'switch' | 'action';

/** СТРОКА КАРТОЧКИ. Одна структура на все виды строк: лишние поля просто пусты. */
export interface NativeMsgButtonRow {
  /** Ключ правки: у check/switch/action он уезжает в msgTap, у field — в msgSet.
   *  У nav ключ служебный (`btn.row.*`) — он открывает лист, а письма не трогает. */
  key: string;
  kind: NativeMsgButtonRowKind;
  title: string;
  /** Пояснение под строкой. Видно ВСЕГДА (правило кабинета: пояснения не прячем). */
  hint?: string;
  /** 'nav': ГОТОВОЕ значение справа (уже на языке кабинета). */
  value?: string;
  /** 'nav': значение — заглушка пустоты («Без подписи»): рисуется тускло. */
  empty?: boolean;
  /** 'nav': кружок 20 pt перед значением — роль цвета из `skin` (строка «Цвет»). */
  swatch?: NativeMsgButtonSwatch;
  /** 'check' и 'switch': положение. */
  on?: boolean;
  /** Строка не нажимается — тускло, причина в `hint`. */
  disabled?: boolean;
  /** 'nav': лист, который открывает тап. */
  sheet?: NativeMsgButtonSheet;
  /** 'field': поле прямо в строке. */
  field?: NativeMsgButtonField;
  /** 'nav' и 'action': роль цвета подписи — обычная, акцентная (розовая «Добавить
   *  метку»), опасная (красная «Удалить кнопку»). Пусто — обычная. */
  tone?: 'plain' | 'accent' | 'danger';
}

/** КАРТОЧКА-ПУЗЫРЬ страницы кнопки (группа сгруппированной таблицы). */
export interface NativeMsgButtonSection {
  key: string;
  /** Заголовок карточки («Вид», «Что делает», «Метка», «Адрес»). Пусто — заголовка нет. */
  title?: string;
  /** Серая заметка ПОД карточкой («Метка сохраняется в карточке пользователя»,
   *  «Работает только в личной переписке с ботом»). Пусто — заметки нет. */
  note?: string;
  rows: NativeMsgButtonRow[];
}

/** СТРАНИЦА ОДНОЙ КНОПКИ — стопкой поверх карточки: подпись, что кнопка делает
 *  (открыть ссылку / поставить метку / «не пишите мне» / открыть мини-апп) и
 *  «Удалить кнопку» внизу красным.
 *
 *  Строки — ТОТ ЖЕ протокол, что у родных страниц «Аккаунта» (`NativeAccountField`):
 *  второй формы строки в приложении не бывает. Что за строки показать при выбранном виде,
 *  решает веб — натив только рисует и возвращает намерения. */
export interface NativeMsgButtonPage {
  /** Какую кнопку правим (её же ключ из списка пузыря). */
  key: string;
  title: string;
  groups: NativeAccountGroup[];
  /** Подпись красной кнопки внизу. */
  remove: string;
  /** НОВАЯ ФОРМА (ТЗ 21.09.2026, раздел 4): страница карточками. Есть — новая сборка
   *  рисует страницу по ним и `groups` не читает; нет — всё как было.
   *
   *  «Удалить кнопку» живёт ПОСЛЕДНЕЙ карточкой (строка `btn-remove`, тон danger), а
   *  прежнее поле `remove` остаётся пустым: иначе старая сборка вернула бы прибитую
   *  красную плашку внизу, отменённую владельцем (И11.8). */
  sections?: NativeMsgButtonSection[];
  /** ГАЛОЧКА СТРАНИЦЫ АКТИВНА (П130 п.23, владелец 24.09.2026: «кнопку без подписи
   *  сохранять нельзя»): false — галочка гаснет, пока подпись пуста. Ключ аддитивный:
   *  старая сборка его не читает, а веб пустую кнопку всё равно не кладёт.
   *  С ТЗ П155 п.1.8 гаснет и у «Открыть ссылку» / «Открыть мини-приложение» с пустым
   *  или негодным адресом. */
  doneOn?: boolean;
  /** ЧЕГО НЕ ХВАТАЕТ ГАЛОЧКЕ (ТЗ П155 п.1.9): 'text' — подписи, 'url' — годного адреса,
   *  '' — всего хватает (`doneOn` true). Подпись проверяется первой. По нему натив по
   *  тапу погашенной галочки даёт вибрацию ошибки, прокручивает к строке (у неё же
   *  приходит `error`) и подсвечивает поле. Ключ аддитивный: старая сборка не читает. */
  doneWhy?: 'text' | 'url' | '';
}

/** Всё, что нужно нативу для окна «Сообщение» (msgOpen/msgPatch). */
export interface NativeMsgData {
  theme: 'light' | 'dark';
  /** Заголовок окна — ГОТОВОЕ слово кабинета («Сообщение»). */
  title: string;
  /** Подзаголовок шапки — откуда пришли («Письмо 2 · через 1 день», «Приветствие»). */
  subtitle: string;
  /**
   * ОКНО — СТРАНИЦА ШТОРКИ ПИСЬМА (К23.1 п.3, владелец 07.09.2026): композер открыт СО
   * СТРАНИЦЫ ПИСЬМА цепочки, значит натив кладёт его внутрь той же шторки (push, шапка
   * `WizPageHead`, стрелка «назад» возвращает к письму), а не поднимает отдельный лист.
   * Нет ключа — прежнее поведение (окно шага анкеты, разовая рассылка).
   *
   * INVARIANT(scriptera): ключ АДДИТИВНЫЙ — старая сборка его не читает и откроет окно,
   * как открывала (контракт моста заморожен, CLAUDE.md запрет №8).
   */
  flow?: boolean;
  /** ОТДЕЛЬНЫМ ЛИСТОМ ПОВЕРХ (П130 п.13, владелец 24.09.2026: «сбоку ничего не
   *  выезжает»): страница встаёт вертикальной шторкой поверх верхнего показанного
   *  экрана (полушторка при `half`, полная иначе), а не едет боком в шторку потока.
   *  `stack` сильнее `flow`. Ключ АДДИТИВНЫЙ — старая сборка его не читает и кладёт
   *  страницу боком, как клала (контракт моста заморожен, CLAUDE.md запрет №8). */
  stack?: boolean;
  dict: NativeMsgDict;
  /** Карточка-прототип письма (то, что видно сразу). */
  card: NativeMsgCard;
  /** Правка текста НА МЕСТЕ: полоса разметки, счётчик и подсказка пустого слота. */
  text: NativeMsgTextEdit;
  /** Лист «Вложение» — его открывает тап по слоту медиа и по строке «приложить ещё». */
  attach: NativeMsgAttachSheet;
  /** Открыта страница одной кнопки; пусто — страницы нет (её снимают тем же патчем). */
  button?: NativeMsgButtonPage;
  /** Сколько байт файла ещё разумно провезти МОСТОМ (тот же капкан веса, что у
   *  переписки: строка base64 живёт сразу в двух памятях). Тяжелее — веб откроет свой
   *  прежний выбор файла. */
  bridgeBytes: number;
  /** Идёт подготовка файла — ГОТОВАЯ строка («Готовим файл…»); пусто — всё готово. */
  busy: string;
  /** ГОТОВАЯ строка помехи, которую человек починит САМ (файл тяжелее предела, кнопка
   *  без адреса). Технических поломок платформы тут не бывает — CLAUDE.md запрет №7. */
  error: string;
  /** «Готово» в шапке работает: письмо не пусто, файлы готовы и уйдёт оно ОДНИМ
   *  сообщением (И9.8). Ложь — кружок в шапке выключен. */
  canDone: boolean;
  /** ОКНО ЗАКРЫВАТЬ НЕЛЬЗЯ (П132 п.2, владелец 25.09.2026): идёт заливка — в сообщении есть
   *  файл `pending` без срыва, и уход унёс бы его (незалитое в ответы анкеты не пишется,
   *  очередь файлов умирает с окном). Натив по нему гасит «‹»/«×» и запрещает свайп
   *  (`isModalInPresentation`); заливка кончилась — ключ уходит тем же патчем. Нет ключа —
   *  закрывать можно. Ключ АДДИТИВНЫЙ: старая сборка его не читает и ведёт себя как раньше
   *  (CLAUDE.md запрет №8). */
  lock?: boolean;
  /** Фон окна, два фирменных пятна, голубой Телеграма (им красятся inline-кнопки — это
   *  ЕГО фирменный цвет, не наш) и пара цветов пузыря — те же роли, что у переписки.
   *
   *  ЦВЕТА ТЕЛЕГРАМА ДЛЯ ПРОТОТИПА (ТЗ И11.7, И11.9 п.5): `tgBubbleIn` — фон ВХОДЯЩЕГО
   *  пузыря, `tgChatBg` — фон его чата; ими и только ими красится карточка-прототип, в
   *  остальном кабинете эти роли не живут. `mut` — серый «тихого языка»: им говорят все
   *  места «добавить» (пунктир, слова, значок), розовый там бывает только на нажатии.
   *  Поля необязательны для читающей стороны: старая сборка их не знает и рисует как
   *  раньше (контракт моста заморожен, CLAUDE.md запрет №8). */
  skin: {
    bg: string;
    spotPink: string;
    spotGreen: string;
    blue: string;
    bubble?: NativeBubbleColors;
    tgBubbleIn?: string;
    tgChatBg?: string;
    mut?: string;
    /** ЛИНИЯ (роль --line) — обводка пузыря-прототипа в 1 pt (ТЗ М2.3/М3.2): белый пузырь
     *  на светлом фоне без неё не читается (владелец 16.09.2026). Ключ НОВЫЙ и
     *  необязательный: старая сборка рисует пузырь без обводки, как раньше. */
    line?: string;
    /** СИГНАЛЬНЫЙ КРАСНЫЙ палитры (роль --step-error) — им красится кнопка со стилем
     *  'danger' (ТЗ М2.6). Ключ НОВЫЙ и необязательный. */
    danger?: string;
    /** ЗЕЛЁНЫЙ палитры (роль --green) — кнопка со стилем 'success' (ТЗ М2.6). Не путать
     *  со `spotGreen`: тот — бледная подложка пятна, этим цветом подпись не прочитать.
     *  Ключ НОВЫЙ и необязательный. */
    green?: string;
  };
}

/** Роли палитры, которыми красится лента переписки: свой пузырь и чужой.
 *
 *  ПАСПОРТ 04.09.2026 (docs/CHAT-LOOK-PLAN.md §3.1): текст реплики — ЧЕРНИЛА у обеих
 *  сторон, а цвет стороны носит только НАРИСОВАННОЕ внутри пузыря (подписи кнопок,
 *  полоска цитаты, ссылки, значок нажатия). Поэтому mineInk/peerInk теперь оба `--ink`,
 *  а роли обводки приезжают отдельными полями. Заказ 31.08 «текст цветом фона, ещё
 *  насыщеннее» отменён этим паспортом.
 *
 *  Поля mineStroke/peerStroke/faint ДОБАВЛЕНЫ и необязательны для читающей стороны:
 *  старая сборка приложения читает только четыре прежних ключа и рисует как раньше,
 *  новая берёт `mineStroke ?? mineInk` (контракт моста заморожен, CLAUDE.md запрет №8). */
export interface NativeBubbleColors {
  /** Фон своего пузыря — роль --bub-mine-bg. */
  mineBg: string;
  /** Текст своей реплики — роль --ink (было --accent-stroke до 04.09.2026). */
  mineInk: string;
  /** Фон чужого пузыря — роль --bub-peer-bg. */
  peerBg: string;
  /** Текст чужой реплики — роль --ink (было --green-stroke до 04.09.2026). */
  peerInk: string;
  /** Нарисованное в СВОЁМ пузыре (кнопки, цитата, ссылки) — роль --accent-stroke. */
  mineStroke?: string;
  /** Нарисованное в ЧУЖОМ пузыре — роль --green-stroke. */
  peerStroke?: string;
  /** Мелкие подписи ленты (время, «правлено», статус, событие) — роль --msg-faint. */
  faint?: string;
}

/**
 * ЗАМЕР РОЛЕЙ ПАЛИТРЫ — ОДИН РАЗ НА СОСТОЯНИЕ ТЕМЫ (ТЗ ANIM-FIX О4, 04.10.2026). Каждый живой
 * замер (`readRoleHex`/`readRoleAlpha`) вставляет пробник в `.cab` и читает его
 * getComputedStyle — это ПРИНУДИТЕЛЬНЫЙ пересчёт стилей всей грязной части страницы. Карточки
 * «Ботов», окно бота и родные экраны зовут его десятками раз за одну перерисовку (в следе
 * закрытия окна бота — 14 пересчётов по 20–40 мс подряд в одном касании). Значение роли
 * зависит только от темы и ширины: атрибутов `<html>`/`<body>` (data-theme, классы),
 * системной темы, набора подключённых стилей и размера окна (роли из
 * @container/@media кабинета) — поэтому замер кэшируется по слепку
 * этих признаков. Слепок дешёвый (атрибуты и счётчик, без пересчёта стилей); сменилась тема
 * или доехал стиль — слепок другой, роль меряется заново живым пробником, как прежде.
 */
let roleCacheKey = '';
const roleCache = new Map<string, string | number>();
let darkMq: MediaQueryList | null = null;
/** Ширина окна меняет роли (часть объявлена в @container/@media кабинета): смена размера
 *  сбрасывает кэш. Подписка одна, ставится при первом замере. */
let roleResizeEpoch = 0;
let roleResizeOn = false;
/** Атрибуты элемента в слепок — БЕЗ инлайн-`style`: в нём у `<html>`/`<body>` живёт только
 *  геометрия (замок прокрутки body, `--th-p` шапки на каждый кадр прокрутки, `--msg-inset`,
 *  `--native-*`), цветов там нет — роли палитры задаются data-theme и классами. С `style`
 *  кэш промахивался бы на каждом кадре прокрутки и открытии слоя. */
function attrsKey(el: Element | null): string {
  if (!el) return '';
  let out = '';
  for (const name of el.getAttributeNames()) {
    // `data-page-theme` — память метки theme-color браузера (page-theme-color.ts), в CSS не
    // участвует и меняется на каждой смене раздела.
    if (name === 'style' || name === 'data-page-theme') continue;
    out += `${name}=${el.getAttribute(name) ?? ''};`;
  }
  return out;
}
function roleCacheFresh(): Map<string, string | number> {
  if (!darkMq && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    darkMq = window.matchMedia('(prefers-color-scheme: dark)');
  }
  if (!roleResizeOn && typeof window !== 'undefined') {
    roleResizeOn = true;
    window.addEventListener('resize', () => {
      roleResizeEpoch += 1;
    });
  }
  const key = `${attrsKey(document.documentElement)}|${attrsKey(document.body)}|${darkMq?.matches ? 1 : 0}|${document.styleSheets.length}|${roleResizeEpoch}`;
  if (key !== roleCacheKey) {
    roleCacheKey = key;
    roleCache.clear();
  }
  return roleCache;
}

/** Общий кэш замеров цвета по тому же слепку темы — для соседних пробников (метка
 *  theme-color браузера, lib/page-theme-color.ts). `key` обязан включать всё, от чего
 *  зависит ответ, кроме темы и ширины (их держит слепок). */
export function themeCached<T extends string | number>(key: string, measure: () => T): T {
  if (typeof document === 'undefined') return measure();
  const cache = roleCacheFresh();
  const ck = `ext|${key}`;
  const hit = cache.get(ck);
  if (hit !== undefined) return hit as T;
  const value = measure();
  cache.set(ck, value);
  return value;
}

/** Цвет РОЛИ палитры строкой '#rrggbb'. Разбор тот же, что у readAccentRgb (rgb(from …)
 *  приводит к srgb, oklab/color(srgb) читаем напрямую), а замер — ВНУТРИ `.cab`: часть
 *  ролей (бирюзовые ступени) объявлена на нём, а не на :root, и на body они не видны. */
export function readRoleHex(role: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const host = document.querySelector('.cab') ?? document.body;
  if (!host) return fallback;
  // Кэш — по живому хозяину (`.cab` или body: часть ролей объявлена только на `.cab`) и
  // роли; не разобралась роль — в кэше пустая строка, ответом идёт запасное значение
  // вызывающего (у разных мест оно своё).
  const cache = roleCacheFresh();
  const ck = `hex|${host === document.body ? 'b' : 'c'}|${role}`;
  let hit = cache.get(ck);
  if (typeof hit !== 'string') {
    hit = measureRoleHex(host, role, '');
    cache.set(ck, hit);
  }
  return hit === '' ? fallback : hit;
}

function measureRoleHex(host: Element, role: string, fallback: string): string {
  const probe = document.createElement('span');
  probe.style.color = `rgb(from var(${role}) r g b)`;
  probe.style.display = 'none';
  host.appendChild(probe);
  let raw = getComputedStyle(probe).color;
  if (!/^rgb|color\(srgb/.test(raw)) {
    probe.style.color = `var(${role})`;
    raw = getComputedStyle(probe).color;
  }
  probe.remove();
  const mRgb = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec(raw);
  const mSrgb = /color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/.exec(raw);
  const hex = (r: number, g: number, b: number) =>
    `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
  if (mRgb) return hex(Number(mRgb[1]), Number(mRgb[2]), Number(mRgb[3]));
  if (mSrgb) return hex(Number(mSrgb[1]) * 255, Number(mSrgb[2]) * 255, Number(mSrgb[3]) * 255);
  return fallback;
}

/** ФИРМЕННЫЙ ФОН РОДНОГО ЭКРАНА — ОДНА РУКА (владелец 11.09.2026, П11 обхода: справка
 *  рисовала ЧЁРНЫЕ пятна). Числа пятен (геометрия, прозрачность) живут у натива по
 *  словарю `.cab::before`, отсюда едут только живые цвета РОЛЕЙ — и роли эти одни на все
 *  родные экраны: фон `--bg`, розовое пятно `--accent-soft`, зелёное `--green-soft`.
 *
 *  INVARIANT(scriptera): цвета пятен родного экрана берутся ТОЛЬКО отсюда — своей тройки
 *  ролей у экрана не бывает. Причина: `help-native.ts` спросил несуществующие роли
 *  `--spot-pink`/`--spot-green`, а `readRoleHex` у незнакомой роли отдаёт вычисленный
 *  чёрный — справка приехала с чёрными пятнами (скрин владельца 11.09, 10:33).
 *  Канон: CLAUDE.md «Цвет — только по ролям палитры». */
export function nativeSkin(theme: 'light' | 'dark'): { bg: string; spotPink: string; spotGreen: string } {
  return {
    bg: readRoleHex('--bg', theme === 'dark' ? '#101014' : '#fbfbfd'),
    spotPink: readRoleHex('--accent-soft', '#ffe7ed'),
    spotGreen: readRoleHex('--green-soft', '#e0f5f7'),
  };
}

/** ПРОЗРАЧНОСТЬ РОЛИ живой страницы. readRoleHex берёт у роли только цвет (rgb(from …)
 *  снимает канал прозрачности), а столбик графика в вебе нарисован чернилами ВПОЛСИЛЫ
 *  (--ov-bar = ink 30%, в тёмной теме 42%). Своего числа у натива быть не должно —
 *  меряем его тем же живым пробником, что и цвет. Рука ОДНА на все родные экраны
 *  («Аналитика», окна карточки бота): второй правды у прозрачности нет. */
export function readRoleAlpha(role: string, fallback: number): number {
  if (typeof document === 'undefined') return fallback;
  const host = document.querySelector('.cab') ?? document.body;
  if (!host) return fallback;
  const cache = roleCacheFresh();
  const ck = `alpha|${host === document.body ? 'b' : 'c'}|${role}`;
  let hit = cache.get(ck);
  if (typeof hit !== 'number') {
    hit = measureRoleAlpha(host, role, Number.NaN);
    cache.set(ck, hit);
  }
  return Number.isNaN(hit) ? fallback : hit;
}

function measureRoleAlpha(host: Element, role: string, fallback: number): number {
  const probe = document.createElement('span');
  probe.style.color = `var(${role})`;
  probe.style.display = 'none';
  host.appendChild(probe);
  const raw = getComputedStyle(probe).color;
  probe.remove();
  // Прозрачность есть только у ЧЕТВЁРТОЙ составляющей: 'rgba(r, g, b, a)' или запись со
  // слэшем ('rgb(r g b / a)', 'color(srgb … / a)'). Без неё цвет непрозрачный — 1.
  // Ловить «последнее число перед скобкой» нельзя: у 'rgb(0, 0, 0)' им был бы синий.
  const m =
    /^rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)$/.exec(raw) ??
    /\/\s*([\d.]+)\s*\)$/.exec(raw);
  const value = m ? Number(m[1]) : 1;
  return Number.isFinite(value) ? value : fallback;
}

/** Цвета пузырей ленты для родного экрана переписки (владелец 31.08.2026). Веб — хозяин
 *  палитры: тема и набор кабинета живые, поэтому роли читаются с живой страницы, а не
 *  зашиваются в Swift. Запасные значения — светлая пара из tokens.css/cabinet.css. */
export function readBubbleColors(): NativeBubbleColors {
  // Чернила реплики ОДНИ на обе стороны (паспорт §3.1): у нативных мессенджеров фон
  // пузыря подкрашен, а текст всегда чернильный — тон-в-тон читается как плашка.
  const ink = readRoleHex('--ink', '#16181d');
  return {
    // Фон — СВОЯ роль пузыря (--bub-*-bg): роль темы, разбавленная поверхностью темы.
    // Считать её тут нельзя: она живёт в палитре кабинета.
    // Запасные значения — светлая тема паспорта §3-v2.2 (роль 16% + поверхность): они
    // уходят в дело только там, где палитру прочитать нечем (сервер, юнит).
    mineBg: readRoleHex('--bub-mine-bg', '#ffe2e7'),
    mineInk: ink,
    peerBg: readRoleHex('--bub-peer-bg', '#deeef0'),
    peerInk: ink,
    // Нарисованное внутри пузыря — роль обводки своей стороны.
    mineStroke: readRoleHex('--accent-stroke', '#e23570'),
    peerStroke: readRoleHex('--green-stroke', '#0095a2'),
    // Подписи — чернила 65% (§3-v2.2): прежние 55% не добирали 4.5:1.
    faint: readRoleHex('--msg-faint', '#5e5f63'),
  };
}

/** RGB текущего акцента кабинета (--accent) для нативных элементов (таб-бар, композер).
 *  Тот же разбор, что в shell.tsx sendBarStyle: rgb(from …) приводит к srgb, oklab/color(srgb)
 *  разбираем напрямую, иначе в натив уезжал бы запасной розовый в тёмных наборах. */
export function readAccentRgb(): { red: number; green: number; blue: number } {
  let red = 191;
  let green = 64;
  let blue = 121;
  if (typeof document === 'undefined') return { red, green, blue };
  const probe = document.createElement('span');
  probe.style.color = 'rgb(from var(--accent) r g b)';
  probe.style.display = 'none';
  document.body.appendChild(probe);
  let raw = getComputedStyle(probe).color;
  if (!/^rgb|color\(srgb/.test(raw)) {
    probe.style.color = 'var(--accent)';
    raw = getComputedStyle(probe).color;
  }
  probe.remove();
  const mRgb = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec(raw);
  const mSrgb = /color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/.exec(raw);
  if (mRgb) {
    red = Math.round(Number(mRgb[1]));
    green = Math.round(Number(mRgb[2]));
    blue = Math.round(Number(mRgb[3]));
  } else if (mSrgb) {
    red = Math.round(Number(mSrgb[1]) * 255);
    green = Math.round(Number(mSrgb[2]) * 255);
    blue = Math.round(Number(mSrgb[3]) * 255);
  }
  return { red, green, blue };
}

declare global {
  interface Window {
    Capacitor?: { Plugins?: { ScripteraNative?: ScripteraNativeBridge } };
  }
}

export function isNativeApp(): boolean {
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('ScripteraApp');
}

/** Номер сборки приложения из UA (`ScripteraApp/0.2 (build 241; commit …)` → 241).
 *  Браузер или сборка без `(build N)` → null (гейт считает такую сборку старой, 0). */
export function nativeBuildNumber(): number | null {
  if (typeof navigator === 'undefined') return null;
  return parseAppBuild(navigator.userAgent);
}

/** SSR-безопасный вариант: false на сервере и в первом клиентском рендере (без гидрационных расхождений). */
export function useNativeApp(): boolean {
  const [native, setNative] = useState(false);
  useEffect(() => {
    if (isNativeApp()) setNative(true);
  }, []);
  return native;
}

/** Детект метода моста для РАЗМЕТКИ: false на сервере и в первом клиентском рендере,
 *  настоящее значение — после монтирования. Ветвить JSX по nativeMethod() напрямую
 *  НЕЛЬЗЯ: сервер window не видит и рисует веб-вариант, клиент в приложении — нативный,
 *  React считает это провалом гидрации (#418) и пересобирает страницу с нуля
 *  (инцидент 355a478f: 499 повторов на /app/bots из приложения). В эффектах и
 *  обработчиках nativeMethod() по-прежнему зовётся напрямую. */
export function useNativeMethod(name: string): boolean {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (nativeMethod(name)) setOk(true);
  }, [name]);
  return ok;
}

// ── ЖУРНАЛ ВЫЗОВОВ МОСТА (владелец 25.08.2026) ──────────────────────────────────────
// Половина жалоб («жму вкладку, содержимое приходит через пять секунд»; «после выкатки
// приложение виснет») живёт на стыке веба и оболочки, а с телефона нет ни консоли, ни
// логов. Поэтому мост отдаётся вебу ЗАВЁРНУТЫМ: каждый вызов метода пишет в журнал имя
// и длительность (client-journal), события от натива — имя и ключ. Ни аргументов, ни
// текстов в журнал не уходит.
//
// КАПКАН СОХРАНЁН (§11 п.6, nativeMethod): обёртка отдаёт функцию на ЛЮБОЕ имя — ровно
// как исходный Proxy Capacitor. Проверять наличие метода по свойству нельзя было и
// раньше, единственный способ — nativeMethod(); обёртка этого не меняет.
//
// Частые методы (за пальцем, по кадрам, по каждой букве) в журнал НЕ пишем — они забили
// бы кольцо за секунды и вытеснили всё, ради чего журнал заведён.
const BRIDGE_QUIET = new Set([
  'topTabDrag',
  'topTabSettle',
  'sheetTabDrag',
  'sheetTabSettle',
  'setComposerLevel',
  'setComposerText',
  'sheetScrollAtTop',
  'haptic',
]);
// События натива, интересные для разбора. Остальные (kbChange, composerInput, chatOlder…)
// приходят пачками и в журнале только мешают.
// Тапы родного раздела «Рассылки» и родной формы рассылки — тоже в журнал (03.09.2026:
// «Создать рассылку» в приложении молчала, а по журналу было не видно, дошёл ли тап до
// веба). В журнал уходят только имя события и ключ.
const BRIDGE_EVENTS = new Set([
  'tabSelected',
  'layerPopped',
  'pushOpen',
  'headerAction',
  'bcastListTap',
  'bcastListBack',
  'bcastTap',
  'bcastSubmit',
  'bcastCancel',
]);

let wrappedFor: object | null = null;
let wrappedBridge: ScripteraNativeBridge | null = null;

function wrapBridge(raw: ScripteraNativeBridge): ScripteraNativeBridge {
  // Обёртки методов кэшируем: ссылка на метод обязана быть стабильной между обращениями.
  const cache = new Map<string, unknown>();
  return new Proxy(raw as unknown as Record<string, unknown>, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof prop !== 'string' || typeof value !== 'function') return value;
      const hit = cache.get(prop);
      if (hit) return hit;
      const fn = value as (...args: unknown[]) => unknown;
      const wrapped =
        prop === 'addListener'
          ? (event: string, cb: (data: Record<string, unknown>) => void, ...rest: unknown[]) =>
              fn.call(
                target,
                event,
                (data: Record<string, unknown>) => {
                  if (BRIDGE_EVENTS.has(event)) {
                    noteBridgeEvent(event, typeof data?.key === 'string' ? data.key : undefined);
                  }
                  cb(data);
                },
                ...rest,
              )
          : (...args: unknown[]) => {
              const quiet = BRIDGE_QUIET.has(prop);
              const t0 = quiet ? 0 : Date.now();
              let res: unknown;
              try {
                res = fn.apply(target, args);
              } catch (e) {
                if (!quiet) noteBridgeCall(prop, Date.now() - t0, e instanceof Error ? e.message : String(e));
                throw e;
              }
              if (quiet) return res;
              if (res && typeof (res as Promise<unknown>).then === 'function') {
                return (res as Promise<unknown>).then(
                  (v) => {
                    noteBridgeCall(prop, Date.now() - t0);
                    return v;
                  },
                  (e: unknown) => {
                    noteBridgeCall(prop, Date.now() - t0, e instanceof Error ? e.message : String(e));
                    throw e;
                  },
                );
              }
              noteBridgeCall(prop, Date.now() - t0);
              return res;
            };
      cache.set(prop, wrapped);
      return wrapped;
    },
  }) as unknown as ScripteraNativeBridge;
}

/** Мост к нативному коду приложения; null в браузере и в старых сборках приложения. */
export function nativeBridge(): ScripteraNativeBridge | null {
  const raw = (typeof window !== 'undefined' && window.Capacitor?.Plugins?.ScripteraNative) || null;
  if (!raw) return null;
  if (wrappedFor !== raw || !wrappedBridge) {
    wrappedFor = raw as unknown as object;
    wrappedBridge = wrapBridge(raw);
  }
  return wrappedBridge;
}

/**
 * ОТКРЫТЬ ЧУЖОЙ АДРЕС (мини-апп формы бота, страница его чата) РОДНЫМ ЛИСТОМ-СТОПКОЙ.
 * Н38 (владелец 02.09.2026): «Открыть форму / Открыть чат — стопка» — до этого ссылка
 * уходила в системный браузер и человек выпадал из приложения.
 *
 * Вернула false — сборка так не умеет (старая установка, Android) или это браузер:
 * вызывающий открывает ссылку прежним способом. Второго способа открытия ссылки в
 * кабинете не заводим — все входы зовут эту руку.
 */
export function openWebSheet(url: string, theme: 'light' | 'dark'): boolean {
  if (!url || !isNativeApp() || !nativeMethod('webSheetOpen')) return false;
  const br = nativeBridge();
  if (!br?.webSheetOpen) return false;
  void br.webSheetOpen({ url, theme });
  return true;
}

/**
 * ОТКРЫТЬ СПРАВКУ (/help) РОДНЫМ ЛИСТОМ-СТОПКОЙ С НАШИМ СОДЕРЖИМЫМ (ТЗ этап G п.2,
 * владелец 09.09.2026: у справки не должно быть хрома Сафари — ни адресной строки, ни
 * кнопки «поделиться»).
 *
 * Вернула false — сборка так не умеет: вызывающий открывает справку прежним путём
 * (openWebSheet, а без него — новым окном). Второго способа открыть справку не заводим.
 */
export function openHelpSheet(url: string, theme: 'light' | 'dark', title: string): boolean {
  if (!url || !isNativeApp() || !nativeMethod('helpSheetOpen')) return false;
  const br = nativeBridge();
  if (!br?.helpSheetOpen) return false;
  void br.helpSheetOpen({ url, theme, title });
  return true;
}
